// src/rentals-api-v3/runtime.ts
import { formatISO } from "date-fns";
var BASE_PATH = "https://rentals.api.jucy.cloud".replace(/\/+$/, "");
var Configuration = class {
  constructor(configuration = {}) {
    this.configuration = configuration;
  }
  set config(configuration) {
    this.configuration = configuration;
  }
  get basePath() {
    return this.configuration.basePath != null ? this.configuration.basePath : BASE_PATH;
  }
  get fetchApi() {
    return this.configuration.fetchApi || fetch;
  }
  get middleware() {
    return this.configuration.middleware || [];
  }
  get queryParamsStringify() {
    return this.configuration.queryParamsStringify || querystring;
  }
  get username() {
    return this.configuration.username;
  }
  get password() {
    return this.configuration.password;
  }
  get apiKey() {
    const apiKey = this.configuration.apiKey;
    if (apiKey) {
      return typeof apiKey === "function" ? apiKey : () => apiKey;
    }
    return void 0;
  }
  get accessToken() {
    const accessToken = this.configuration.accessToken;
    if (accessToken) {
      return typeof accessToken === "function" ? accessToken : async () => accessToken;
    }
    return void 0;
  }
  get headers() {
    return this.configuration.headers;
  }
  get credentials() {
    return this.configuration.credentials;
  }
};
var DefaultConfig = new Configuration();
async function createResponseErrorMessage(response, fallback = "Response returned an error code") {
  try {
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.startsWith("application/json")) {
      const json = await response.json();
      return new ResponseError(response, json?.message || json?.Message || response.statusText || fallback, json);
    }
    const body = await response.text();
    return new ResponseError(response, body || response.statusText || fallback, { body });
  } catch (e) {
    return new ResponseError(response, fallback, { error: e });
  }
}
var BaseAPI = class _BaseAPI {
  constructor(configuration = DefaultConfig) {
    this.configuration = configuration;
    this.middleware = configuration.middleware;
  }
  static jsonRegex = new RegExp("^(:?application/json|[^;/ 	]+/[^;/ 	]+[+]json)[ 	]*(:?;.*)?$", "i");
  middleware;
  withMiddleware(...middlewares) {
    const next = this.clone();
    next.middleware = next.middleware.concat(...middlewares);
    return next;
  }
  withPreMiddleware(...preMiddlewares) {
    const middlewares = preMiddlewares.map((pre) => ({ pre }));
    return this.withMiddleware(...middlewares);
  }
  withPostMiddleware(...postMiddlewares) {
    const middlewares = postMiddlewares.map((post) => ({ post }));
    return this.withMiddleware(...middlewares);
  }
  /**
   * Check if the given MIME is a JSON MIME.
   * JSON MIME examples:
   *   application/json
   *   application/json; charset=UTF8
   *   APPLICATION/JSON
   *   application/vnd.company+json
   * @param mime - MIME (Multipurpose Internet Mail Extensions)
   * @return True if the given MIME is JSON, false otherwise.
   */
  isJsonMime(mime) {
    if (!mime) {
      return false;
    }
    return _BaseAPI.jsonRegex.test(mime);
  }
  async request(context, initOverrides) {
    const { url, init } = await this.createFetchParams(context, initOverrides);
    const response = await this.fetchApi(url, init);
    if (response && response.status >= 200 && response.status < 300) {
      return response;
    }
    throw await createResponseErrorMessage(response, "Response returned an error code");
  }
  async createFetchParams(context, initOverrides) {
    let url = this.configuration.basePath + context.path;
    if (context.query !== void 0 && Object.keys(context.query).length !== 0) {
      url += "?" + this.configuration.queryParamsStringify(context.query);
    }
    const headers = Object.assign({}, this.configuration.headers, context.headers);
    Object.keys(headers).forEach((key) => headers[key] === void 0 ? delete headers[key] : {});
    const initOverrideFn = typeof initOverrides === "function" ? initOverrides : async () => initOverrides;
    const initParams = {
      method: context.method,
      headers,
      body: context.body,
      credentials: this.configuration.credentials
    };
    const overriddenInit = {
      ...initParams,
      ...await initOverrideFn({
        init: initParams,
        context
      })
    };
    let body;
    if (isFormData(overriddenInit.body) || overriddenInit.body instanceof URLSearchParams || isBlob(overriddenInit.body)) {
      body = overriddenInit.body;
    } else if (this.isJsonMime(headers["Content-Type"])) {
      body = JSON.stringify(overriddenInit.body);
    } else {
      body = overriddenInit.body;
    }
    const init = {
      ...overriddenInit,
      body
    };
    return { url, init };
  }
  fetchApi = async (url, init) => {
    let fetchParams = { url, init };
    for (const middleware of this.middleware) {
      if (middleware.pre) {
        fetchParams = await middleware.pre({
          fetch: this.fetchApi,
          ...fetchParams
        }) || fetchParams;
      }
    }
    let response = void 0;
    try {
      response = await (this.configuration.fetchApi || fetch)(fetchParams.url, fetchParams.init);
    } catch (e) {
      for (const middleware of this.middleware) {
        if (middleware.onError) {
          response = await middleware.onError({
            fetch: this.fetchApi,
            url: fetchParams.url,
            init: fetchParams.init,
            error: e,
            response: response ? response.clone() : void 0
          }) || response;
        }
      }
      if (response === void 0) {
        if (e instanceof Error) {
          throw new FetchError(e, "The request failed and the interceptors did not return an alternative response");
        } else {
          throw e;
        }
      }
    }
    for (const middleware of this.middleware) {
      if (middleware.post) {
        response = await middleware.post({
          fetch: this.fetchApi,
          url: fetchParams.url,
          init: fetchParams.init,
          response: response.clone()
        }) || response;
      }
    }
    return response;
  };
  /**
   * Create a shallow clone of `this` by constructing a new instance
   * and then shallow cloning data members.
   */
  clone() {
    const constructor = this.constructor;
    const next = new constructor(this.configuration);
    next.middleware = this.middleware.slice();
    return next;
  }
};
function isBlob(value) {
  return typeof Blob !== "undefined" && value instanceof Blob;
}
function isFormData(value) {
  return typeof FormData !== "undefined" && value instanceof FormData;
}
function stringToDate(value) {
  return value ? new Date(value.replace(/Z$/, "")) : /* @__PURE__ */ new Date("invalid");
}
function dateToString(value) {
  return value ? formatISO(value)?.replace(/Z$/, "") : void 0;
}
var ResponseError = class extends Error {
  constructor(response, msg, data) {
    super(msg);
    this.response = response;
    this.data = data;
  }
  name = "ResponseError";
  data;
};
var FetchError = class extends Error {
  constructor(cause, msg) {
    super(msg);
    this.cause = cause;
  }
  name = "FetchError";
};
var RequiredError = class extends Error {
  constructor(field, msg) {
    super(msg);
    this.field = field;
  }
  name = "RequiredError";
};
function querystring(params, prefix = "") {
  return Object.keys(params).map((key) => querystringSingleKey(key, params[key], prefix)).filter((part) => part.length > 0).join("&");
}
function querystringSingleKey(key, value, keyPrefix = "") {
  const fullKey = keyPrefix + (keyPrefix.length ? `[${key}]` : key);
  if (value instanceof Array) {
    const multiValue = value.map((singleValue) => encodeURIComponent(String(singleValue))).join(`&${encodeURIComponent(fullKey)}=`);
    return `${encodeURIComponent(fullKey)}=${multiValue}`;
  }
  if (value instanceof Set) {
    const valueAsArray = Array.from(value);
    return querystringSingleKey(key, valueAsArray, keyPrefix);
  }
  if (value instanceof Date) {
    return `${encodeURIComponent(fullKey)}=${encodeURIComponent(value.toISOString())}`;
  }
  if (value instanceof Object) {
    return querystring(value, fullKey);
  }
  return `${encodeURIComponent(fullKey)}=${encodeURIComponent(String(value))}`;
}
var JSONApiResponse = class {
  constructor(raw, transformer = (jsonValue) => jsonValue) {
    this.raw = raw;
    this.transformer = transformer;
  }
  async value() {
    return this.transformer(await this.raw.json());
  }
};
var TextApiResponse = class {
  constructor(raw) {
    this.raw = raw;
  }
  async value() {
    return await this.raw.text();
  }
};

// src/rentals-api-v3/models/Address.ts
function AddressFromJSON(json) {
  return AddressFromJSONTyped(json, false);
}
function AddressFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    street: json["street"] == null ? void 0 : json["street"],
    state: json["state"] == null ? void 0 : json["state"],
    city: json["city"] == null ? void 0 : json["city"],
    country: json["country"] == null ? void 0 : json["country"],
    postCode: json["postCode"] == null ? void 0 : json["postCode"]
  };
}

// src/rentals-api-v3/models/CurrencyValue.ts
function CurrencyValueFromJSON(json) {
  return CurrencyValueFromJSONTyped(json, false);
}
function CurrencyValueFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    currencyCode: json["currencyCode"],
    value: json["value"]
  };
}

// src/rentals-api-v3/models/AgentRegionSettings.ts
function AgentRegionSettingsFromJSON(json) {
  return AgentRegionSettingsFromJSONTyped(json, false);
}
function AgentRegionSettingsFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    rateType: json["rateType"] == null ? void 0 : json["rateType"],
    paymentType: json["paymentType"] == null ? void 0 : json["paymentType"],
    commission: json["commission"] == null ? void 0 : json["commission"]
  };
}

// src/rentals-api-v3/models/LeadTime1.ts
function LeadTime1FromJSON(json) {
  return LeadTime1FromJSONTyped(json, false);
}
function LeadTime1FromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    fleetTypeCode: json["fleetTypeCode"] == null ? void 0 : json["fleetTypeCode"],
    leadTime: json["leadTime"] == null ? void 0 : json["leadTime"]
  };
}

// src/rentals-api-v3/models/AgentSettings.ts
function AgentSettingsFromJSON(json) {
  return AgentSettingsFromJSONTyped(json, false);
}
function AgentSettingsFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    au: AgentRegionSettingsFromJSON(json["au"]),
    nz: AgentRegionSettingsFromJSON(json["nz"]),
    cancellationMinimum: json["cancellationMinimum"] == null ? void 0 : json["cancellationMinimum"],
    leadTimes: json["leadTimes"] == null ? void 0 : json["leadTimes"].map(LeadTime1FromJSON)
  };
}

// src/rentals-api-v3/models/AgentInfo.ts
function AgentInfoFromJSON(json) {
  return AgentInfoFromJSONTyped(json, false);
}
function AgentInfoFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    id: json["id"],
    name: json["name"],
    code: json["code"],
    parent: json["parent"] == null ? void 0 : json["parent"],
    branch: json["branch"] == null ? void 0 : json["branch"],
    type: json["type"] == null ? void 0 : json["type"],
    logo: json["logo"] == null ? void 0 : json["logo"],
    settings: json["settings"] == null ? void 0 : AgentSettingsFromJSON(json["settings"]),
    features: json["features"] == null ? void 0 : json["features"]
  };
}

// src/rentals-api-v3/models/JucyCountryCode.ts
function JucyCountryCodeFromJSON(json) {
  return JucyCountryCodeFromJSONTyped(json, false);
}
function JucyCountryCodeFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}
function JucyCountryCodeToJSON(value) {
  return value;
}

// src/rentals-api-v3/models/FleetTypeCode.ts
function FleetTypeCodeFromJSON(json) {
  return FleetTypeCodeFromJSONTyped(json, false);
}
function FleetTypeCodeFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}

// src/rentals-api-v3/models/AutoPaymentRequest.ts
function AutoPaymentRequestToJSON(json) {
  return AutoPaymentRequestToJSONTyped(json, false);
}
function AutoPaymentRequestToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    setAutoPayment: value["setAutoPayment"]
  };
}

// src/rentals-api-v3/models/ProductCategory.ts
function ProductCategoryFromJSON(json) {
  return ProductCategoryFromJSONTyped(json, false);
}
function ProductCategoryFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}

// src/rentals-api-v3/models/MandatoryProduct.ts
function MandatoryProductFromJSON(json) {
  return MandatoryProductFromJSONTyped(json, false);
}
function MandatoryProductFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    productId: json["productId"],
    sortOrder: json["sortOrder"],
    productCode: json["productCode"],
    name: json["name"],
    category: ProductCategoryFromJSON(json["category"]),
    isMandatory: json["isMandatory"],
    description: json["description"],
    price: CurrencyValueFromJSON(json["price"]),
    productType: json["productType"],
    isPercentageOnTotalCost: json["isPercentageOnTotalCost"],
    allowMultiple: json["allowMultiple"],
    commissionable: json["commissionable"],
    associatedProductCode: json["associatedProductCode"] == null ? void 0 : json["associatedProductCode"],
    rentalDays: json["rentalDays"],
    numberOfDaysCalculated: json["numberOfDaysCalculated"],
    maxPrice: json["maxPrice"] == null ? void 0 : CurrencyValueFromJSON(json["maxPrice"]),
    qty: json["qty"],
    total: CurrencyValueFromJSON(json["total"]),
    isExcluded: json["isExcluded"] == null ? void 0 : json["isExcluded"]
  };
}

// src/rentals-api-v3/models/RewardPoints1.ts
function RewardPoints1FromJSON(json) {
  return RewardPoints1FromJSONTyped(json, false);
}
function RewardPoints1FromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    identifier: json["identifier"],
    bonusPoints: json["bonusPoints"],
    pointMultiplier: json["pointMultiplier"],
    type: json["type"]
  };
}

// src/rentals-api-v3/models/FleetCategory.ts
function FleetCategoryFromJSON(json) {
  return FleetCategoryFromJSONTyped(json, false);
}
function FleetCategoryFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    name: json["name"],
    description: json["description"],
    code: json["code"],
    categoryCode: json["categoryCode"],
    campaignCode: json["campaignCode"] == null ? void 0 : json["campaignCode"],
    campaignId: json["campaignId"] == null ? void 0 : json["campaignId"],
    id: json["id"],
    type: json["type"],
    rentalDays: json["rentalDays"],
    dealText: json["dealText"] == null ? void 0 : json["dealText"],
    freeDays: json["freeDays"] == null ? void 0 : json["freeDays"],
    fleetTypeCode: json["fleetTypeCode"],
    availabilityMessage: json["availabilityMessage"],
    dailyRate: CurrencyValueFromJSON(json["dailyRate"]),
    total: CurrencyValueFromJSON(json["total"]),
    discountPercentageApplied: json["discountPercentageApplied"] == null ? void 0 : json["discountPercentageApplied"],
    mandatoryFees: json["mandatoryFees"] == null ? void 0 : json["mandatoryFees"].map(MandatoryProductFromJSON),
    minimumHirePeriod: json["minimumHirePeriod"] == null ? void 0 : json["minimumHirePeriod"],
    isAfterHourPickUp: json["isAfterHourPickUp"] == null ? void 0 : json["isAfterHourPickUp"],
    rewardPoints: json["rewardPoints"] == null ? void 0 : RewardPoints1FromJSON(json["rewardPoints"]),
    availability: json["availability"]
  };
}

// src/rentals-api-v3/models/AvailabilitySearchResponse.ts
function AvailabilitySearchResponseFromJSON(json) {
  return AvailabilitySearchResponseFromJSONTyped(json, false);
}
function AvailabilitySearchResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    pickUpLocation: json["pickUpLocation"],
    dropOffLocation: json["dropOffLocation"],
    pickUpDate: stringToDate(json["pickUpDate"]),
    dropOffDate: stringToDate(json["dropOffDate"]),
    rentalDays: json["rentalDays"],
    fleetCategories: json["fleetCategories"].map(FleetCategoryFromJSON)
  };
}

// src/rentals-api-v3/models/AvailabilityType.ts
function AvailabilityTypeFromJSON(json) {
  return AvailabilityTypeFromJSONTyped(json, false);
}
function AvailabilityTypeFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}

// src/rentals-api-v3/models/BookedWith.ts
function BookedWithFromJSON(json) {
  return BookedWithFromJSONTyped(json, false);
}
function BookedWithFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    name: json["name"],
    email: json["email"] == null ? void 0 : json["email"],
    phone: json["phone"] == null ? void 0 : json["phone"],
    logo: json["logo"] == null ? void 0 : json["logo"]
  };
}

// src/rentals-api-v3/models/InsuranceType.ts
function InsuranceTypeFromJSON(json) {
  return InsuranceTypeFromJSONTyped(json, false);
}
function InsuranceTypeFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}

// src/rentals-api-v3/models/BookingLineItem.ts
function BookingLineItemFromJSON(json) {
  return BookingLineItemFromJSONTyped(json, false);
}
function BookingLineItemFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    productId: json["productId"],
    sortOrder: json["sortOrder"],
    productCode: json["productCode"],
    name: json["name"],
    category: ProductCategoryFromJSON(json["category"]),
    isMandatory: json["isMandatory"],
    description: json["description"],
    price: CurrencyValueFromJSON(json["price"]),
    productType: json["productType"],
    isPercentageOnTotalCost: json["isPercentageOnTotalCost"],
    allowMultiple: json["allowMultiple"],
    commissionable: json["commissionable"],
    associatedProductCode: json["associatedProductCode"] == null ? void 0 : json["associatedProductCode"],
    rentalDays: json["rentalDays"],
    numberOfDaysCalculated: json["numberOfDaysCalculated"],
    maxPrice: json["maxPrice"] == null ? void 0 : CurrencyValueFromJSON(json["maxPrice"]),
    qty: json["qty"],
    total: CurrencyValueFromJSON(json["total"]),
    isExcluded: json["isExcluded"] == null ? void 0 : json["isExcluded"],
    excessType: InsuranceTypeFromJSON(json["excessType"]),
    excess: CurrencyValueFromJSON(json["excess"]),
    bond: CurrencyValueFromJSON(json["bond"])
  };
}

// src/rentals-api-v3/models/BookingPaymentInfo.ts
function BookingPaymentInfoFromJSON(json) {
  return BookingPaymentInfoFromJSONTyped(json, false);
}
function BookingPaymentInfoFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    amount: CurrencyValueFromJSON(json["amount"]),
    date: stringToDate(json["date"]),
    type: json["type"],
    merchantFeeName: json["merchantFeeName"] == null ? void 0 : json["merchantFeeName"],
    merchantFeeAmount: json["merchantFeeAmount"] == null ? void 0 : CurrencyValueFromJSON(json["merchantFeeAmount"]),
    total: json["total"] == null ? void 0 : CurrencyValueFromJSON(json["total"])
  };
}

// src/rentals-api-v3/models/BookingStatus.ts
function BookingStatusFromJSON(json) {
  return BookingStatusFromJSONTyped(json, false);
}
function BookingStatusFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}

// src/rentals-api-v3/models/DriversLicence.ts
function DriversLicenceFromJSON(json) {
  return DriversLicenceFromJSONTyped(json, false);
}
function DriversLicenceFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    expiry: json["expiry"] == null ? void 0 : stringToDate(json["expiry"]),
    country: json["country"],
    number: json["number"] == null ? void 0 : json["number"]
  };
}

// src/rentals-api-v3/models/HirerDetails.ts
function HirerDetailsFromJSON(json) {
  return HirerDetailsFromJSONTyped(json, false);
}
function HirerDetailsFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    firstName: json["firstName"],
    lastName: json["lastName"],
    email: json["email"],
    mobileNumber: json["mobileNumber"],
    address: json["address"] == null ? void 0 : AddressFromJSON(json["address"]),
    dateOfBirth: json["dateOfBirth"] == null ? void 0 : stringToDate(json["dateOfBirth"]),
    driversLicence: json["driversLicence"] == null ? void 0 : DriversLicenceFromJSON(json["driversLicence"]),
    lucyClubNumber: json["lucyClubNumber"] == null ? void 0 : json["lucyClubNumber"],
    mailingList: json["mailingList"] == null ? void 0 : json["mailingList"],
    arrivalFlightNumber: json["arrivalFlightNumber"] == null ? void 0 : json["arrivalFlightNumber"],
    departureFlightNumber: json["departureFlightNumber"] == null ? void 0 : json["departureFlightNumber"],
    numberOfPeople: json["numberOfPeople"] == null ? void 0 : json["numberOfPeople"],
    loyalty: json["loyalty"] == null ? void 0 : json["loyalty"]
  };
}

// src/rentals-api-v3/models/Booking.ts
function BookingFromJSON(json) {
  return BookingFromJSONTyped(json, false);
}
function BookingFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    quoteId: json["quoteId"],
    reservationId: json["reservationId"],
    reservationReference: json["reservationReference"],
    agentBalanceDue: json["agentBalanceDue"] == null ? void 0 : CurrencyValueFromJSON(json["agentBalanceDue"]),
    agentCollects: json["agentCollects"] == null ? void 0 : CurrencyValueFromJSON(json["agentCollects"]),
    agentCommission: json["agentCommission"] == null ? void 0 : CurrencyValueFromJSON(json["agentCommission"]),
    voucherReference: json["voucherReference"] == null ? void 0 : json["voucherReference"],
    agentName: json["agentName"] == null ? void 0 : json["agentName"],
    balanceDue: json["balanceDue"] == null ? void 0 : CurrencyValueFromJSON(json["balanceDue"]),
    payments: json["payments"] == null ? void 0 : json["payments"].map(BookingPaymentInfoFromJSON),
    status: BookingStatusFromJSON(json["status"]),
    pickUpLocation: json["pickUpLocation"],
    dropOffLocation: json["dropOffLocation"],
    dateCreated: stringToDate(json["dateCreated"]),
    pickUpDate: stringToDate(json["pickUpDate"]),
    dropOffDate: stringToDate(json["dropOffDate"]),
    rentalDays: json["rentalDays"],
    totalPrice: CurrencyValueFromJSON(json["totalPrice"]),
    driverAge: json["driverAge"],
    lines: json["lines"] == null ? void 0 : json["lines"].map(BookingLineItemFromJSON),
    hirerDetails: json["hirerDetails"] == null ? void 0 : HirerDetailsFromJSON(json["hirerDetails"]),
    fleetCategory: FleetCategoryFromJSON(json["fleetCategory"]),
    referrerCode: json["referrerCode"] == null ? void 0 : json["referrerCode"]
  };
}

// src/rentals-api-v3/models/InsuranceProduct.ts
function InsuranceProductFromJSON(json) {
  return InsuranceProductFromJSONTyped(json, false);
}
function InsuranceProductFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    productId: json["productId"],
    sortOrder: json["sortOrder"],
    productCode: json["productCode"],
    name: json["name"],
    category: ProductCategoryFromJSON(json["category"]),
    isMandatory: json["isMandatory"],
    description: json["description"],
    price: CurrencyValueFromJSON(json["price"]),
    productType: json["productType"],
    isPercentageOnTotalCost: json["isPercentageOnTotalCost"],
    allowMultiple: json["allowMultiple"],
    commissionable: json["commissionable"],
    associatedProductCode: json["associatedProductCode"] == null ? void 0 : json["associatedProductCode"],
    rentalDays: json["rentalDays"],
    numberOfDaysCalculated: json["numberOfDaysCalculated"],
    maxPrice: json["maxPrice"] == null ? void 0 : CurrencyValueFromJSON(json["maxPrice"]),
    excessType: InsuranceTypeFromJSON(json["excessType"]),
    excess: CurrencyValueFromJSON(json["excess"]),
    bond: CurrencyValueFromJSON(json["bond"]),
    rcmId: json["rcmId"] == null ? void 0 : json["rcmId"]
  };
}

// src/rentals-api-v3/models/InsuranceProductGroup.ts
function InsuranceProductGroupFromJSON(json) {
  return InsuranceProductGroupFromJSONTyped(json, false);
}
function InsuranceProductGroupFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    group: json["group"],
    webCategory: json["webCategory"],
    required: json["required"],
    allowMultiple: json["allowMultiple"],
    description: json["description"],
    items: json["items"].map(InsuranceProductFromJSON)
  };
}

// src/rentals-api-v3/models/SecondaryProduct.ts
function SecondaryProductFromJSON(json) {
  return SecondaryProductFromJSONTyped(json, false);
}
function SecondaryProductFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    productId: json["productId"],
    sortOrder: json["sortOrder"],
    productCode: json["productCode"],
    name: json["name"],
    category: json["category"],
    isMandatory: json["isMandatory"],
    description: json["description"],
    price: CurrencyValueFromJSON(json["price"]),
    productType: json["productType"],
    isPercentageOnTotalCost: json["isPercentageOnTotalCost"],
    allowMultiple: json["allowMultiple"],
    commissionable: json["commissionable"],
    associatedProductCode: json["associatedProductCode"] == null ? void 0 : json["associatedProductCode"],
    rentalDays: json["rentalDays"],
    numberOfDaysCalculated: json["numberOfDaysCalculated"],
    maxPrice: json["maxPrice"] == null ? void 0 : CurrencyValueFromJSON(json["maxPrice"])
  };
}

// src/rentals-api-v3/models/ProductBundle.ts
function ProductBundleFromJSON(json) {
  return ProductBundleFromJSONTyped(json, false);
}
function ProductBundleFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    productCode: json["productCode"],
    bundledProducts: json["bundledProducts"].map(SecondaryProductFromJSON)
  };
}

// src/rentals-api-v3/models/SecondaryProductGroup.ts
function SecondaryProductGroupFromJSON(json) {
  return SecondaryProductGroupFromJSONTyped(json, false);
}
function SecondaryProductGroupFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    group: json["group"],
    webCategory: json["webCategory"],
    required: json["required"],
    allowMultiple: json["allowMultiple"],
    description: json["description"],
    items: json["items"].map(SecondaryProductFromJSON)
  };
}

// src/rentals-api-v3/models/BookingOptions.ts
function BookingOptionsFromJSON(json) {
  return BookingOptionsFromJSONTyped(json, false);
}
function BookingOptionsFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    secondaryProducts: json["secondaryProducts"].map(SecondaryProductGroupFromJSON),
    insuranceProducts: InsuranceProductGroupFromJSON(json["insuranceProducts"]),
    bundles: json["bundles"] == null ? void 0 : json["bundles"].map(ProductBundleFromJSON)
  };
}

// src/rentals-api-v3/models/BookingSummaryHirer.ts
function BookingSummaryHirerFromJSON(json) {
  return BookingSummaryHirerFromJSONTyped(json, false);
}
function BookingSummaryHirerFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    firstName: json["firstName"],
    lastName: json["lastName"],
    email: json["email"],
    checkedIn: json["checkedIn"]
  };
}

// src/rentals-api-v3/models/Brand.ts
function BrandFromJSON(json) {
  return BrandFromJSONTyped(json, false);
}
function BrandFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}

// src/rentals-api-v3/models/QuoteAvailability.ts
function QuoteAvailabilityFromJSON(json) {
  return QuoteAvailabilityFromJSONTyped(json, false);
}
function QuoteAvailabilityFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}

// src/rentals-api-v3/models/QuoteStatus.ts
function QuoteStatusFromJSON(json) {
  return QuoteStatusFromJSONTyped(json, false);
}
function QuoteStatusFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}

// src/rentals-api-v3/models/BookingSummary.ts
function BookingSummaryFromJSON(json) {
  return BookingSummaryFromJSONTyped(json, false);
}
function BookingSummaryFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    id: json["id"],
    ref: json["ref"],
    bookingNo: json["bookingNo"],
    reservationId: json["reservationId"],
    voucherRef: json["voucherRef"] == null ? void 0 : json["voucherRef"],
    created: stringToDate(json["created"]),
    updated: stringToDate(json["updated"]),
    status: QuoteStatusFromJSON(json["status"]),
    availability: QuoteAvailabilityFromJSON(json["availability"]),
    hirer: BookingSummaryHirerFromJSON(json["hirer"]),
    pickUpSiteCode: json["pickUpSiteCode"],
    pickUpSiteName: json["pickUpSiteName"],
    dropOffSiteCode: json["dropOffSiteCode"],
    dropOffSiteName: json["dropOffSiteName"],
    rentalDays: json["rentalDays"],
    pickUpDate: stringToDate(json["pickUpDate"]),
    dropOffDate: stringToDate(json["dropOffDate"]),
    disableCheckIn: json["disableCheckIn"],
    bookedWith: json["bookedWith"] == null ? void 0 : BookedWithFromJSON(json["bookedWith"]),
    brand: BrandFromJSON(json["brand"])
  };
}

// src/rentals-api-v3/models/BookingSummaryResponse.ts
function BookingSummaryResponseFromJSON(json) {
  return BookingSummaryResponseFromJSONTyped(json, false);
}
function BookingSummaryResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    items: json["items"].map(BookingSummaryFromJSON)
  };
}

// src/rentals-api-v3/models/BookingSummaryStatus.ts
function BookingSummaryStatusFromJSON(json) {
  return BookingSummaryStatusFromJSONTyped(json, false);
}
function BookingSummaryStatusFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}

// src/rentals-api-v3/models/BookingType.ts
function BookingTypeToJSON(value) {
  return value;
}

// src/rentals-api-v3/models/BranchContentPages.ts
function BranchContentPagesFromJSON(json) {
  return BranchContentPagesFromJSONTyped(json, false);
}
function BranchContentPagesFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    car: json["car"] == null ? void 0 : json["car"],
    camper: json["camper"] == null ? void 0 : json["camper"],
    motorhome: json["motorhome"] == null ? void 0 : json["motorhome"]
  };
}

// src/rentals-api-v3/models/BranchContent.ts
function BranchContentFromJSON(json) {
  return BranchContentFromJSONTyped(json, false);
}
function BranchContentFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    id: json["id"],
    name: json["name"],
    code: json["code"],
    mapInfo: json["mapInfo"],
    title: json["title"],
    content: json["content"],
    pages: BranchContentPagesFromJSON(json["pages"]),
    brand: json["brand"],
    timezone: json["timezone"],
    countryCode: JucyCountryCodeFromJSON(json["countryCode"])
  };
}

// src/rentals-api-v3/models/BusinessUnit.ts
function BusinessUnitFromJSON(json) {
  return BusinessUnitFromJSONTyped(json, false);
}
function BusinessUnitFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    name: json["name"],
    guid: json["guid"],
    code: json["code"],
    countryCode: json["countryCode"],
    salesTaxPercentage: json["salesTaxPercentage"],
    brand: json["brand"]
  };
}

// src/rentals-api-v3/models/CancelReservationResponse.ts
function CancelReservationResponseFromJSON(json) {
  return CancelReservationResponseFromJSONTyped(json, false);
}
function CancelReservationResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    message: json["message"],
    reservationRef: json["reservationRef"]
  };
}

// src/rentals-api-v3/models/CatalogFloorPlan.ts
function CatalogFloorPlanFromJSON(json) {
  return CatalogFloorPlanFromJSONTyped(json, false);
}
function CatalogFloorPlanFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    ...json,
    name: json["name"],
    url: json["url"]
  };
}

// src/rentals-api-v3/models/CatalogItemPage.ts
function CatalogItemPageFromJSON(json) {
  return CatalogItemPageFromJSONTyped(json, false);
}
function CatalogItemPageFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    ...json,
    locale: json["locale"],
    title: json["title"] == null ? void 0 : json["title"],
    url: json["url"]
  };
}

// src/rentals-api-v3/models/ExcessReductionFeatureSchemaBasic.ts
function ExcessReductionFeatureSchemaBasicFromJSON(json) {
  return ExcessReductionFeatureSchemaBasicFromJSONTyped(json, false);
}
function ExcessReductionFeatureSchemaBasicFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}

// src/rentals-api-v3/models/ExcessReductionFeatureType.ts
function ExcessReductionFeatureTypeFromJSON(json) {
  return ExcessReductionFeatureTypeFromJSONTyped(json, false);
}
function ExcessReductionFeatureTypeFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}

// src/rentals-api-v3/models/ExcessReductionFeatureSchema.ts
function ExcessReductionFeatureSchemaFromJSON(json) {
  return ExcessReductionFeatureSchemaFromJSONTyped(json, false);
}
function ExcessReductionFeatureSchemaFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    ...json,
    name: json["name"],
    type: ExcessReductionFeatureTypeFromJSON(json["type"]),
    basic: ExcessReductionFeatureSchemaBasicFromJSON(json["basic"]),
    standard: ExcessReductionFeatureSchemaBasicFromJSON(json["standard"]),
    full: ExcessReductionFeatureSchemaBasicFromJSON(json["full"])
  };
}

// src/rentals-api-v3/models/FeatureItem.ts
function FeatureItemFromJSON(json) {
  return FeatureItemFromJSONTyped(json, false);
}
function FeatureItemFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    ...json,
    title: json["title"],
    type: json["type"],
    icon: json["icon"] == null ? void 0 : json["icon"],
    iconClass: json["iconClass"] == null ? void 0 : json["iconClass"]
  };
}

// src/rentals-api-v3/models/FeatureList.ts
function FeatureListFromJSON(json) {
  return FeatureListFromJSONTyped(json, false);
}
function FeatureListFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    ...json,
    title: json["title"],
    content: json["content"] == null ? void 0 : json["content"],
    items: json["items"]
  };
}

// src/rentals-api-v3/models/GalleryItemType.ts
function GalleryItemTypeFromJSON(json) {
  return GalleryItemTypeFromJSONTyped(json, false);
}
function GalleryItemTypeFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}

// src/rentals-api-v3/models/GalleryItem.ts
function GalleryItemFromJSON(json) {
  return GalleryItemFromJSONTyped(json, false);
}
function GalleryItemFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    ...json,
    originalAlt: json["originalAlt"] == null ? void 0 : json["originalAlt"],
    original: json["original"],
    thumbnail: json["thumbnail"],
    type: GalleryItemTypeFromJSON(json["type"]),
    carousel: json["carousel"]
  };
}

// src/rentals-api-v3/models/CatalogItem.ts
function CatalogItemFromJSON(json) {
  return CatalogItemFromJSONTyped(json, false);
}
function CatalogItemFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    ...json,
    id: json["id"],
    code: json["code"],
    name: json["name"],
    region: json["region"],
    fleetType: json["fleetType"],
    fleetTypeId: json["fleetTypeId"],
    fleetTypeSlug: json["fleetTypeSlug"],
    description: json["description"],
    shortDescription: json["shortDescription"],
    featuresDescription: json["featuresDescription"],
    highlightedFeatures: json["highlightedFeatures"] == null ? void 0 : json["highlightedFeatures"],
    vehicleDescription: json["vehicleDescription"] == null ? void 0 : json["vehicleDescription"],
    seatCount: json["seatCount"] == null ? void 0 : json["seatCount"],
    sleepCount: json["sleepCount"] == null ? void 0 : json["sleepCount"],
    sleepDescription: json["sleepDescription"] == null ? void 0 : json["sleepDescription"],
    transmission: json["transmission"] == null ? void 0 : json["transmission"],
    engineSize: json["engineSize"] == null ? void 0 : json["engineSize"],
    bodyStyle: json["bodyStyle"] == null ? void 0 : json["bodyStyle"],
    weight: json["weight"] == null ? void 0 : json["weight"],
    length: json["length"] == null ? void 0 : json["length"],
    width: json["width"] == null ? void 0 : json["width"],
    luggageSmall: json["luggageSmall"] == null ? void 0 : json["luggageSmall"],
    luggageLarge: json["luggageLarge"] == null ? void 0 : json["luggageLarge"],
    excessReductionFeatures: json["excessReductionFeatures"].map(ExcessReductionFeatureSchemaFromJSON),
    excessReductionDescription: json["excessReductionDescription"],
    specificationsDescription: json["specificationsDescription"],
    specifications: json["specifications"],
    scenicImage: json["scenicImage"] == null ? void 0 : json["scenicImage"],
    studioImage: json["studioImage"] == null ? void 0 : json["studioImage"],
    emailImage: json["emailImage"] == null ? void 0 : json["emailImage"],
    floorPlans: json["floorPlans"] == null ? void 0 : json["floorPlans"].map(CatalogFloorPlanFromJSON),
    gallery: json["gallery"].map(GalleryItemFromJSON),
    keyFeatures: json["keyFeatures"].map(FeatureItemFromJSON),
    includedWith: json["includedWith"].map(FeatureItemFromJSON),
    features: json["features"].map(FeatureListFromJSON),
    pages: json["pages"].map(CatalogItemPageFromJSON),
    maxOccupants: json["maxOccupants"] == null ? void 0 : json["maxOccupants"]
  };
}

// src/rentals-api-v3/models/CommissionStatus.ts
function CommissionStatusFromJSON(json) {
  return CommissionStatusFromJSONTyped(json, false);
}
function CommissionStatusFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}

// src/rentals-api-v3/models/Commission.ts
function CommissionFromJSON(json) {
  return CommissionFromJSONTyped(json, false);
}
function CommissionFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    quoteId: json["quoteId"],
    reservationId: json["reservationId"],
    reservationReference: json["reservationReference"],
    bookingNo: json["bookingNo"],
    region: JucyCountryCodeFromJSON(json["region"]),
    dateCreated: stringToDate(json["dateCreated"]),
    pickUpDate: stringToDate(json["pickUpDate"]),
    commission: CurrencyValueFromJSON(json["commission"]),
    campaignCode: json["campaignCode"] == null ? void 0 : json["campaignCode"],
    referrerCode: json["referrerCode"] == null ? void 0 : json["referrerCode"],
    status: CommissionStatusFromJSON(json["status"])
  };
}

// src/rentals-api-v3/models/CreateBookingResponseStatus.ts
function CreateBookingResponseStatusFromJSON(json) {
  return CreateBookingResponseStatusFromJSONTyped(json, false);
}
function CreateBookingResponseStatusFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}

// src/rentals-api-v3/models/ConvertQuoteResponse.ts
function ConvertQuoteResponseFromJSON(json) {
  return ConvertQuoteResponseFromJSONTyped(json, false);
}
function ConvertQuoteResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    paymentUrl: json["paymentUrl"] == null ? void 0 : json["paymentUrl"],
    status: CreateBookingResponseStatusFromJSON(json["status"]),
    reference: json["reference"],
    bookingNo: json["bookingNo"]
  };
}

// src/rentals-api-v3/models/CountryData.ts
function CountryDataFromJSON(json) {
  return CountryDataFromJSONTyped(json, false);
}
function CountryDataFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    ...json,
    code: json["code"],
    name: json["name"],
    phonePrefix: json["phonePrefix"],
    suggested: json["suggested"] == null ? void 0 : json["suggested"],
    licensePermitExempt: json["licensePermitExempt"] == null ? void 0 : json["licensePermitExempt"]
  };
}

// src/rentals-api-v3/models/HirerDetailsSummary.ts
function HirerDetailsSummaryToJSON(json) {
  return HirerDetailsSummaryToJSONTyped(json, false);
}
function HirerDetailsSummaryToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    firstName: value["firstName"],
    lastName: value["lastName"],
    mobileNumber: value["mobileNumber"],
    email: value["email"],
    driversLicenceCountry: value["driversLicenceCountry"],
    acceptedTerms: value["acceptedTerms"],
    mailingList: value["mailingList"],
    loyalty: value["loyalty"],
    dateOfBirth: value["dateOfBirth"] == null ? void 0 : dateToString(value["dateOfBirth"])?.substring(0, 10)
  };
}

// src/rentals-api-v3/models/CreateBookingRequest.ts
function CreateBookingRequestToJSON(json) {
  return CreateBookingRequestToJSONTyped(json, false);
}
function CreateBookingRequestToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    pickUpLocation: value["pickUpLocation"],
    dropOffLocation: value["dropOffLocation"],
    pickUpDate: value["pickUpDate"],
    dropOffDate: value["dropOffDate"],
    fleetCategory: value["fleetCategory"],
    driverAge: value["driverAge"],
    numberOfPeople: value["numberOfPeople"],
    campaignCode: value["campaignCode"],
    pascal: value["pascal"],
    type: BookingTypeToJSON(value["type"]),
    insurance: value["insurance"],
    secondaryProducts: value["secondaryProducts"],
    hirerDetails: HirerDetailsSummaryToJSON(value["hirerDetails"]),
    voucherReference: value["voucherReference"],
    agentName: value["agentName"],
    gateway: value["gateway"],
    payment: value["payment"]
  };
}

// src/rentals-api-v3/models/CreateBookingResponse.ts
function CreateBookingResponseFromJSON(json) {
  return CreateBookingResponseFromJSONTyped(json, false);
}
function CreateBookingResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    booking: BookingFromJSON(json["booking"]),
    status: CreateBookingResponseStatusFromJSON(json["status"]),
    convertUrl: json["convertUrl"] == null ? void 0 : json["convertUrl"],
    reference: json["reference"],
    bookingNo: json["bookingNo"],
    paymentToken: json["paymentToken"] == null ? void 0 : json["paymentToken"]
  };
}

// src/rentals-api-v3/models/ExchangeRates.ts
function ExchangeRatesFromJSON(json) {
  return ExchangeRatesFromJSONTyped(json, false);
}
function ExchangeRatesFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    nZD: json["NZD"],
    aUD: json["AUD"],
    eUR: json["EUR"],
    cAD: json["CAD"],
    uSD: json["USD"],
    gBP: json["GBP"],
    cHF: json["CHF"],
    jPY: json["JPY"],
    kRW: json["KRW"],
    sGD: json["SGD"],
    cNY: json["CNY"],
    aRS: json["ARS"],
    hKD: json["HKD"],
    mYR: json["MYR"],
    iNR: json["INR"]
  };
}

// src/rentals-api-v3/models/CurrencyRateResult.ts
function CurrencyRateResultFromJSON(json) {
  return CurrencyRateResultFromJSONTyped(json, false);
}
function CurrencyRateResultFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    nZD: ExchangeRatesFromJSON(json["NZD"]),
    aUD: ExchangeRatesFromJSON(json["AUD"])
  };
}

// src/rentals-api-v3/models/EmailQuoteRequest.ts
function EmailQuoteRequestToJSON(json) {
  return EmailQuoteRequestToJSONTyped(json, false);
}
function EmailQuoteRequestToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    pickUpLocation: value["pickUpLocation"],
    dropOffLocation: value["dropOffLocation"],
    pickUpDate: value["pickUpDate"],
    dropOffDate: value["dropOffDate"],
    fleetCategory: value["fleetCategory"],
    driverAge: value["driverAge"],
    numberOfPeople: value["numberOfPeople"],
    campaignCode: value["campaignCode"],
    pascal: value["pascal"],
    insurance: value["insurance"],
    secondaryProducts: value["secondaryProducts"],
    hirerDetails: HirerDetailsSummaryToJSON(value["hirerDetails"]),
    voucherReference: value["voucherReference"],
    reservationReference: value["reservationReference"]
  };
}

// src/rentals-api-v3/models/FerryAccommodationProduct.ts
function FerryAccommodationProductFromJSON(json) {
  return FerryAccommodationProductFromJSONTyped(json, false);
}
function FerryAccommodationProductFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    productId: json["productId"],
    sortOrder: json["sortOrder"],
    productCode: json["productCode"],
    name: json["name"],
    category: json["category"],
    isMandatory: json["isMandatory"],
    description: json["description"],
    price: CurrencyValueFromJSON(json["price"]),
    productType: json["productType"],
    isPercentageOnTotalCost: json["isPercentageOnTotalCost"],
    allowMultiple: json["allowMultiple"],
    commissionable: json["commissionable"],
    associatedProductCode: json["associatedProductCode"] == null ? void 0 : json["associatedProductCode"],
    rentalDays: json["rentalDays"],
    numberOfDaysCalculated: json["numberOfDaysCalculated"],
    maxPrice: json["maxPrice"] == null ? void 0 : CurrencyValueFromJSON(json["maxPrice"]),
    berths: json["berths"],
    maxAdults: json["maxAdults"],
    maxOccupancy: json["maxOccupancy"],
    minOccupancy: json["minOccupancy"],
    quantityAvailable: json["quantityAvailable"]
  };
}

// src/rentals-api-v3/models/FerryPassenger.ts
function FerryPassengerFromJSON(json) {
  return FerryPassengerFromJSONTyped(json, false);
}
function FerryPassengerFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    firstName: json["firstName"],
    lastName: json["lastName"],
    phoneNumber: json["phoneNumber"] == null ? void 0 : json["phoneNumber"],
    age: json["age"] == null ? void 0 : json["age"],
    type: json["type"]
  };
}
function FerryPassengerToJSON(json) {
  return FerryPassengerToJSONTyped(json, false);
}
function FerryPassengerToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    firstName: value["firstName"],
    lastName: value["lastName"],
    phoneNumber: value["phoneNumber"],
    age: value["age"],
    type: value["type"]
  };
}

// src/rentals-api-v3/models/FerryPort.ts
function FerryPortFromJSON(json) {
  return FerryPortFromJSONTyped(json, false);
}
function FerryPortFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    code: json["code"],
    name: json["name"],
    country: json["country"] == null ? void 0 : json["country"],
    lat: json["lat"] == null ? void 0 : json["lat"],
    lng: json["lng"] == null ? void 0 : json["lng"]
  };
}

// src/rentals-api-v3/models/FerryProductLine.ts
function FerryProductLineFromJSON(json) {
  return FerryProductLineFromJSONTyped(json, false);
}
function FerryProductLineFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    ...json,
    productId: json["productId"],
    sortOrder: json["sortOrder"],
    productCode: json["productCode"],
    name: json["name"],
    category: ProductCategoryFromJSON(json["category"]),
    isMandatory: json["isMandatory"],
    description: json["description"],
    price: CurrencyValueFromJSON(json["price"]),
    productType: json["productType"],
    isPercentageOnTotalCost: json["isPercentageOnTotalCost"],
    allowMultiple: json["allowMultiple"],
    commissionable: json["commissionable"],
    associatedProductCode: json["associatedProductCode"] == null ? void 0 : json["associatedProductCode"],
    rentalDays: json["rentalDays"],
    numberOfDaysCalculated: json["numberOfDaysCalculated"],
    maxPrice: json["maxPrice"] == null ? void 0 : CurrencyValueFromJSON(json["maxPrice"]),
    qty: json["qty"],
    total: CurrencyValueFromJSON(json["total"])
  };
}

// src/rentals-api-v3/models/FerrySailingTripAllOfId.ts
function FerrySailingTripAllOfIdFromJSON(json) {
  return FerrySailingTripAllOfIdFromJSONTyped(json, false);
}
function FerrySailingTripAllOfIdFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}

// src/rentals-api-v3/models/FerryShip.ts
function FerryShipFromJSON(json) {
  return FerryShipFromJSONTyped(json, false);
}
function FerryShipFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    name: json["name"]
  };
}

// src/rentals-api-v3/models/FerrySailingBooking.ts
function FerrySailingBookingFromJSON(json) {
  return FerrySailingBookingFromJSONTyped(json, false);
}
function FerrySailingBookingFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    id: FerrySailingTripAllOfIdFromJSON(json["id"]),
    isAvailable: json["isAvailable"],
    code: json["code"],
    departPort: FerryPortFromJSON(json["departPort"]),
    departDateTime: stringToDate(json["departDateTime"]),
    destinationPort: FerryPortFromJSON(json["destinationPort"]),
    arriveDateTime: stringToDate(json["arriveDateTime"]),
    ship: FerryShipFromJSON(json["ship"]),
    lines: json["lines"].map(FerryProductLineFromJSON)
  };
}

// src/rentals-api-v3/models/FerryBooking.ts
function FerryBookingFromJSON(json) {
  return FerryBookingFromJSONTyped(json, false);
}
function FerryBookingFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    reference: json["reference"],
    rentalReservationReference: json["rentalReservationReference"],
    bookingNo: json["bookingNo"] == null ? void 0 : json["bookingNo"],
    status: CreateBookingResponseStatusFromJSON(json["status"]),
    passengers: json["passengers"].map(FerryPassengerFromJSON),
    outboundTrip: FerrySailingBookingFromJSON(json["outboundTrip"]),
    returnTrip: json["returnTrip"] == null ? void 0 : FerrySailingBookingFromJSON(json["returnTrip"]),
    contactDetails: json["contactDetails"] == null ? void 0 : HirerDetailsFromJSON(json["contactDetails"]),
    subTotal: CurrencyValueFromJSON(json["subTotal"]),
    merchantFee: json["merchantFee"] == null ? void 0 : CurrencyValueFromJSON(json["merchantFee"]),
    total: CurrencyValueFromJSON(json["total"]),
    lastUpdated: json["lastUpdated"] == null ? void 0 : stringToDate(json["lastUpdated"])
  };
}

// src/rentals-api-v3/models/FerryCreateQuoteRequest.ts
function FerryCreateQuoteRequestToJSON(json) {
  return FerryCreateQuoteRequestToJSONTyped(json, false);
}
function FerryCreateQuoteRequestToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    outboundTrip: value["outboundTrip"],
    returnTrip: value["returnTrip"],
    passengers: value["passengers"].map(FerryPassengerToJSON),
    gateway: value["gateway"],
    returnUrl: value["returnUrl"],
    failedUrl: value["failedUrl"]
  };
}

// src/rentals-api-v3/models/FerryMessage.ts
function FerryMessageFromJSON(json) {
  return FerryMessageFromJSONTyped(json, false);
}
function FerryMessageFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    code: json["code"] == null ? void 0 : json["code"],
    description: json["description"],
    severity: json["severity"] == null ? void 0 : json["severity"]
  };
}

// src/rentals-api-v3/models/FerryGetBookingResponse.ts
function FerryGetBookingResponseFromJSON(json) {
  return FerryGetBookingResponseFromJSONTyped(json, false);
}
function FerryGetBookingResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    messages: json["messages"] == null ? void 0 : json["messages"].map(FerryMessageFromJSON),
    ferryBooking: FerryBookingFromJSON(json["ferryBooking"]),
    status: CreateBookingResponseStatusFromJSON(json["status"]),
    convertUrl: json["convertUrl"] == null ? void 0 : json["convertUrl"],
    reference: json["reference"],
    bookingNo: json["bookingNo"] == null ? void 0 : json["bookingNo"],
    paymentToken: json["paymentToken"] == null ? void 0 : json["paymentToken"]
  };
}

// src/rentals-api-v3/models/FerryGetQuoteResponse.ts
function FerryGetQuoteResponseFromJSON(json) {
  return FerryGetQuoteResponseFromJSONTyped(json, false);
}
function FerryGetQuoteResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    messages: json["messages"] == null ? void 0 : json["messages"].map(FerryMessageFromJSON),
    ferryBooking: FerryBookingFromJSON(json["ferryBooking"]),
    status: CreateBookingResponseStatusFromJSON(json["status"]),
    reference: json["reference"],
    bookingNo: json["bookingNo"] == null ? void 0 : json["bookingNo"]
  };
}

// src/rentals-api-v3/models/FerryGetSailingsRequestAllOfOutboundTrip.ts
function FerryGetSailingsRequestAllOfOutboundTripToJSON(json) {
  return FerryGetSailingsRequestAllOfOutboundTripToJSONTyped(json, false);
}
function FerryGetSailingsRequestAllOfOutboundTripToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    departPort: value["departPort"],
    destinationPort: value["destinationPort"],
    sailingDate: value["sailingDate"],
    fareType: value["fareType"]
  };
}

// src/rentals-api-v3/models/FerryGetSailingsRequestAllOfPassengers.ts
function FerryGetSailingsRequestAllOfPassengersToJSON(json) {
  return FerryGetSailingsRequestAllOfPassengersToJSONTyped(json, false);
}
function FerryGetSailingsRequestAllOfPassengersToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    adults: value["adults"],
    children: value["children"],
    infants: value["infants"]
  };
}

// src/rentals-api-v3/models/FerryGetSailingsRequestAllOfReturnTrip.ts
function FerryGetSailingsRequestAllOfReturnTripToJSON(json) {
  return FerryGetSailingsRequestAllOfReturnTripToJSONTyped(json, false);
}
function FerryGetSailingsRequestAllOfReturnTripToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    departPort: value["departPort"],
    destinationPort: value["destinationPort"],
    sailingDate: value["sailingDate"],
    fareType: value["fareType"]
  };
}

// src/rentals-api-v3/models/FerryGetSailingsRequest.ts
function FerryGetSailingsRequestToJSON(json) {
  return FerryGetSailingsRequestToJSONTyped(json, false);
}
function FerryGetSailingsRequestToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    outboundTrip: FerryGetSailingsRequestAllOfOutboundTripToJSON(value["outboundTrip"]),
    returnTrip: FerryGetSailingsRequestAllOfReturnTripToJSON(value["returnTrip"]),
    passengers: FerryGetSailingsRequestAllOfPassengersToJSON(value["passengers"])
  };
}

// src/rentals-api-v3/models/FerrySailingOrder.ts
function FerrySailingOrderFromJSON(json) {
  return FerrySailingOrderFromJSONTyped(json, false);
}
function FerrySailingOrderFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    id: FerrySailingTripAllOfIdFromJSON(json["id"]),
    isAvailable: json["isAvailable"],
    code: json["code"],
    departPort: FerryPortFromJSON(json["departPort"]),
    departDateTime: stringToDate(json["departDateTime"]),
    destinationPort: FerryPortFromJSON(json["destinationPort"]),
    arriveDateTime: stringToDate(json["arriveDateTime"]),
    ship: FerryShipFromJSON(json["ship"]),
    lines: json["lines"].map(FerryProductLineFromJSON),
    options: json["options"].map(SecondaryProductFromJSON),
    accommodationOptions: json["accommodationOptions"].map(FerryAccommodationProductFromJSON),
    total: CurrencyValueFromJSON(json["total"])
  };
}

// src/rentals-api-v3/models/FerryGetSailingsResponse.ts
function FerryGetSailingsResponseFromJSON(json) {
  return FerryGetSailingsResponseFromJSONTyped(json, false);
}
function FerryGetSailingsResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    total: CurrencyValueFromJSON(json["total"]),
    outboundTrip: FerrySailingOrderFromJSON(json["outboundTrip"]),
    returnTrip: json["returnTrip"] == null ? void 0 : FerrySailingOrderFromJSON(json["returnTrip"])
  };
}

// src/rentals-api-v3/models/FerryGetStatusResponse.ts
function FerryGetStatusResponseFromJSON(json) {
  return FerryGetStatusResponseFromJSONTyped(json, false);
}
function FerryGetStatusResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    available: json["available"],
    reservationRef: json["reservationRef"],
    region: json["region"],
    status: json["status"]
  };
}

// src/rentals-api-v3/models/FerryOptions.ts
function FerryOptionsFromJSON(json) {
  return FerryOptionsFromJSONTyped(json, false);
}
function FerryOptionsFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    vehicleProducts: json["vehicleProducts"].map(SecondaryProductFromJSON),
    passengerProducts: json["passengerProducts"].map(SecondaryProductFromJSON),
    accommodationProducts: json["accommodationProducts"].map(SecondaryProductFromJSON)
  };
}

// src/rentals-api-v3/models/FerryRoute.ts
function FerryRouteFromJSON(json) {
  return FerryRouteFromJSONTyped(json, false);
}
function FerryRouteFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    code: json["code"],
    departPort: FerryPortFromJSON(json["departPort"]),
    destinationPort: FerryPortFromJSON(json["destinationPort"])
  };
}

// src/rentals-api-v3/models/FerrySailing.ts
function FerrySailingFromJSON(json) {
  return FerrySailingFromJSONTyped(json, false);
}
function FerrySailingFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    code: json["code"],
    departPort: FerryPortFromJSON(json["departPort"]),
    departDateTime: stringToDate(json["departDateTime"]),
    destinationPort: FerryPortFromJSON(json["destinationPort"]),
    arriveDateTime: stringToDate(json["arriveDateTime"]),
    ship: FerryShipFromJSON(json["ship"])
  };
}

// src/rentals-api-v3/models/FerryUpdateQuoteRequest.ts
function FerryUpdateQuoteRequestToJSON(json) {
  return FerryUpdateQuoteRequestToJSONTyped(json, false);
}
function FerryUpdateQuoteRequestToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    outboundTrip: value["outboundTrip"],
    returnTrip: value["returnTrip"],
    passengers: value["passengers"].map(FerryPassengerToJSON),
    gateway: value["gateway"],
    returnUrl: value["returnUrl"],
    failedUrl: value["failedUrl"]
  };
}

// src/rentals-api-v3/models/FindReservationRequest.ts
function FindReservationRequestToJSON(json) {
  return FindReservationRequestToJSONTyped(json, false);
}
function FindReservationRequestToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    lastName: value["lastName"],
    bookingNumber: value["bookingNumber"],
    pickUpBranchCode: value["pickUpBranchCode"]
  };
}

// src/rentals-api-v3/models/FindReservationResultHirer.ts
function FindReservationResultHirerFromJSON(json) {
  return FindReservationResultHirerFromJSONTyped(json, false);
}
function FindReservationResultHirerFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    firstName: json["firstName"],
    lastName: json["lastName"],
    email: json["email"],
    checkedIn: json["checkedIn"] == null ? void 0 : json["checkedIn"]
  };
}

// src/rentals-api-v3/models/FindReservationResult.ts
function FindReservationResultFromJSON(json) {
  return FindReservationResultFromJSONTyped(json, false);
}
function FindReservationResultFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    id: json["id"],
    reservationId: json["reservationId"] == null ? void 0 : json["reservationId"],
    allocatedReservationId: json["allocatedReservationId"] == null ? void 0 : json["allocatedReservationId"],
    accountId: json["accountId"] == null ? void 0 : json["accountId"],
    ref: json["ref"],
    status: BookingSummaryStatusFromJSON(json["status"]),
    voucherRef: json["voucherRef"] == null ? void 0 : json["voucherRef"],
    allocated: json["allocated"] == null ? void 0 : json["allocated"],
    rego: json["rego"] == null ? void 0 : json["rego"],
    availability: AvailabilityTypeFromJSON(json["availability"]),
    created: stringToDate(json["created"]),
    updated: stringToDate(json["updated"]),
    hirer: json["hirer"] == null ? void 0 : FindReservationResultHirerFromJSON(json["hirer"]),
    fleetTypeCode: FleetTypeCodeFromJSON(json["fleetTypeCode"]),
    pickUpSiteCode: json["pickUpSiteCode"],
    dropOffSiteCode: json["dropOffSiteCode"],
    fleetCategoryCode: json["fleetCategoryCode"],
    rentalDays: json["rentalDays"],
    pickUpDate: stringToDate(json["pickUpDate"]),
    dropOffDate: stringToDate(json["dropOffDate"])
  };
}

// src/rentals-api-v3/models/VideoChapter.ts
function VideoChapterFromJSON(json) {
  return VideoChapterFromJSONTyped(json, false);
}
function VideoChapterFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    name: json["name"],
    startTime: json["startTime"],
    endTime: json["endTime"]
  };
}

// src/rentals-api-v3/models/VideoContent.ts
function VideoContentFromJSON(json) {
  return VideoContentFromJSONTyped(json, false);
}
function VideoContentFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    name: json["name"],
    intro: json["intro"],
    youtubeId: json["youtubeId"],
    chapters: json["chapters"].map(VideoChapterFromJSON)
  };
}

// src/rentals-api-v3/models/FleetCategoryContent.ts
function FleetCategoryContentFromJSON(json) {
  return FleetCategoryContentFromJSONTyped(json, false);
}
function FleetCategoryContentFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    title: json["title"],
    subTitle: json["subTitle"],
    content: json["content"],
    name: json["name"],
    code: json["code"],
    countryCode: json["countryCode"],
    manualLink: json["manualLink"] == null ? void 0 : json["manualLink"],
    showThroughVideo: json["showThroughVideo"] == null ? void 0 : VideoContentFromJSON(json["showThroughVideo"])
  };
}

// src/rentals-api-v3/models/LinkReservationRequest.ts
function LinkReservationRequestToJSON(json) {
  return LinkReservationRequestToJSONTyped(json, false);
}
function LinkReservationRequestToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    lastName: value["lastName"],
    bookingNumber: value["bookingNumber"],
    pickUpBranchCode: value["pickUpBranchCode"]
  };
}

// src/rentals-api-v3/models/LogSelfServiceEventRequest.ts
function LogSelfServiceEventRequestToJSON(json) {
  return LogSelfServiceEventRequestToJSONTyped(json, false);
}
function LogSelfServiceEventRequestToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    eventType: value["eventType"],
    eventName: value["eventName"]
  };
}

// src/rentals-api-v3/models/LoyaltyRequestData.ts
function LoyaltyRequestDataToJSON(json) {
  return LoyaltyRequestDataToJSONTyped(json, false);
}
function LoyaltyRequestDataToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    id: value["id"],
    type: value["type"],
    firstName: value["firstName"],
    lastName: value["lastName"],
    pointsEarned: value["pointsEarned"],
    bonusPointsEarned: value["bonusPointsEarned"],
    earnableTotal: value["earnableTotal"]
  };
}

// src/rentals-api-v3/models/MapMarker.ts
function MapMarkerFromJSON(json) {
  return MapMarkerFromJSONTyped(json, false);
}
function MapMarkerFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    lat: json["lat"],
    lng: json["lng"],
    zoom: json["zoom"]
  };
}

// src/rentals-api-v3/models/MessageResponse.ts
function MessageResponseFromJSON(json) {
  return MessageResponseFromJSONTyped(json, false);
}
function MessageResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    message: json["message"],
    statusCode: json["statusCode"] == null ? void 0 : json["statusCode"],
    data: json["data"] == null ? void 0 : json["data"]
  };
}

// src/rentals-api-v3/models/MinimumHirePeriod.ts
function MinimumHirePeriodFromJSON(json) {
  return MinimumHirePeriodFromJSONTyped(json, false);
}
function MinimumHirePeriodFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    id: json["id"],
    pickUpLocationCode: json["pickUpLocationCode"] == null ? void 0 : json["pickUpLocationCode"],
    dropOffLocationCode: json["dropOffLocationCode"] == null ? void 0 : json["dropOffLocationCode"],
    fleetCategoryCode: json["fleetCategoryCode"] == null ? void 0 : json["fleetCategoryCode"],
    region: JucyCountryCodeFromJSON(json["region"]),
    minDays: json["minDays"],
    feeAmount: json["feeAmount"] == null ? void 0 : CurrencyValueFromJSON(json["feeAmount"]),
    daysNoCharge: json["daysNoCharge"] == null ? void 0 : json["daysNoCharge"],
    pickUpStartDate: json["pickUpStartDate"] == null ? void 0 : stringToDate(json["pickUpStartDate"]),
    pickUpEndDate: json["pickUpEndDate"] == null ? void 0 : stringToDate(json["pickUpEndDate"])
  };
}

// src/rentals-api-v3/models/ProductLine.ts
function ProductLineFromJSON(json) {
  return ProductLineFromJSONTyped(json, false);
}
function ProductLineFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    ...json,
    productId: json["productId"],
    sortOrder: json["sortOrder"],
    productCode: json["productCode"],
    name: json["name"],
    category: ProductCategoryFromJSON(json["category"]),
    isMandatory: json["isMandatory"],
    description: json["description"],
    price: CurrencyValueFromJSON(json["price"]),
    productType: json["productType"],
    isPercentageOnTotalCost: json["isPercentageOnTotalCost"],
    allowMultiple: json["allowMultiple"],
    commissionable: json["commissionable"],
    associatedProductCode: json["associatedProductCode"] == null ? void 0 : json["associatedProductCode"],
    rentalDays: json["rentalDays"],
    numberOfDaysCalculated: json["numberOfDaysCalculated"],
    maxPrice: json["maxPrice"] == null ? void 0 : CurrencyValueFromJSON(json["maxPrice"]),
    qty: json["qty"],
    total: CurrencyValueFromJSON(json["total"]),
    isExcluded: json["isExcluded"] == null ? void 0 : json["isExcluded"]
  };
}

// src/rentals-api-v3/models/RentalCatalogResponse.ts
function RentalCatalogResponseFromJSON(json) {
  return RentalCatalogResponseFromJSONTyped(json, false);
}
function RentalCatalogResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    products: json["products"].map(CatalogItemFromJSON)
  };
}

// src/rentals-api-v3/models/ReservationRewardPointsStatus.ts
function ReservationRewardPointsStatusFromJSON(json) {
  return ReservationRewardPointsStatusFromJSONTyped(json, false);
}
function ReservationRewardPointsStatusFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    eligible: json["eligible"],
    type: json["type"] == null ? void 0 : json["type"],
    baseMultiplier: json["baseMultiplier"] == null ? void 0 : json["baseMultiplier"],
    bonusPoints: json["bonusPoints"] == null ? void 0 : json["bonusPoints"],
    bonusMultiplier: json["bonusMultiplier"] == null ? void 0 : json["bonusMultiplier"],
    maxBasePoints: json["maxBasePoints"],
    maxBonusPoints: json["maxBonusPoints"],
    exchangeRate: json["exchangeRate"],
    membershipId: json["membershipId"] == null ? void 0 : json["membershipId"],
    membershipFirstName: json["membershipFirstName"] == null ? void 0 : json["membershipFirstName"],
    membershipLastName: json["membershipLastName"] == null ? void 0 : json["membershipLastName"],
    pointEstimate: json["pointEstimate"] == null ? void 0 : json["pointEstimate"],
    bonusPointEstimate: json["bonusPointEstimate"] == null ? void 0 : json["bonusPointEstimate"],
    earnableTotal: json["earnableTotal"] == null ? void 0 : CurrencyValueFromJSON(json["earnableTotal"]),
    bookingTotal: json["bookingTotal"] == null ? void 0 : CurrencyValueFromJSON(json["bookingTotal"]),
    locked: json["locked"]
  };
}

// src/rentals-api-v3/models/RewardPointsConfig.ts
function RewardPointsConfigFromJSON(json) {
  return RewardPointsConfigFromJSONTyped(json, false);
}
function RewardPointsConfigFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    eligible: json["eligible"],
    type: json["type"] == null ? void 0 : json["type"],
    baseMultiplier: json["baseMultiplier"] == null ? void 0 : json["baseMultiplier"],
    bonusPoints: json["bonusPoints"] == null ? void 0 : json["bonusPoints"],
    bonusMultiplier: json["bonusMultiplier"] == null ? void 0 : json["bonusMultiplier"],
    maxBasePoints: json["maxBasePoints"],
    maxBonusPoints: json["maxBonusPoints"],
    exchangeRate: json["exchangeRate"]
  };
}

// src/rentals-api-v3/models/SelfServiceBooking.ts
function SelfServiceBookingFromJSON(json) {
  return SelfServiceBookingFromJSONTyped(json, false);
}
function SelfServiceBookingFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    quoteId: json["quoteId"],
    reservationId: json["reservationId"],
    reservationReference: json["reservationReference"],
    agentBalanceDue: json["agentBalanceDue"] == null ? void 0 : CurrencyValueFromJSON(json["agentBalanceDue"]),
    agentCollects: json["agentCollects"] == null ? void 0 : CurrencyValueFromJSON(json["agentCollects"]),
    agentCommission: json["agentCommission"] == null ? void 0 : CurrencyValueFromJSON(json["agentCommission"]),
    voucherReference: json["voucherReference"] == null ? void 0 : json["voucherReference"],
    agentName: json["agentName"] == null ? void 0 : json["agentName"],
    balanceDue: json["balanceDue"] == null ? void 0 : CurrencyValueFromJSON(json["balanceDue"]),
    payments: json["payments"] == null ? void 0 : json["payments"].map(BookingPaymentInfoFromJSON),
    status: BookingStatusFromJSON(json["status"]),
    pickUpLocation: json["pickUpLocation"],
    dropOffLocation: json["dropOffLocation"],
    dateCreated: stringToDate(json["dateCreated"]),
    pickUpDate: stringToDate(json["pickUpDate"]),
    dropOffDate: stringToDate(json["dropOffDate"]),
    rentalDays: json["rentalDays"],
    totalPrice: CurrencyValueFromJSON(json["totalPrice"]),
    driverAge: json["driverAge"],
    lines: json["lines"] == null ? void 0 : json["lines"].map(BookingLineItemFromJSON),
    hirerDetails: json["hirerDetails"] == null ? void 0 : HirerDetailsFromJSON(json["hirerDetails"]),
    fleetCategory: FleetCategoryFromJSON(json["fleetCategory"]),
    referrerCode: json["referrerCode"] == null ? void 0 : json["referrerCode"],
    bookedWith: json["bookedWith"] == null ? void 0 : BookedWithFromJSON(json["bookedWith"])
  };
}

// src/rentals-api-v3/models/SelfServicePreferences.ts
function SelfServicePreferencesFromJSON(json) {
  return SelfServicePreferencesFromJSONTyped(json, false);
}
function SelfServicePreferencesFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    directContent: json["directContent"],
    type: json["type"]
  };
}

// src/rentals-api-v3/models/SendPaymentRequest.ts
function SendPaymentRequestToJSON(json) {
  return SendPaymentRequestToJSONTyped(json, false);
}
function SendPaymentRequestToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    customerEmail: value["customerEmail"],
    customerName: value["customerName"],
    total: value["total"],
    reservationRef: value["reservationRef"],
    paymentRef: value["paymentRef"],
    region: JucyCountryCodeToJSON(value["region"]),
    disableMerchantFee: value["disableMerchantFee"],
    gateway: value["gateway"],
    brand: value["brand"]
  };
}

// src/rentals-api-v3/models/ShuttleImage.ts
function ShuttleImageFromJSON(json) {
  return ShuttleImageFromJSONTyped(json, false);
}
function ShuttleImageFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    small: json["small"] == null ? void 0 : json["small"],
    medium: json["medium"] == null ? void 0 : json["medium"],
    thumbnail: json["thumbnail"] == null ? void 0 : json["thumbnail"],
    main: json["main"] == null ? void 0 : json["main"]
  };
}

// src/rentals-api-v3/models/Shuttle.ts
function ShuttleFromJSON(json) {
  return ShuttleFromJSONTyped(json, false);
}
function ShuttleFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    name: json["name"],
    code: json["code"],
    image: json["image"] == null ? void 0 : ShuttleImageFromJSON(json["image"]),
    meetingLocation: MapMarkerFromJSON(json["meetingLocation"]),
    branchCode: json["branchCode"],
    timeZone: json["timeZone"]
  };
}

// src/rentals-api-v3/models/SiteAddress1.ts
function SiteAddress1FromJSON(json) {
  return SiteAddress1FromJSONTyped(json, false);
}
function SiteAddress1FromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    name: json["name"],
    line1: json["line1"],
    line2: json["line2"] == null ? void 0 : json["line2"],
    line3: json["line3"] == null ? void 0 : json["line3"],
    latitude: json["latitude"] == null ? void 0 : json["latitude"],
    longitude: json["longitude"] == null ? void 0 : json["longitude"],
    state: json["state"] == null ? void 0 : json["state"],
    city: json["city"],
    postCode: json["postCode"],
    county: json["county"] == null ? void 0 : json["county"],
    country: json["country"]
  };
}

// src/rentals-api-v3/models/SiteServiceTime.ts
function SiteServiceTimeFromJSON(json) {
  return SiteServiceTimeFromJSONTyped(json, false);
}
function SiteServiceTimeFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    pickUp: json["pickUp"],
    dropOff: json["dropOff"]
  };
}

// src/rentals-api-v3/models/SiteDefaultTimes.ts
function SiteDefaultTimesFromJSON(json) {
  return SiteDefaultTimesFromJSONTyped(json, false);
}
function SiteDefaultTimesFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    sunday: SiteServiceTimeFromJSON(json["sunday"]),
    monday: SiteServiceTimeFromJSON(json["monday"]),
    tuesday: SiteServiceTimeFromJSON(json["tuesday"]),
    wednesday: SiteServiceTimeFromJSON(json["wednesday"]),
    thursday: SiteServiceTimeFromJSON(json["thursday"]),
    friday: SiteServiceTimeFromJSON(json["friday"]),
    saturday: SiteServiceTimeFromJSON(json["saturday"])
  };
}

// src/rentals-api-v3/models/SiteHoliday.ts
function SiteHolidayFromJSON(json) {
  return SiteHolidayFromJSONTyped(json, false);
}
function SiteHolidayFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    name: json["name"],
    startDateTime: stringToDate(json["startDateTime"]),
    endDateTime: stringToDate(json["endDateTime"]),
    dow: json["dow"] == null ? void 0 : json["dow"]
  };
}

// src/rentals-api-v3/models/SiteOfficeTime.ts
function SiteOfficeTimeFromJSON(json) {
  return SiteOfficeTimeFromJSONTyped(json, false);
}
function SiteOfficeTimeFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    open: json["open"],
    close: json["close"]
  };
}

// src/rentals-api-v3/models/SiteSettingsServiceHours.ts
function SiteSettingsServiceHoursFromJSON(json) {
  return SiteSettingsServiceHoursFromJSONTyped(json, false);
}
function SiteSettingsServiceHoursFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    sunday: SiteOfficeTimeFromJSON(json["sunday"]),
    monday: SiteOfficeTimeFromJSON(json["monday"]),
    tuesday: SiteOfficeTimeFromJSON(json["tuesday"]),
    wednesday: SiteOfficeTimeFromJSON(json["wednesday"]),
    thursday: SiteOfficeTimeFromJSON(json["thursday"]),
    friday: SiteOfficeTimeFromJSON(json["friday"]),
    saturday: SiteOfficeTimeFromJSON(json["saturday"])
  };
}

// src/rentals-api-v3/models/SiteSettings.ts
function SiteSettingsFromJSON(json) {
  return SiteSettingsFromJSONTyped(json, false);
}
function SiteSettingsFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    fleetTypeId: json["fleetTypeId"],
    fleetTypeName: json["fleetTypeName"],
    fleetTypeSlug: json["fleetTypeSlug"],
    serviceHours: SiteSettingsServiceHoursFromJSON(json["serviceHours"]),
    isAfterHoursPickUpAvailable: json["isAfterHoursPickUpAvailable"],
    isAfterHoursDropOffAvailable: json["isAfterHoursDropOffAvailable"]
  };
}

// src/rentals-api-v3/models/Site.ts
function SiteFromJSON(json) {
  return SiteFromJSONTyped(json, false);
}
function SiteFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    id: json["id"],
    name: json["name"],
    siteCode: json["siteCode"],
    geoLocation: MapMarkerFromJSON(json["geoLocation"]),
    domesticPickUp: json["domesticPickUp"],
    internationalPickUp: json["internationalPickUp"],
    directionsLink: json["directionsLink"] == null ? void 0 : json["directionsLink"],
    address1: SiteAddress1FromJSON(json["address1"]),
    phone: json["phone"] == null ? void 0 : json["phone"],
    reservationsPhone: json["reservationsPhone"] == null ? void 0 : json["reservationsPhone"],
    timeZone: json["timeZone"],
    country: json["country"],
    countryCode: JucyCountryCodeFromJSON(json["countryCode"]),
    minAges: json["minAges"],
    siteSettings: json["siteSettings"].map(SiteSettingsFromJSON),
    holidays: json["holidays"].map(SiteHolidayFromJSON),
    defaultTimes: SiteDefaultTimesFromJSON(json["defaultTimes"]),
    businessUnit: json["businessUnit"]
  };
}

// src/rentals-api-v3/models/SubscribeRequest.ts
function SubscribeRequestToJSON(json) {
  return SubscribeRequestToJSONTyped(json, false);
}
function SubscribeRequestToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    list: JucyCountryCodeToJSON(value["list"]),
    email: value["email"],
    firstName: value["firstName"],
    lastName: value["lastName"],
    tags: value["tags"]
  };
}

// src/rentals-api-v3/models/SubscribeResponse.ts
function SubscribeResponseFromJSON(json) {
  return SubscribeResponseFromJSONTyped(json, false);
}
function SubscribeResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    status: json["status"],
    message: json["message"] == null ? void 0 : json["message"]
  };
}

// src/rentals-api-v3/models/TripDetails.ts
function TripDetailsFromJSON(json) {
  return TripDetailsFromJSONTyped(json, false);
}
function TripDetailsFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    pickUpLocation: json["pickUpLocation"],
    dropOffLocation: json["dropOffLocation"],
    pickUpDate: stringToDate(json["pickUpDate"]),
    dropOffDate: stringToDate(json["dropOffDate"]),
    rentalDays: json["rentalDays"],
    totalPrice: CurrencyValueFromJSON(json["totalPrice"]),
    lines: json["lines"] == null ? void 0 : json["lines"].map(ProductLineFromJSON),
    fleetCategory: FleetCategoryFromJSON(json["fleetCategory"]),
    secondaryProducts: json["secondaryProducts"].map(SecondaryProductGroupFromJSON),
    insuranceProducts: InsuranceProductGroupFromJSON(json["insuranceProducts"]),
    bundles: json["bundles"] == null ? void 0 : json["bundles"].map(ProductBundleFromJSON)
  };
}

// src/rentals-api-v3/models/UpdateFlightDetailsRequestAdditionalDriversInner.ts
function UpdateFlightDetailsRequestAdditionalDriversInnerToJSON(json) {
  return UpdateFlightDetailsRequestAdditionalDriversInnerToJSONTyped(json, false);
}
function UpdateFlightDetailsRequestAdditionalDriversInnerToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    additionalDriverId: value["additionalDriverId"],
    isDelete: value["isDelete"],
    firstName: value["firstName"],
    lastName: value["lastName"],
    email: value["email"],
    licenseNumber: value["licenseNumber"],
    licenseCountry: value["licenseCountry"],
    licenseExpiry: value["licenseExpiry"],
    licenseFrontImage: value["licenseFrontImage"],
    licenseBackImage: value["licenseBackImage"]
  };
}

// src/rentals-api-v3/models/UpdateFlightDetailsRequestProductAndQuantityMapInner.ts
function UpdateFlightDetailsRequestProductAndQuantityMapInnerToJSON(json) {
  return UpdateFlightDetailsRequestProductAndQuantityMapInnerToJSONTyped(json, false);
}
function UpdateFlightDetailsRequestProductAndQuantityMapInnerToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    code: value["code"],
    qty: value["qty"]
  };
}

// src/rentals-api-v3/models/UpdateFlightDetailsRequest.ts
function UpdateFlightDetailsRequestToJSON(json) {
  return UpdateFlightDetailsRequestToJSONTyped(json, false);
}
function UpdateFlightDetailsRequestToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    reservationReference: value["reservationReference"],
    departureFlightNumber: value["departureFlightNumber"],
    departureShuttleRequired: value["departureShuttleRequired"],
    arrivalFlightNumber: value["arrivalFlightNumber"],
    arrivalShuttleRequired: value["arrivalShuttleRequired"],
    numberOfPeople: value["numberOfPeople"],
    country: value["country"],
    firstName: value["firstName"],
    lastName: value["lastName"],
    email: value["email"],
    mobile: value["mobile"],
    dateOfBirth: value["dateOfBirth"],
    licenseNumber: value["licenseNumber"],
    licenseCountry: value["licenseCountry"],
    licenseExpiry: value["licenseExpiry"],
    agentVoucherReference: value["agentVoucherReference"],
    productAndQuantityMap: value["productAndQuantityMap"] == null ? void 0 : value["productAndQuantityMap"].map(UpdateFlightDetailsRequestProductAndQuantityMapInnerToJSON),
    deposit: value["deposit"],
    additionalDrivers: value["additionalDrivers"] == null ? void 0 : value["additionalDrivers"].map(UpdateFlightDetailsRequestAdditionalDriversInnerToJSON),
    licenseFrontImage: value["licenseFrontImage"],
    licenseBackImage: value["licenseBackImage"]
  };
}

// src/rentals-api-v3/models/UpdateReservationRequest.ts
function UpdateReservationRequestToJSON(json) {
  return UpdateReservationRequestToJSONTyped(json, false);
}
function UpdateReservationRequestToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    pickUpLocation: value["pickUpLocation"],
    dropOffLocation: value["dropOffLocation"],
    pickUpDate: value["pickUpDate"],
    dropOffDate: value["dropOffDate"],
    fleetCategory: value["fleetCategory"],
    driverAge: value["driverAge"],
    numberOfPeople: value["numberOfPeople"],
    campaignCode: value["campaignCode"],
    pascal: value["pascal"],
    insurance: value["insurance"],
    secondaryProducts: value["secondaryProducts"],
    hirerDetails: HirerDetailsSummaryToJSON(value["hirerDetails"]),
    voucherReference: value["voucherReference"],
    gateway: value["gateway"],
    payment: value["payment"]
  };
}

// src/rentals-api-v3/models/UpdateReservationValidationStatus.ts
function UpdateReservationValidationStatusFromJSON(json) {
  return UpdateReservationValidationStatusFromJSONTyped(json, false);
}
function UpdateReservationValidationStatusFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}

// src/rentals-api-v3/models/ValidateUpdateResponse.ts
function ValidateUpdateResponseFromJSON(json) {
  return ValidateUpdateResponseFromJSONTyped(json, false);
}
function ValidateUpdateResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    updates: json["updates"] == null ? void 0 : TripDetailsFromJSON(json["updates"]),
    message: json["message"] == null ? void 0 : json["message"],
    original: json["original"] == null ? void 0 : BookingFromJSON(json["original"]),
    status: UpdateReservationValidationStatusFromJSON(json["status"]),
    reference: json["reference"],
    bookingNo: json["bookingNo"] == null ? void 0 : json["bookingNo"]
  };
}

// src/rentals-api-v3/apis/AgentApi.ts
var AgentApi = class extends BaseAPI {
  /**
   * Get information about an agent account
   */
  async getAgentDetailsRaw(requestParameters, initOverrides) {
    if (requestParameters["id"] == null) {
      throw new RequiredError("id", 'Required parameter "id" was null or undefined when calling getAgentDetails().');
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/agent/{id}`;
    urlPath = urlPath.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters["id"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => AgentInfoFromJSON(jsonValue));
  }
  /**
   * Get information about an agent account
   */
  async getAgentDetails(id, initOverrides) {
    const response = await this.getAgentDetailsRaw({ id }, initOverrides);
    return await response.value();
  }
  /**
   * Download agent rates file
   */
  async getAgentRatesRaw(requestParameters, initOverrides) {
    if (requestParameters["region"] == null) {
      throw new RequiredError("region", 'Required parameter "region" was null or undefined when calling getAgentRates().');
    }
    if (requestParameters["id"] == null) {
      throw new RequiredError("id", 'Required parameter "id" was null or undefined when calling getAgentRates().');
    }
    const queryParameters = {};
    if (requestParameters["region"] != null) {
      queryParameters["region"] = requestParameters["region"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/agent/{id}/rates`;
    urlPath = urlPath.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters["id"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    if (this.isJsonMime(response.headers.get("content-type"))) {
      return new JSONApiResponse(response);
    } else {
      return new TextApiResponse(response);
    }
  }
  /**
   * Download agent rates file
   */
  async getAgentRates(region, id, initOverrides) {
    const response = await this.getAgentRatesRaw({ region, id }, initOverrides);
    return await response.value();
  }
};

// src/rentals-api-v3/apis/BusinessUnitsApi.ts
var BusinessUnitsApi = class extends BaseAPI {
  /**
   * List active business units
   */
  async listBusinessUnitsRaw(initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/business-units`;
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => jsonValue.map(BusinessUnitFromJSON));
  }
  /**
   * List active business units
   */
  async listBusinessUnits(initOverrides) {
    const response = await this.listBusinessUnitsRaw(initOverrides);
    return await response.value();
  }
};

// src/rentals-api-v3/apis/CatalogApi.ts
var CatalogApi = class extends BaseAPI {
  /**
   * Get a list of rental products
   */
  async getRentalCatalogRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    if (requestParameters["fleetCategory"] != null) {
      queryParameters["fleetCategory"] = requestParameters["fleetCategory"];
    }
    if (requestParameters["fleetType"] != null) {
      queryParameters["fleetType"] = requestParameters["fleetType"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/rental-catalog`;
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => RentalCatalogResponseFromJSON(jsonValue));
  }
  /**
   * Get a list of rental products
   */
  async getRentalCatalog(fleetCategory, fleetType, initOverrides) {
    const response = await this.getRentalCatalogRaw({ fleetCategory, fleetType }, initOverrides);
    return await response.value();
  }
};

// src/rentals-api-v3/apis/CommissionsApi.ts
var CommissionsApi = class extends BaseAPI {
  /**
   * List all commissions
   */
  async getCommissionsRaw(initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/commissions`;
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => jsonValue.map(CommissionFromJSON));
  }
  /**
   * List all commissions
   */
  async getCommissions(initOverrides) {
    const response = await this.getCommissionsRaw(initOverrides);
    return await response.value();
  }
};

// src/rentals-api-v3/apis/CountryApi.ts
var CountryApi = class extends BaseAPI {
  /**
   * Get a list of countries
   */
  async getCountriesRaw(initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/countries`;
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => jsonValue.map(CountryDataFromJSON));
  }
  /**
   * Get a list of countries
   */
  async getCountries(initOverrides) {
    const response = await this.getCountriesRaw(initOverrides);
    return await response.value();
  }
};

// src/rentals-api-v3/apis/DataApi.ts
var DataApi = class extends BaseAPI {
  /**
   * Get currency conversion rate for a currency code
   */
  async getCurrencyRateRaw(initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/data/get-currency-rate`;
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => CurrencyRateResultFromJSON(jsonValue));
  }
  /**
   * Get currency conversion rate for a currency code
   */
  async getCurrencyRate(initOverrides) {
    const response = await this.getCurrencyRateRaw(initOverrides);
    return await response.value();
  }
  /**
   * List current hire periods
   */
  async getMinimumHirePeriodsRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    if (requestParameters["pickUpLocationCode"] != null) {
      queryParameters["pickUpLocationCode"] = requestParameters["pickUpLocationCode"];
    }
    if (requestParameters["dropOffLocationCode"] != null) {
      queryParameters["dropOffLocationCode"] = requestParameters["dropOffLocationCode"];
    }
    if (requestParameters["fleetCategoryCode"] != null) {
      queryParameters["fleetCategoryCode"] = requestParameters["fleetCategoryCode"];
    }
    if (requestParameters["fleetTypeCode"] != null) {
      queryParameters["fleetTypeCode"] = requestParameters["fleetTypeCode"];
    }
    if (requestParameters["region"] != null) {
      queryParameters["region"] = requestParameters["region"];
    }
    if (requestParameters["format"] != null) {
      queryParameters["format"] = requestParameters["format"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/data/minimum-hire-periods`;
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => jsonValue.map(MinimumHirePeriodFromJSON));
  }
  /**
   * List current hire periods
   */
  async getMinimumHirePeriods(pickUpLocationCode, dropOffLocationCode, fleetCategoryCode, fleetTypeCode, region, format, initOverrides) {
    const response = await this.getMinimumHirePeriodsRaw(
      {
        pickUpLocationCode,
        dropOffLocationCode,
        fleetCategoryCode,
        fleetTypeCode,
        region,
        format
      },
      initOverrides
    );
    return await response.value();
  }
};

// src/rentals-api-v3/apis/FerryApi.ts
var FerryApi = class extends BaseAPI {
  /**
   * Create ferry booking quote
   */
  async createFerryQuoteRaw(requestParameters, initOverrides) {
    if (requestParameters["reservationRef"] == null) {
      throw new RequiredError(
        "reservationRef",
        'Required parameter "reservationRef" was null or undefined when calling createFerryQuote().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/ferry/{reservationRef}/quote`;
    urlPath = urlPath.replace(`{${"reservationRef"}}`, encodeURIComponent(String(requestParameters["reservationRef"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "POST",
        headers: headerParameters,
        query: queryParameters,
        body: FerryCreateQuoteRequestToJSON(requestParameters["ferryCreateQuoteRequest"])
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => FerryGetBookingResponseFromJSON(jsonValue));
  }
  /**
   * Create ferry booking quote
   */
  async createFerryQuote(reservationRef, ferryCreateQuoteRequest, initOverrides) {
    const response = await this.createFerryQuoteRaw(
      { reservationRef, ferryCreateQuoteRequest },
      initOverrides
    );
    return await response.value();
  }
  /**
   * Get a ferry booking
   */
  async getFerryBookingRaw(requestParameters, initOverrides) {
    if (requestParameters["reservationRef"] == null) {
      throw new RequiredError(
        "reservationRef",
        'Required parameter "reservationRef" was null or undefined when calling getFerryBooking().'
      );
    }
    if (requestParameters["bookingRef"] == null) {
      throw new RequiredError("bookingRef", 'Required parameter "bookingRef" was null or undefined when calling getFerryBooking().');
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/ferry/{reservationRef}/bookings/{bookingRef}`;
    urlPath = urlPath.replace(`{${"reservationRef"}}`, encodeURIComponent(String(requestParameters["reservationRef"])));
    urlPath = urlPath.replace(`{${"bookingRef"}}`, encodeURIComponent(String(requestParameters["bookingRef"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => FerryBookingFromJSON(jsonValue));
  }
  /**
   * Get a ferry booking
   */
  async getFerryBooking(reservationRef, bookingRef, initOverrides) {
    const response = await this.getFerryBookingRaw({ reservationRef, bookingRef }, initOverrides);
    return await response.value();
  }
  /**
   * Get all ferry bookings for a given reservation
   */
  async getFerryBookingsRaw(requestParameters, initOverrides) {
    if (requestParameters["reservationRef"] == null) {
      throw new RequiredError(
        "reservationRef",
        'Required parameter "reservationRef" was null or undefined when calling getFerryBookings().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/ferry/{reservationRef}/bookings`;
    urlPath = urlPath.replace(`{${"reservationRef"}}`, encodeURIComponent(String(requestParameters["reservationRef"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => jsonValue.map(FerryBookingFromJSON));
  }
  /**
   * Get all ferry bookings for a given reservation
   */
  async getFerryBookings(reservationRef, initOverrides) {
    const response = await this.getFerryBookingsRaw({ reservationRef }, initOverrides);
    return await response.value();
  }
  /**
   * Get ferry options
   */
  async getFerryOptionsRaw(requestParameters, initOverrides) {
    if (requestParameters["reservationRef"] == null) {
      throw new RequiredError(
        "reservationRef",
        'Required parameter "reservationRef" was null or undefined when calling getFerryOptions().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/ferry/{reservationRef}/options`;
    urlPath = urlPath.replace(`{${"reservationRef"}}`, encodeURIComponent(String(requestParameters["reservationRef"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => FerryOptionsFromJSON(jsonValue));
  }
  /**
   * Get ferry options
   */
  async getFerryOptions(reservationRef, initOverrides) {
    const response = await this.getFerryOptionsRaw({ reservationRef }, initOverrides);
    return await response.value();
  }
  /**
   * Retrieve ferry booking quote
   */
  async getFerryQuoteRaw(requestParameters, initOverrides) {
    if (requestParameters["reservationRef"] == null) {
      throw new RequiredError(
        "reservationRef",
        'Required parameter "reservationRef" was null or undefined when calling getFerryQuote().'
      );
    }
    if (requestParameters["bookingRef"] == null) {
      throw new RequiredError("bookingRef", 'Required parameter "bookingRef" was null or undefined when calling getFerryQuote().');
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/ferry/{reservationRef}/quote/{bookingRef}`;
    urlPath = urlPath.replace(`{${"reservationRef"}}`, encodeURIComponent(String(requestParameters["reservationRef"])));
    urlPath = urlPath.replace(`{${"bookingRef"}}`, encodeURIComponent(String(requestParameters["bookingRef"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => FerryGetQuoteResponseFromJSON(jsonValue));
  }
  /**
   * Retrieve ferry booking quote
   */
  async getFerryQuote(reservationRef, bookingRef, initOverrides) {
    const response = await this.getFerryQuoteRaw({ reservationRef, bookingRef }, initOverrides);
    return await response.value();
  }
  /**
   * Get available ferry routes
   */
  async getFerryRoutesRaw(requestParameters, initOverrides) {
    if (requestParameters["reservationRef"] == null) {
      throw new RequiredError(
        "reservationRef",
        'Required parameter "reservationRef" was null or undefined when calling getFerryRoutes().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/ferry/{reservationRef}/routes`;
    urlPath = urlPath.replace(`{${"reservationRef"}}`, encodeURIComponent(String(requestParameters["reservationRef"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => jsonValue.map(FerryRouteFromJSON));
  }
  /**
   * Get available ferry routes
   */
  async getFerryRoutes(reservationRef, initOverrides) {
    const response = await this.getFerryRoutesRaw({ reservationRef }, initOverrides);
    return await response.value();
  }
  /**
   * Get available ferry sailings
   */
  async getFerrySailingsRaw(requestParameters, initOverrides) {
    if (requestParameters["reservationRef"] == null) {
      throw new RequiredError(
        "reservationRef",
        'Required parameter "reservationRef" was null or undefined when calling getFerrySailings().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/ferry/{reservationRef}/sailings`;
    urlPath = urlPath.replace(`{${"reservationRef"}}`, encodeURIComponent(String(requestParameters["reservationRef"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "POST",
        headers: headerParameters,
        query: queryParameters,
        body: FerryGetSailingsRequestToJSON(requestParameters["ferryGetSailingsRequest"])
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => FerryGetSailingsResponseFromJSON(jsonValue));
  }
  /**
   * Get available ferry sailings
   */
  async getFerrySailings(reservationRef, ferryGetSailingsRequest, initOverrides) {
    const response = await this.getFerrySailingsRaw(
      { reservationRef, ferryGetSailingsRequest },
      initOverrides
    );
    return await response.value();
  }
  /**
   * Get ferry status for given reservation
   */
  async getFerryStatusRaw(requestParameters, initOverrides) {
    if (requestParameters["reservationRef"] == null) {
      throw new RequiredError(
        "reservationRef",
        'Required parameter "reservationRef" was null or undefined when calling getFerryStatus().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/ferry/{reservationRef}/status`;
    urlPath = urlPath.replace(`{${"reservationRef"}}`, encodeURIComponent(String(requestParameters["reservationRef"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => FerryGetStatusResponseFromJSON(jsonValue));
  }
  /**
   * Get ferry status for given reservation
   */
  async getFerryStatus(reservationRef, initOverrides) {
    const response = await this.getFerryStatusRaw({ reservationRef }, initOverrides);
    return await response.value();
  }
  /**
   * Get ferry timetable
   */
  async getFerryTimetableRaw(requestParameters, initOverrides) {
    if (requestParameters["departPort"] == null) {
      throw new RequiredError("departPort", 'Required parameter "departPort" was null or undefined when calling getFerryTimetable().');
    }
    if (requestParameters["destinationPort"] == null) {
      throw new RequiredError(
        "destinationPort",
        'Required parameter "destinationPort" was null or undefined when calling getFerryTimetable().'
      );
    }
    if (requestParameters["fromSailingDate"] == null) {
      throw new RequiredError(
        "fromSailingDate",
        'Required parameter "fromSailingDate" was null or undefined when calling getFerryTimetable().'
      );
    }
    if (requestParameters["toSailingDate"] == null) {
      throw new RequiredError(
        "toSailingDate",
        'Required parameter "toSailingDate" was null or undefined when calling getFerryTimetable().'
      );
    }
    if (requestParameters["reservationRef"] == null) {
      throw new RequiredError(
        "reservationRef",
        'Required parameter "reservationRef" was null or undefined when calling getFerryTimetable().'
      );
    }
    const queryParameters = {};
    if (requestParameters["departPort"] != null) {
      queryParameters["departPort"] = requestParameters["departPort"];
    }
    if (requestParameters["destinationPort"] != null) {
      queryParameters["destinationPort"] = requestParameters["destinationPort"];
    }
    if (requestParameters["fromSailingDate"] != null) {
      queryParameters["fromSailingDate"] = requestParameters["fromSailingDate"];
    }
    if (requestParameters["toSailingDate"] != null) {
      queryParameters["toSailingDate"] = requestParameters["toSailingDate"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/ferry/{reservationRef}/timetable`;
    urlPath = urlPath.replace(`{${"reservationRef"}}`, encodeURIComponent(String(requestParameters["reservationRef"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => jsonValue.map(FerrySailingFromJSON));
  }
  /**
   * Get ferry timetable
   */
  async getFerryTimetable(departPort, destinationPort, fromSailingDate, toSailingDate, reservationRef, initOverrides) {
    const response = await this.getFerryTimetableRaw(
      {
        departPort,
        destinationPort,
        fromSailingDate,
        toSailingDate,
        reservationRef
      },
      initOverrides
    );
    return await response.value();
  }
  /**
   * Update ferry booking quote
   */
  async updateFerryQuoteRaw(requestParameters, initOverrides) {
    if (requestParameters["reservationRef"] == null) {
      throw new RequiredError(
        "reservationRef",
        'Required parameter "reservationRef" was null or undefined when calling updateFerryQuote().'
      );
    }
    if (requestParameters["bookingRef"] == null) {
      throw new RequiredError("bookingRef", 'Required parameter "bookingRef" was null or undefined when calling updateFerryQuote().');
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/ferry/{reservationRef}/quote/{bookingRef}`;
    urlPath = urlPath.replace(`{${"reservationRef"}}`, encodeURIComponent(String(requestParameters["reservationRef"])));
    urlPath = urlPath.replace(`{${"bookingRef"}}`, encodeURIComponent(String(requestParameters["bookingRef"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "PUT",
        headers: headerParameters,
        query: queryParameters,
        body: FerryUpdateQuoteRequestToJSON(requestParameters["ferryUpdateQuoteRequest"])
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => FerryGetBookingResponseFromJSON(jsonValue));
  }
  /**
   * Update ferry booking quote
   */
  async updateFerryQuote(reservationRef, bookingRef, ferryUpdateQuoteRequest, initOverrides) {
    const response = await this.updateFerryQuoteRaw(
      { reservationRef, bookingRef, ferryUpdateQuoteRequest },
      initOverrides
    );
    return await response.value();
  }
};

// src/rentals-api-v3/apis/FleetCategoriesApi.ts
var FleetCategoriesApi = class extends BaseAPI {
  /**
   * Get a list of fleet categories
   */
  async getFleetCategoriesRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    if (requestParameters["fleetType"] != null) {
      queryParameters["fleetType"] = requestParameters["fleetType"];
    }
    if (requestParameters["countryCode"] != null) {
      queryParameters["countryCode"] = requestParameters["countryCode"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/fleet-categories`;
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => jsonValue.map(CatalogItemFromJSON));
  }
  /**
   * Get a list of fleet categories
   */
  async getFleetCategories(fleetType, countryCode, initOverrides) {
    const response = await this.getFleetCategoriesRaw({ fleetType, countryCode }, initOverrides);
    return await response.value();
  }
  /**
   * Get fleet category information
   */
  async getFleetCategoryByCodeRaw(requestParameters, initOverrides) {
    if (requestParameters["fleetCategoryCode"] == null) {
      throw new RequiredError(
        "fleetCategoryCode",
        'Required parameter "fleetCategoryCode" was null or undefined when calling getFleetCategoryByCode().'
      );
    }
    const queryParameters = {};
    if (requestParameters["countryCode"] != null) {
      queryParameters["countryCode"] = requestParameters["countryCode"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/fleet-categories/{fleetCategoryCode}`;
    urlPath = urlPath.replace(`{${"fleetCategoryCode"}}`, encodeURIComponent(String(requestParameters["fleetCategoryCode"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => CatalogItemFromJSON(jsonValue));
  }
  /**
   * Get fleet category information
   */
  async getFleetCategoryByCode(fleetCategoryCode, countryCode, initOverrides) {
    const response = await this.getFleetCategoryByCodeRaw({ fleetCategoryCode, countryCode }, initOverrides);
    return await response.value();
  }
  /**
   * Get fleet category page information
   */
  async getFleetCategoryContentByCodeRaw(requestParameters, initOverrides) {
    if (requestParameters["fleetCategoryCode"] == null) {
      throw new RequiredError(
        "fleetCategoryCode",
        'Required parameter "fleetCategoryCode" was null or undefined when calling getFleetCategoryContentByCode().'
      );
    }
    const queryParameters = {};
    if (requestParameters["countryCode"] != null) {
      queryParameters["countryCode"] = requestParameters["countryCode"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/fleet-categories/{fleetCategoryCode}/content`;
    urlPath = urlPath.replace(`{${"fleetCategoryCode"}}`, encodeURIComponent(String(requestParameters["fleetCategoryCode"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => FleetCategoryContentFromJSON(jsonValue));
  }
  /**
   * Get fleet category page information
   */
  async getFleetCategoryContentByCode(fleetCategoryCode, countryCode, initOverrides) {
    const response = await this.getFleetCategoryContentByCodeRaw(
      { fleetCategoryCode, countryCode },
      initOverrides
    );
    return await response.value();
  }
  /**
   * List fleet category page information
   */
  async listFleetCategoryContentRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    if (requestParameters["countryCode"] != null) {
      queryParameters["countryCode"] = requestParameters["countryCode"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/fleet-categories/content`;
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => jsonValue.map(FleetCategoryContentFromJSON));
  }
  /**
   * List fleet category page information
   */
  async listFleetCategoryContent(countryCode, initOverrides) {
    const response = await this.listFleetCategoryContentRaw({ countryCode }, initOverrides);
    return await response.value();
  }
};

// src/rentals-api-v3/apis/MailListApi.ts
var MailListApi = class extends BaseAPI {
  /**
   * Subscribe to Jucy newsletters
   */
  async subscribeToListRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/mail-list/subscribe`;
    const response = await this.request(
      {
        path: urlPath,
        method: "PUT",
        headers: headerParameters,
        query: queryParameters,
        body: SubscribeRequestToJSON(requestParameters["subscribeRequest"])
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => SubscribeResponseFromJSON(jsonValue));
  }
  /**
   * Subscribe to Jucy newsletters
   */
  async subscribeToList(subscribeRequest, initOverrides) {
    const response = await this.subscribeToListRaw({ subscribeRequest }, initOverrides);
    return await response.value();
  }
};

// src/rentals-api-v3/apis/PaymentApi.ts
var PaymentApi = class extends BaseAPI {
  /**
   * send a payment request email to a customer
   */
  async sendPaymentRequestRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/payment/send-payment-request`;
    const response = await this.request(
      {
        path: urlPath,
        method: "POST",
        headers: headerParameters,
        query: queryParameters,
        body: SendPaymentRequestToJSON(requestParameters["sendPaymentRequest"])
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => MessageResponseFromJSON(jsonValue));
  }
  /**
   * send a payment request email to a customer
   */
  async sendPaymentRequest(sendPaymentRequest, initOverrides) {
    const response = await this.sendPaymentRequestRaw({ sendPaymentRequest }, initOverrides);
    return await response.value();
  }
};

// src/rentals-api-v3/apis/QuotesApi.ts
var QuotesApi = class extends BaseAPI {
  /**
   * Cancel an existing quote
   */
  async cancelQuoteRaw(requestParameters, initOverrides) {
    if (requestParameters["reservationRef"] == null) {
      throw new RequiredError(
        "reservationRef",
        'Required parameter "reservationRef" was null or undefined when calling cancelQuote().'
      );
    }
    const queryParameters = {};
    if (requestParameters["onlineLogin"] != null) {
      queryParameters["onlineLogin"] = requestParameters["onlineLogin"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/quotes/{reservationRef}`;
    urlPath = urlPath.replace(`{${"reservationRef"}}`, encodeURIComponent(String(requestParameters["reservationRef"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "DELETE",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => CancelReservationResponseFromJSON(jsonValue));
  }
  /**
   * Cancel an existing quote
   */
  async cancelQuote(reservationRef, onlineLogin, initOverrides) {
    const response = await this.cancelQuoteRaw({ reservationRef, onlineLogin }, initOverrides);
    return await response.value();
  }
  /**
   * Convert a quote in to a reservation
   */
  async convertQuoteRaw(requestParameters, initOverrides) {
    if (requestParameters["reservationRef"] == null) {
      throw new RequiredError(
        "reservationRef",
        'Required parameter "reservationRef" was null or undefined when calling convertQuote().'
      );
    }
    const queryParameters = {};
    if (requestParameters["gateway"] != null) {
      queryParameters["gateway"] = requestParameters["gateway"];
    }
    if (requestParameters["returnUrl"] != null) {
      queryParameters["returnUrl"] = requestParameters["returnUrl"];
    }
    if (requestParameters["failedUrl"] != null) {
      queryParameters["failedUrl"] = requestParameters["failedUrl"];
    }
    if (requestParameters["type"] != null) {
      queryParameters["type"] = requestParameters["type"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/quotes/{reservationRef}/convert`;
    urlPath = urlPath.replace(`{${"reservationRef"}}`, encodeURIComponent(String(requestParameters["reservationRef"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => ConvertQuoteResponseFromJSON(jsonValue));
  }
  /**
   * Convert a quote in to a reservation
   */
  async convertQuote(reservationRef, gateway, returnUrl, failedUrl, type, initOverrides) {
    const response = await this.convertQuoteRaw(
      { reservationRef, gateway, returnUrl, failedUrl, type },
      initOverrides
    );
    return await response.value();
  }
  /**
   * Create a new quote and email it to the hirer
   */
  async emailQuoteRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/quotes`;
    const response = await this.request(
      {
        path: urlPath,
        method: "POST",
        headers: headerParameters,
        query: queryParameters,
        body: EmailQuoteRequestToJSON(requestParameters["emailQuoteRequest"])
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => CreateBookingResponseFromJSON(jsonValue));
  }
  /**
   * Create a new quote and email it to the hirer
   */
  async emailQuote(emailQuoteRequest, initOverrides) {
    const response = await this.emailQuoteRaw({ emailQuoteRequest }, initOverrides);
    return await response.value();
  }
  /**
   * Make updates and edit an existing quote
   */
  async updateQuoteRaw(requestParameters, initOverrides) {
    if (requestParameters["reservationRef"] == null) {
      throw new RequiredError(
        "reservationRef",
        'Required parameter "reservationRef" was null or undefined when calling updateQuote().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/quotes/{reservationRef}/update`;
    urlPath = urlPath.replace(`{${"reservationRef"}}`, encodeURIComponent(String(requestParameters["reservationRef"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "PATCH",
        headers: headerParameters,
        query: queryParameters,
        body: UpdateReservationRequestToJSON(requestParameters["updateReservationRequest"])
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => CreateBookingResponseFromJSON(jsonValue));
  }
  /**
   * Make updates and edit an existing quote
   */
  async updateQuote(reservationRef, updateReservationRequest, initOverrides) {
    const response = await this.updateQuoteRaw(
      { reservationRef, updateReservationRequest },
      initOverrides
    );
    return await response.value();
  }
};

// src/rentals-api-v3/apis/ReservationsApi.ts
var ReservationsApi = class extends BaseAPI {
  /**
   * Cancel an existing reservation
   */
  async cancelReservationRaw(requestParameters, initOverrides) {
    if (requestParameters["reservationRef"] == null) {
      throw new RequiredError(
        "reservationRef",
        'Required parameter "reservationRef" was null or undefined when calling cancelReservation().'
      );
    }
    const queryParameters = {};
    if (requestParameters["onlineLogin"] != null) {
      queryParameters["onlineLogin"] = requestParameters["onlineLogin"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/reservations/{reservationRef}`;
    urlPath = urlPath.replace(`{${"reservationRef"}}`, encodeURIComponent(String(requestParameters["reservationRef"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "DELETE",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => CancelReservationResponseFromJSON(jsonValue));
  }
  /**
   * Cancel an existing reservation
   */
  async cancelReservation(reservationRef, onlineLogin, initOverrides) {
    const response = await this.cancelReservationRaw({ reservationRef, onlineLogin }, initOverrides);
    return await response.value();
  }
  /**
   * Create a new booking
   */
  async createBookingRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/reservations`;
    const response = await this.request(
      {
        path: urlPath,
        method: "POST",
        headers: headerParameters,
        query: queryParameters,
        body: CreateBookingRequestToJSON(requestParameters["createBookingRequest"])
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => CreateBookingResponseFromJSON(jsonValue));
  }
  /**
   * Create a new booking
   */
  async createBooking(createBookingRequest, initOverrides) {
    const response = await this.createBookingRaw({ createBookingRequest }, initOverrides);
    return await response.value();
  }
  /**
   * Fetch a booking
   */
  async fetchBookingRaw(requestParameters, initOverrides) {
    if (requestParameters["reservationRef"] == null) {
      throw new RequiredError(
        "reservationRef",
        'Required parameter "reservationRef" was null or undefined when calling fetchBooking().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/reservations/{reservationRef}`;
    urlPath = urlPath.replace(`{${"reservationRef"}}`, encodeURIComponent(String(requestParameters["reservationRef"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => BookingFromJSON(jsonValue));
  }
  /**
   * Fetch a booking
   */
  async fetchBooking(reservationRef, initOverrides) {
    const response = await this.fetchBookingRaw({ reservationRef }, initOverrides);
    return await response.value();
  }
  /**
   * Find your reservation
   */
  async findReservationRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/reservations/find`;
    const response = await this.request(
      {
        path: urlPath,
        method: "POST",
        headers: headerParameters,
        query: queryParameters,
        body: FindReservationRequestToJSON(requestParameters["findReservationRequest"])
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => FindReservationResultFromJSON(jsonValue));
  }
  /**
   * Find your reservation
   */
  async findReservation(findReservationRequest, initOverrides) {
    const response = await this.findReservationRaw({ findReservationRequest }, initOverrides);
    return await response.value();
  }
  /**
   * Return options available for a booking
   */
  async getOptionsRaw(requestParameters, initOverrides) {
    if (requestParameters["reservationRef"] == null) {
      throw new RequiredError("reservationRef", 'Required parameter "reservationRef" was null or undefined when calling getOptions().');
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/reservations/{reservationRef}/options`;
    urlPath = urlPath.replace(`{${"reservationRef"}}`, encodeURIComponent(String(requestParameters["reservationRef"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => BookingOptionsFromJSON(jsonValue));
  }
  /**
   * Return options available for a booking
   */
  async getOptions(reservationRef, initOverrides) {
    const response = await this.getOptionsRaw({ reservationRef }, initOverrides);
    return await response.value();
  }
  /**
   * Retrieve loyalty point status
   */
  async getRewardPointsStatusRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    if (requestParameters["fleetType"] != null) {
      queryParameters["fleetType"] = requestParameters["fleetType"];
    }
    if (requestParameters["dealId"] != null) {
      queryParameters["dealId"] = requestParameters["dealId"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/rewards/points`;
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => jsonValue.map(RewardPointsConfigFromJSON));
  }
  /**
   * Retrieve loyalty point status
   */
  async getRewardPointsStatus(fleetType, dealId, initOverrides) {
    const response = await this.getRewardPointsStatusRaw({ fleetType, dealId }, initOverrides);
    return await response.value();
  }
  /**
   * Make updates and edit an existing reservation
   */
  async updateReservationRaw(requestParameters, initOverrides) {
    if (requestParameters["reservationRef"] == null) {
      throw new RequiredError(
        "reservationRef",
        'Required parameter "reservationRef" was null or undefined when calling updateReservation().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/reservations/{reservationRef}`;
    urlPath = urlPath.replace(`{${"reservationRef"}}`, encodeURIComponent(String(requestParameters["reservationRef"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "PATCH",
        headers: headerParameters,
        query: queryParameters,
        body: UpdateReservationRequestToJSON(requestParameters["updateReservationRequest"])
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => CreateBookingResponseFromJSON(jsonValue));
  }
  /**
   * Make updates and edit an existing reservation
   */
  async updateReservation(reservationRef, updateReservationRequest, initOverrides) {
    const response = await this.updateReservationRaw(
      { reservationRef, updateReservationRequest },
      initOverrides
    );
    return await response.value();
  }
  /**
   * Validate the update reservation
   */
  async validateUpdateReservationRaw(requestParameters, initOverrides) {
    if (requestParameters["reservationRef"] == null) {
      throw new RequiredError(
        "reservationRef",
        'Required parameter "reservationRef" was null or undefined when calling validateUpdateReservation().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/reservations/{reservationRef}/validate-update-reservation`;
    urlPath = urlPath.replace(`{${"reservationRef"}}`, encodeURIComponent(String(requestParameters["reservationRef"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "POST",
        headers: headerParameters,
        query: queryParameters,
        body: UpdateReservationRequestToJSON(requestParameters["updateReservationRequest"])
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => ValidateUpdateResponseFromJSON(jsonValue));
  }
  /**
   * Validate the update reservation
   */
  async validateUpdateReservation(reservationRef, updateReservationRequest, initOverrides) {
    const response = await this.validateUpdateReservationRaw(
      { reservationRef, updateReservationRequest },
      initOverrides
    );
    return await response.value();
  }
};

// src/rentals-api-v3/apis/SelfServiceApi.ts
var SelfServiceApi = class extends BaseAPI {
  /**
   * Get self service preference details
   */
  async getReservationPreferencesRaw(requestParameters, initOverrides) {
    if (requestParameters["ref"] == null) {
      throw new RequiredError("ref", 'Required parameter "ref" was null or undefined when calling getReservationPreferences().');
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/self-service/reservation/ref/{ref}/preferences`;
    urlPath = urlPath.replace(`{${"ref"}}`, encodeURIComponent(String(requestParameters["ref"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "POST",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => SelfServicePreferencesFromJSON(jsonValue));
  }
  /**
   * Get self service preference details
   */
  async getReservationPreferences(ref, initOverrides) {
    const response = await this.getReservationPreferencesRaw({ ref }, initOverrides);
    return await response.value();
  }
  /**
   * Retrieve loyalty status for a reservation
   */
  async getReservationRewardPointsStatusRaw(requestParameters, initOverrides) {
    if (requestParameters["ref"] == null) {
      throw new RequiredError("ref", 'Required parameter "ref" was null or undefined when calling getReservationRewardPointsStatus().');
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/self-service/reservation/ref/{ref}/reward-points`;
    urlPath = urlPath.replace(`{${"ref"}}`, encodeURIComponent(String(requestParameters["ref"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => ReservationRewardPointsStatusFromJSON(jsonValue));
  }
  /**
   * Retrieve loyalty status for a reservation
   */
  async getReservationRewardPointsStatus(ref, initOverrides) {
    const response = await this.getReservationRewardPointsStatusRaw({ ref }, initOverrides);
    return await response.value();
  }
  /**
   * Get a booking by reservation reference selfservice
   */
  async getSelfServiceBookingRaw(requestParameters, initOverrides) {
    if (requestParameters["ref"] == null) {
      throw new RequiredError("ref", 'Required parameter "ref" was null or undefined when calling getSelfServiceBooking().');
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/self-service/reservation/ref/{ref}`;
    urlPath = urlPath.replace(`{${"ref"}}`, encodeURIComponent(String(requestParameters["ref"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => SelfServiceBookingFromJSON(jsonValue));
  }
  /**
   * Get a booking by reservation reference selfservice
   */
  async getSelfServiceBooking(ref, initOverrides) {
    const response = await this.getSelfServiceBookingRaw({ ref }, initOverrides);
    return await response.value();
  }
  /**
   * Get a summaries
   */
  async getSelfServiceSummariesRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    if (requestParameters["status"] != null) {
      queryParameters["status"] = requestParameters["status"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/self-service/booking-summaries`;
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => BookingSummaryResponseFromJSON(jsonValue));
  }
  /**
   * Get a summaries
   */
  async getSelfServiceSummaries(status, initOverrides) {
    const response = await this.getSelfServiceSummariesRaw({ status }, initOverrides);
    return await response.value();
  }
  /**
   * Link a reservation with your JUCY account
   */
  async linkReservationRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/self-service/link-reservation`;
    const response = await this.request(
      {
        path: urlPath,
        method: "POST",
        headers: headerParameters,
        query: queryParameters,
        body: LinkReservationRequestToJSON(requestParameters["linkReservationRequest"])
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => MessageResponseFromJSON(jsonValue));
  }
  /**
   * Link a reservation with your JUCY account
   */
  async linkReservation(linkReservationRequest, initOverrides) {
    const response = await this.linkReservationRaw({ linkReservationRequest }, initOverrides);
    return await response.value();
  }
  /**
   * Record events for self-service
   */
  async logSelfServiceEventRaw(requestParameters, initOverrides) {
    if (requestParameters["reservationRef"] == null) {
      throw new RequiredError(
        "reservationRef",
        'Required parameter "reservationRef" was null or undefined when calling logSelfServiceEvent().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/self-service/log-event/{reservationRef}`;
    urlPath = urlPath.replace(`{${"reservationRef"}}`, encodeURIComponent(String(requestParameters["reservationRef"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "POST",
        headers: headerParameters,
        query: queryParameters,
        body: LogSelfServiceEventRequestToJSON(requestParameters["logSelfServiceEventRequest"])
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => MessageResponseFromJSON(jsonValue));
  }
  /**
   * Record events for self-service
   */
  async logSelfServiceEvent(reservationRef, logSelfServiceEventRequest, initOverrides) {
    const response = await this.logSelfServiceEventRaw(
      { reservationRef, logSelfServiceEventRequest },
      initOverrides
    );
    return await response.value();
  }
  /**
   * Save loyalty point membership information to a reservation
   */
  async saveReservationRewardPointsRaw(requestParameters, initOverrides) {
    if (requestParameters["ref"] == null) {
      throw new RequiredError("ref", 'Required parameter "ref" was null or undefined when calling saveReservationRewardPoints().');
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/self-service/reservation/ref/{ref}/reward-points`;
    urlPath = urlPath.replace(`{${"ref"}}`, encodeURIComponent(String(requestParameters["ref"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "PATCH",
        headers: headerParameters,
        query: queryParameters,
        body: LoyaltyRequestDataToJSON(requestParameters["loyaltyRequestData"])
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => MessageResponseFromJSON(jsonValue));
  }
  /**
   * Save loyalty point membership information to a reservation
   */
  async saveReservationRewardPoints(ref, loyaltyRequestData, initOverrides) {
    const response = await this.saveReservationRewardPointsRaw({ ref, loyaltyRequestData }, initOverrides);
    return await response.value();
  }
  /**
   * Set automatic payment to reservation
   */
  async setAutoPaymentDetailsRaw(requestParameters, initOverrides) {
    if (requestParameters["ref"] == null) {
      throw new RequiredError("ref", 'Required parameter "ref" was null or undefined when calling setAutoPaymentDetails().');
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/self-service/reservation/ref/{ref}/auto-payment`;
    urlPath = urlPath.replace(`{${"ref"}}`, encodeURIComponent(String(requestParameters["ref"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "POST",
        headers: headerParameters,
        query: queryParameters,
        body: AutoPaymentRequestToJSON(requestParameters["autoPaymentRequest"])
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => MessageResponseFromJSON(jsonValue));
  }
  /**
   * Set automatic payment to reservation
   */
  async setAutoPaymentDetails(ref, autoPaymentRequest, initOverrides) {
    const response = await this.setAutoPaymentDetailsRaw({ ref, autoPaymentRequest }, initOverrides);
    return await response.value();
  }
  /**
   * Update flight details in your booking details
   */
  async updateFlightDetailsRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/self-service/update-flight-details`;
    const response = await this.request(
      {
        path: urlPath,
        method: "POST",
        headers: headerParameters,
        query: queryParameters,
        body: UpdateFlightDetailsRequestToJSON(requestParameters["updateFlightDetailsRequest"])
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => MessageResponseFromJSON(jsonValue));
  }
  /**
   * Update flight details in your booking details
   */
  async updateFlightDetails(updateFlightDetailsRequest, initOverrides) {
    const response = await this.updateFlightDetailsRaw({ updateFlightDetailsRequest }, initOverrides);
    return await response.value();
  }
};

// src/rentals-api-v3/apis/ShuttlesApi.ts
var ShuttlesApi = class extends BaseAPI {
  /**
   * List Shuttle information
   */
  async listShuttlesRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    if (requestParameters["countryCode"] != null) {
      queryParameters["countryCode"] = requestParameters["countryCode"];
    }
    if (requestParameters["branchCode"] != null) {
      queryParameters["branchCode"] = requestParameters["branchCode"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/shuttles`;
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => jsonValue.map(ShuttleFromJSON));
  }
  /**
   * List Shuttle information
   */
  async listShuttles(countryCode, branchCode, initOverrides) {
    const response = await this.listShuttlesRaw({ countryCode, branchCode }, initOverrides);
    return await response.value();
  }
};

// src/rentals-api-v3/apis/SitesApi.ts
var SitesApi = class extends BaseAPI {
  /**
   * Get branch page information
   */
  async getBranchContentByCodeRaw(requestParameters, initOverrides) {
    if (requestParameters["siteCode"] == null) {
      throw new RequiredError("siteCode", 'Required parameter "siteCode" was null or undefined when calling getBranchContentByCode().');
    }
    const queryParameters = {};
    if (requestParameters["brand"] != null) {
      queryParameters["brand"] = requestParameters["brand"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/sites/{siteCode}/content`;
    urlPath = urlPath.replace(`{${"siteCode"}}`, encodeURIComponent(String(requestParameters["siteCode"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => BranchContentFromJSON(jsonValue));
  }
  /**
   * Get branch page information
   */
  async getBranchContentByCode(siteCode, brand, initOverrides) {
    const response = await this.getBranchContentByCodeRaw({ siteCode, brand }, initOverrides);
    return await response.value();
  }
  /**
   * Get branch information
   */
  async getSiteByCodeRaw(requestParameters, initOverrides) {
    if (requestParameters["siteCode"] == null) {
      throw new RequiredError("siteCode", 'Required parameter "siteCode" was null or undefined when calling getSiteByCode().');
    }
    const queryParameters = {};
    if (requestParameters["businessUnitCode"] != null) {
      queryParameters["businessUnitCode"] = requestParameters["businessUnitCode"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/sites/{siteCode}`;
    urlPath = urlPath.replace(`{${"siteCode"}}`, encodeURIComponent(String(requestParameters["siteCode"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => SiteFromJSON(jsonValue));
  }
  /**
   * Get branch information
   */
  async getSiteByCode(siteCode, businessUnitCode, initOverrides) {
    const response = await this.getSiteByCodeRaw({ siteCode, businessUnitCode }, initOverrides);
    return await response.value();
  }
  /**
   * List branch page information
   */
  async listBranchContentRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    if (requestParameters["brand"] != null) {
      queryParameters["brand"] = requestParameters["brand"];
    }
    if (requestParameters["countryCode"] != null) {
      queryParameters["countryCode"] = requestParameters["countryCode"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/sites/content`;
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => jsonValue.map(BranchContentFromJSON));
  }
  /**
   * List branch page information
   */
  async listBranchContent(brand, countryCode, initOverrides) {
    const response = await this.listBranchContentRaw({ brand, countryCode }, initOverrides);
    return await response.value();
  }
  /**
   * Get branch information
   */
  async listSitesRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    if (requestParameters["countryCode"] != null) {
      queryParameters["countryCode"] = requestParameters["countryCode"];
    }
    if (requestParameters["businessUnitCode"] != null) {
      queryParameters["businessUnitCode"] = requestParameters["businessUnitCode"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/sites`;
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => jsonValue.map(SiteFromJSON));
  }
  /**
   * Get branch information
   */
  async listSites(countryCode, businessUnitCode, initOverrides) {
    const response = await this.listSitesRaw({ countryCode, businessUnitCode }, initOverrides);
    return await response.value();
  }
};

// src/rentals-api-v3/apis/TripApi.ts
var TripApi = class extends BaseAPI {
  /**
   * Search for availability and pricing for alternative dates
   */
  async alternateDatesSearchRaw(requestParameters, initOverrides) {
    if (requestParameters["pickUpLocation"] == null) {
      throw new RequiredError(
        "pickUpLocation",
        'Required parameter "pickUpLocation" was null or undefined when calling alternateDatesSearch().'
      );
    }
    if (requestParameters["dropOffLocation"] == null) {
      throw new RequiredError(
        "dropOffLocation",
        'Required parameter "dropOffLocation" was null or undefined when calling alternateDatesSearch().'
      );
    }
    if (requestParameters["pickUpDate"] == null) {
      throw new RequiredError(
        "pickUpDate",
        'Required parameter "pickUpDate" was null or undefined when calling alternateDatesSearch().'
      );
    }
    if (requestParameters["dropOffDate"] == null) {
      throw new RequiredError(
        "dropOffDate",
        'Required parameter "dropOffDate" was null or undefined when calling alternateDatesSearch().'
      );
    }
    if (requestParameters["fleetTypeCode"] == null) {
      throw new RequiredError(
        "fleetTypeCode",
        'Required parameter "fleetTypeCode" was null or undefined when calling alternateDatesSearch().'
      );
    }
    if (requestParameters["days"] == null) {
      throw new RequiredError("days", 'Required parameter "days" was null or undefined when calling alternateDatesSearch().');
    }
    const queryParameters = {};
    if (requestParameters["pickUpLocation"] != null) {
      queryParameters["pickUpLocation"] = requestParameters["pickUpLocation"];
    }
    if (requestParameters["dropOffLocation"] != null) {
      queryParameters["dropOffLocation"] = requestParameters["dropOffLocation"];
    }
    if (requestParameters["pickUpDate"] != null) {
      queryParameters["pickUpDate"] = requestParameters["pickUpDate"];
    }
    if (requestParameters["dropOffDate"] != null) {
      queryParameters["dropOffDate"] = requestParameters["dropOffDate"];
    }
    if (requestParameters["fleetTypeCode"] != null) {
      queryParameters["fleetTypeCode"] = requestParameters["fleetTypeCode"];
    }
    if (requestParameters["campaignCode"] != null) {
      queryParameters["campaignCode"] = requestParameters["campaignCode"];
    }
    if (requestParameters["days"] != null) {
      queryParameters["days"] = requestParameters["days"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/trip/alternate-dates-search`;
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => jsonValue.map(AvailabilitySearchResponseFromJSON));
  }
  /**
   * Search for availability and pricing for alternative dates
   */
  async alternateDatesSearch(pickUpLocation, dropOffLocation, pickUpDate, dropOffDate, fleetTypeCode, days, campaignCode, initOverrides) {
    const response = await this.alternateDatesSearchRaw(
      {
        pickUpLocation,
        dropOffLocation,
        pickUpDate,
        dropOffDate,
        fleetTypeCode,
        days,
        campaignCode
      },
      initOverrides
    );
    return await response.value();
  }
  /**
   * Search for availability and pricing
   */
  async availabilitySearchRaw(requestParameters, initOverrides) {
    if (requestParameters["pickUpLocation"] == null) {
      throw new RequiredError(
        "pickUpLocation",
        'Required parameter "pickUpLocation" was null or undefined when calling availabilitySearch().'
      );
    }
    if (requestParameters["dropOffLocation"] == null) {
      throw new RequiredError(
        "dropOffLocation",
        'Required parameter "dropOffLocation" was null or undefined when calling availabilitySearch().'
      );
    }
    if (requestParameters["pickUpDate"] == null) {
      throw new RequiredError("pickUpDate", 'Required parameter "pickUpDate" was null or undefined when calling availabilitySearch().');
    }
    if (requestParameters["dropOffDate"] == null) {
      throw new RequiredError(
        "dropOffDate",
        'Required parameter "dropOffDate" was null or undefined when calling availabilitySearch().'
      );
    }
    if (requestParameters["fleetTypeCode"] == null) {
      throw new RequiredError(
        "fleetTypeCode",
        'Required parameter "fleetTypeCode" was null or undefined when calling availabilitySearch().'
      );
    }
    const queryParameters = {};
    if (requestParameters["pickUpLocation"] != null) {
      queryParameters["pickUpLocation"] = requestParameters["pickUpLocation"];
    }
    if (requestParameters["dropOffLocation"] != null) {
      queryParameters["dropOffLocation"] = requestParameters["dropOffLocation"];
    }
    if (requestParameters["pickUpDate"] != null) {
      queryParameters["pickUpDate"] = requestParameters["pickUpDate"];
    }
    if (requestParameters["dropOffDate"] != null) {
      queryParameters["dropOffDate"] = requestParameters["dropOffDate"];
    }
    if (requestParameters["fleetTypeCode"] != null) {
      queryParameters["fleetTypeCode"] = requestParameters["fleetTypeCode"];
    }
    if (requestParameters["driverAge"] != null) {
      queryParameters["driverAge"] = requestParameters["driverAge"];
    }
    if (requestParameters["campaignCode"] != null) {
      queryParameters["campaignCode"] = requestParameters["campaignCode"];
    }
    if (requestParameters["mergeSurchargesFees"] != null) {
      queryParameters["mergeSurchargesFees"] = requestParameters["mergeSurchargesFees"];
    }
    if (requestParameters["reservationReference"] != null) {
      queryParameters["reservationReference"] = requestParameters["reservationReference"];
    }
    if (requestParameters["pascal"] != null) {
      queryParameters["pascal"] = requestParameters["pascal"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/trip/availability`;
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => AvailabilitySearchResponseFromJSON(jsonValue));
  }
  /**
   * Search for availability and pricing
   */
  async availabilitySearch(pickUpLocation, dropOffLocation, pickUpDate, dropOffDate, fleetTypeCode, driverAge, campaignCode, mergeSurchargesFees, reservationReference, pascal, initOverrides) {
    const response = await this.availabilitySearchRaw(
      {
        pickUpLocation,
        dropOffLocation,
        pickUpDate,
        dropOffDate,
        fleetTypeCode,
        driverAge,
        campaignCode,
        mergeSurchargesFees,
        reservationReference,
        pascal
      },
      initOverrides
    );
    return await response.value();
  }
  /**
   * Get pricing, extras and insurance options for a trip
   */
  async tripInfoRaw(requestParameters, initOverrides) {
    if (requestParameters["pickUpLocation"] == null) {
      throw new RequiredError("pickUpLocation", 'Required parameter "pickUpLocation" was null or undefined when calling tripInfo().');
    }
    if (requestParameters["dropOffLocation"] == null) {
      throw new RequiredError("dropOffLocation", 'Required parameter "dropOffLocation" was null or undefined when calling tripInfo().');
    }
    if (requestParameters["pickUpDate"] == null) {
      throw new RequiredError("pickUpDate", 'Required parameter "pickUpDate" was null or undefined when calling tripInfo().');
    }
    if (requestParameters["dropOffDate"] == null) {
      throw new RequiredError("dropOffDate", 'Required parameter "dropOffDate" was null or undefined when calling tripInfo().');
    }
    if (requestParameters["fleetCategory"] == null) {
      throw new RequiredError("fleetCategory", 'Required parameter "fleetCategory" was null or undefined when calling tripInfo().');
    }
    const queryParameters = {};
    if (requestParameters["pickUpLocation"] != null) {
      queryParameters["pickUpLocation"] = requestParameters["pickUpLocation"];
    }
    if (requestParameters["dropOffLocation"] != null) {
      queryParameters["dropOffLocation"] = requestParameters["dropOffLocation"];
    }
    if (requestParameters["pickUpDate"] != null) {
      queryParameters["pickUpDate"] = requestParameters["pickUpDate"];
    }
    if (requestParameters["dropOffDate"] != null) {
      queryParameters["dropOffDate"] = requestParameters["dropOffDate"];
    }
    if (requestParameters["fleetCategory"] != null) {
      queryParameters["fleetCategory"] = requestParameters["fleetCategory"];
    }
    if (requestParameters["driverAge"] != null) {
      queryParameters["driverAge"] = requestParameters["driverAge"];
    }
    if (requestParameters["numberOfPeople"] != null) {
      queryParameters["numberOfPeople"] = requestParameters["numberOfPeople"];
    }
    if (requestParameters["campaignCode"] != null) {
      queryParameters["campaignCode"] = requestParameters["campaignCode"];
    }
    if (requestParameters["pascal"] != null) {
      queryParameters["pascal"] = requestParameters["pascal"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/trip/info`;
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => TripDetailsFromJSON(jsonValue));
  }
  /**
   * Get pricing, extras and insurance options for a trip
   */
  async tripInfo(pickUpLocation, dropOffLocation, pickUpDate, dropOffDate, fleetCategory, driverAge, numberOfPeople, campaignCode, pascal, initOverrides) {
    const response = await this.tripInfoRaw(
      {
        pickUpLocation,
        dropOffLocation,
        pickUpDate,
        dropOffDate,
        fleetCategory,
        driverAge,
        numberOfPeople,
        campaignCode,
        pascal
      },
      initOverrides
    );
    return await response.value();
  }
};

// src/RentalsApiClientV3.ts
var RentalsApiClient = class {
  config;
  agent;
  mailList;
  reservations;
  selfService;
  paymentService;
  data;
  quotes;
  tripApi;
  sites;
  catalog;
  fleetCategories;
  businessUnits;
  shuttles;
  countries;
  commissions;
  ferryApi;
  constructor(config) {
    this.config = new Configuration({
      ...config,
      fetchApi: fetch
    });
    this.config = new Configuration(config);
    this.agent = new AgentApi(this.config);
    this.mailList = new MailListApi(this.config);
    this.reservations = new ReservationsApi(this.config);
    this.selfService = new SelfServiceApi(this.config);
    this.paymentService = new PaymentApi(this.config);
    this.data = new DataApi(this.config);
    this.quotes = new QuotesApi(this.config);
    this.tripApi = new TripApi(this.config);
    this.sites = new SitesApi(this.config);
    this.catalog = new CatalogApi(this.config);
    this.fleetCategories = new FleetCategoriesApi(this.config);
    this.businessUnits = new BusinessUnitsApi(this.config);
    this.shuttles = new ShuttlesApi(this.config);
    this.countries = new CountryApi(this.config);
    this.commissions = new CommissionsApi(this.config);
    this.ferryApi = new FerryApi(this.config);
  }
};
var RentalsApiClientV3_default = RentalsApiClient;

// src/rentals-api/runtime.ts
import { formatISO as formatISO2 } from "date-fns";
var BASE_PATH2 = "https://rentals.api.jucy.cloud".replace(/\/+$/, "");
var Configuration2 = class {
  constructor(configuration = {}) {
    this.configuration = configuration;
  }
  set config(configuration) {
    this.configuration = configuration;
  }
  get basePath() {
    return this.configuration.basePath != null ? this.configuration.basePath : BASE_PATH2;
  }
  get fetchApi() {
    return this.configuration.fetchApi || fetch;
  }
  get middleware() {
    return this.configuration.middleware || [];
  }
  get queryParamsStringify() {
    return this.configuration.queryParamsStringify || querystring2;
  }
  get username() {
    return this.configuration.username;
  }
  get password() {
    return this.configuration.password;
  }
  get apiKey() {
    const apiKey = this.configuration.apiKey;
    if (apiKey) {
      return typeof apiKey === "function" ? apiKey : () => apiKey;
    }
    return void 0;
  }
  get accessToken() {
    const accessToken = this.configuration.accessToken;
    if (accessToken) {
      return typeof accessToken === "function" ? accessToken : async () => accessToken;
    }
    return void 0;
  }
  get headers() {
    return this.configuration.headers;
  }
  get credentials() {
    return this.configuration.credentials;
  }
};
var DefaultConfig2 = new Configuration2();
async function createResponseErrorMessage2(response, fallback = "Response returned an error code") {
  try {
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.startsWith("application/json")) {
      const json = await response.json();
      return new ResponseError2(response, json?.message || json?.Message || response.statusText || fallback, json);
    }
    const body = await response.text();
    return new ResponseError2(response, body || response.statusText || fallback, { body });
  } catch (e) {
    return new ResponseError2(response, fallback, { error: e });
  }
}
var BaseAPI2 = class _BaseAPI {
  constructor(configuration = DefaultConfig2) {
    this.configuration = configuration;
    this.middleware = configuration.middleware;
  }
  static jsonRegex = new RegExp("^(:?application/json|[^;/ 	]+/[^;/ 	]+[+]json)[ 	]*(:?;.*)?$", "i");
  middleware;
  withMiddleware(...middlewares) {
    const next = this.clone();
    next.middleware = next.middleware.concat(...middlewares);
    return next;
  }
  withPreMiddleware(...preMiddlewares) {
    const middlewares = preMiddlewares.map((pre) => ({ pre }));
    return this.withMiddleware(...middlewares);
  }
  withPostMiddleware(...postMiddlewares) {
    const middlewares = postMiddlewares.map((post) => ({ post }));
    return this.withMiddleware(...middlewares);
  }
  /**
   * Check if the given MIME is a JSON MIME.
   * JSON MIME examples:
   *   application/json
   *   application/json; charset=UTF8
   *   APPLICATION/JSON
   *   application/vnd.company+json
   * @param mime - MIME (Multipurpose Internet Mail Extensions)
   * @return True if the given MIME is JSON, false otherwise.
   */
  isJsonMime(mime) {
    if (!mime) {
      return false;
    }
    return _BaseAPI.jsonRegex.test(mime);
  }
  async request(context, initOverrides) {
    const { url, init } = await this.createFetchParams(context, initOverrides);
    const response = await this.fetchApi(url, init);
    if (response && response.status >= 200 && response.status < 300) {
      return response;
    }
    throw await createResponseErrorMessage2(response, "Response returned an error code");
  }
  async createFetchParams(context, initOverrides) {
    let url = this.configuration.basePath + context.path;
    if (context.query !== void 0 && Object.keys(context.query).length !== 0) {
      url += "?" + this.configuration.queryParamsStringify(context.query);
    }
    const headers = Object.assign({}, this.configuration.headers, context.headers);
    Object.keys(headers).forEach((key) => headers[key] === void 0 ? delete headers[key] : {});
    const initOverrideFn = typeof initOverrides === "function" ? initOverrides : async () => initOverrides;
    const initParams = {
      method: context.method,
      headers,
      body: context.body,
      credentials: this.configuration.credentials
    };
    const overriddenInit = {
      ...initParams,
      ...await initOverrideFn({
        init: initParams,
        context
      })
    };
    let body;
    if (isFormData2(overriddenInit.body) || overriddenInit.body instanceof URLSearchParams || isBlob2(overriddenInit.body)) {
      body = overriddenInit.body;
    } else if (this.isJsonMime(headers["Content-Type"])) {
      body = JSON.stringify(overriddenInit.body);
    } else {
      body = overriddenInit.body;
    }
    const init = {
      ...overriddenInit,
      body
    };
    return { url, init };
  }
  fetchApi = async (url, init) => {
    let fetchParams = { url, init };
    for (const middleware of this.middleware) {
      if (middleware.pre) {
        fetchParams = await middleware.pre({
          fetch: this.fetchApi,
          ...fetchParams
        }) || fetchParams;
      }
    }
    let response = void 0;
    try {
      response = await (this.configuration.fetchApi || fetch)(fetchParams.url, fetchParams.init);
    } catch (e) {
      for (const middleware of this.middleware) {
        if (middleware.onError) {
          response = await middleware.onError({
            fetch: this.fetchApi,
            url: fetchParams.url,
            init: fetchParams.init,
            error: e,
            response: response ? response.clone() : void 0
          }) || response;
        }
      }
      if (response === void 0) {
        if (e instanceof Error) {
          throw new FetchError2(e, "The request failed and the interceptors did not return an alternative response");
        } else {
          throw e;
        }
      }
    }
    for (const middleware of this.middleware) {
      if (middleware.post) {
        response = await middleware.post({
          fetch: this.fetchApi,
          url: fetchParams.url,
          init: fetchParams.init,
          response: response.clone()
        }) || response;
      }
    }
    return response;
  };
  /**
   * Create a shallow clone of `this` by constructing a new instance
   * and then shallow cloning data members.
   */
  clone() {
    const constructor = this.constructor;
    const next = new constructor(this.configuration);
    next.middleware = this.middleware.slice();
    return next;
  }
};
function isBlob2(value) {
  return typeof Blob !== "undefined" && value instanceof Blob;
}
function isFormData2(value) {
  return typeof FormData !== "undefined" && value instanceof FormData;
}
function stringToDate2(value) {
  return value ? new Date(value.replace(/Z$/, "")) : /* @__PURE__ */ new Date("invalid");
}
function dateToString2(value) {
  return value ? formatISO2(value)?.replace(/Z$/, "") : void 0;
}
var ResponseError2 = class extends Error {
  constructor(response, msg, data) {
    super(msg);
    this.response = response;
    this.data = data;
  }
  name = "ResponseError";
  data;
};
var FetchError2 = class extends Error {
  constructor(cause, msg) {
    super(msg);
    this.cause = cause;
  }
  name = "FetchError";
};
var RequiredError2 = class extends Error {
  constructor(field, msg) {
    super(msg);
    this.field = field;
  }
  name = "RequiredError";
};
var COLLECTION_FORMATS = {
  csv: ",",
  ssv: " ",
  tsv: "	",
  pipes: "|"
};
function querystring2(params, prefix = "") {
  return Object.keys(params).map((key) => querystringSingleKey2(key, params[key], prefix)).filter((part) => part.length > 0).join("&");
}
function querystringSingleKey2(key, value, keyPrefix = "") {
  const fullKey = keyPrefix + (keyPrefix.length ? `[${key}]` : key);
  if (value instanceof Array) {
    const multiValue = value.map((singleValue) => encodeURIComponent(String(singleValue))).join(`&${encodeURIComponent(fullKey)}=`);
    return `${encodeURIComponent(fullKey)}=${multiValue}`;
  }
  if (value instanceof Set) {
    const valueAsArray = Array.from(value);
    return querystringSingleKey2(key, valueAsArray, keyPrefix);
  }
  if (value instanceof Date) {
    return `${encodeURIComponent(fullKey)}=${encodeURIComponent(value.toISOString())}`;
  }
  if (value instanceof Object) {
    return querystring2(value, fullKey);
  }
  return `${encodeURIComponent(fullKey)}=${encodeURIComponent(String(value))}`;
}
function exists(json, key) {
  const value = json[key];
  return value !== null && value !== void 0;
}
function mapValues(data, fn) {
  const result = {};
  for (const key of Object.keys(data)) {
    result[key] = fn(data[key]);
  }
  return result;
}
function canConsumeForm(consumes) {
  for (const consume of consumes) {
    if ("multipart/form-data" === consume.contentType) {
      return true;
    }
  }
  return false;
}
var JSONApiResponse2 = class {
  constructor(raw, transformer = (jsonValue) => jsonValue) {
    this.raw = raw;
    this.transformer = transformer;
  }
  async value() {
    return this.transformer(await this.raw.json());
  }
};
var VoidApiResponse = class {
  constructor(raw) {
    this.raw = raw;
  }
  async value() {
    return void 0;
  }
};
var BlobApiResponse = class {
  constructor(raw) {
    this.raw = raw;
  }
  async value() {
    return await this.raw.blob();
  }
};
var TextApiResponse2 = class {
  constructor(raw) {
    this.raw = raw;
  }
  async value() {
    return await this.raw.text();
  }
};

// src/rentals-api/models/CustomerAddress.ts
function instanceOfCustomerAddress(value) {
  if (!("street" in value) || value["street"] === void 0) return false;
  if (!("country" in value) || value["country"] === void 0) return false;
  return true;
}
function CustomerAddressFromJSON(json) {
  return CustomerAddressFromJSONTyped(json, false);
}
function CustomerAddressFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    street: json["street"],
    city: json["city"] == null ? void 0 : json["city"],
    postCode: json["postCode"] == null ? void 0 : json["postCode"],
    state: json["state"] == null ? void 0 : json["state"],
    country: json["country"]
  };
}
function CustomerAddressToJSON(json) {
  return CustomerAddressToJSONTyped(json, false);
}
function CustomerAddressToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    street: value["street"],
    city: value["city"],
    postCode: value["postCode"],
    state: value["state"],
    country: value["country"]
  };
}

// src/rentals-api/models/CustomerDriversLicense.ts
function instanceOfCustomerDriversLicense(value) {
  if (!("number" in value) || value["number"] === void 0) return false;
  if (!("country" in value) || value["country"] === void 0) return false;
  if (!("expiry" in value) || value["expiry"] === void 0) return false;
  return true;
}
function CustomerDriversLicenseFromJSON(json) {
  return CustomerDriversLicenseFromJSONTyped(json, false);
}
function CustomerDriversLicenseFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    number: json["number"],
    country: json["country"],
    expiry: stringToDate2(json["expiry"]),
    frontImage: json["frontImage"] == null ? void 0 : json["frontImage"],
    backImage: json["backImage"] == null ? void 0 : json["backImage"]
  };
}
function CustomerDriversLicenseToJSON(json) {
  return CustomerDriversLicenseToJSONTyped(json, false);
}
function CustomerDriversLicenseToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    number: value["number"],
    country: value["country"],
    expiry: dateToString2(value["expiry"])?.substring(0, 10),
    frontImage: value["frontImage"],
    backImage: value["backImage"]
  };
}

// src/rentals-api/models/AdditionalDriver.ts
function instanceOfAdditionalDriver(value) {
  if (!("firstName" in value) || value["firstName"] === void 0) return false;
  if (!("lastName" in value) || value["lastName"] === void 0) return false;
  if (!("email" in value) || value["email"] === void 0) return false;
  if (!("driversLicense" in value) || value["driversLicense"] === void 0) return false;
  return true;
}
function AdditionalDriverFromJSON(json) {
  return AdditionalDriverFromJSONTyped(json, false);
}
function AdditionalDriverFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    id: json["id"] == null ? void 0 : json["id"],
    firstName: json["firstName"],
    lastName: json["lastName"],
    email: json["email"],
    mobilePhone: json["mobilePhone"] == null ? void 0 : json["mobilePhone"],
    optIn: json["optIn"] == null ? void 0 : json["optIn"],
    dateOfBirth: json["dateOfBirth"] == null ? void 0 : stringToDate2(json["dateOfBirth"]),
    lucyClubNumber: json["lucyClubNumber"] == null ? void 0 : json["lucyClubNumber"],
    driversLicense: CustomerDriversLicenseFromJSON(json["driversLicense"]),
    address: json["address"] == null ? void 0 : CustomerAddressFromJSON(json["address"]),
    isDelete: json["isDelete"] == null ? void 0 : json["isDelete"]
  };
}
function AdditionalDriverToJSON(json) {
  return AdditionalDriverToJSONTyped(json, false);
}
function AdditionalDriverToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    id: value["id"],
    firstName: value["firstName"],
    lastName: value["lastName"],
    email: value["email"],
    mobilePhone: value["mobilePhone"],
    optIn: value["optIn"],
    dateOfBirth: value["dateOfBirth"] == null ? void 0 : dateToString2(value["dateOfBirth"])?.substring(0, 10),
    lucyClubNumber: value["lucyClubNumber"],
    driversLicense: CustomerDriversLicenseToJSON(value["driversLicense"]),
    address: CustomerAddressToJSON(value["address"]),
    isDelete: value["isDelete"]
  };
}

// src/rentals-api/models/Money.ts
function instanceOfMoney(value) {
  return true;
}
function MoneyFromJSON(json) {
  return MoneyFromJSONTyped(json, false);
}
function MoneyFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    currencyCode: json["currencyCode"] == null ? void 0 : json["currencyCode"],
    value: json["value"] == null ? void 0 : json["value"]
  };
}
function MoneyToJSON(json) {
  return MoneyToJSONTyped(json, false);
}
function MoneyToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    currencyCode: value["currencyCode"],
    value: value["value"]
  };
}

// src/rentals-api/models/BalanceDueResponse.ts
function instanceOfBalanceDueResponse(value) {
  return true;
}
function BalanceDueResponseFromJSON(json) {
  return BalanceDueResponseFromJSONTyped(json, false);
}
function BalanceDueResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    balanceDue: json["balanceDue"] == null ? void 0 : MoneyFromJSON(json["balanceDue"])
  };
}
function BalanceDueResponseToJSON(json) {
  return BalanceDueResponseToJSONTyped(json, false);
}
function BalanceDueResponseToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    balanceDue: MoneyToJSON(value["balanceDue"])
  };
}

// src/rentals-api/models/BaseResponse.ts
var BaseResponseResponseTypeEnum = {
  Success: "Success",
  Error: "Error",
  ValidationError: "Validation Error",
  UnexpectedError: "Unexpected Error"
};
function instanceOfBaseResponse(value) {
  return true;
}
function BaseResponseFromJSON(json) {
  return BaseResponseFromJSONTyped(json, false);
}
function BaseResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    responseType: json["ResponseType"] == null ? void 0 : json["ResponseType"],
    message: json["Message"] == null ? void 0 : json["Message"],
    data: json["Data"] == null ? void 0 : json["Data"]
  };
}
function BaseResponseToJSON(json) {
  return BaseResponseToJSONTyped(json, false);
}
function BaseResponseToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    ResponseType: value["responseType"],
    Message: value["message"],
    Data: value["data"]
  };
}

// src/rentals-api/models/BookedWith.ts
function instanceOfBookedWith(value) {
  if (!("name" in value) || value["name"] === void 0) return false;
  return true;
}
function BookedWithFromJSON2(json) {
  return BookedWithFromJSONTyped4(json, false);
}
function BookedWithFromJSONTyped4(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    name: json["name"],
    email: json["email"] == null ? void 0 : json["email"],
    phone: json["phone"] == null ? void 0 : json["phone"],
    logo: json["logo"] == null ? void 0 : json["logo"]
  };
}
function BookedWithToJSON2(json) {
  return BookedWithToJSONTyped3(json, false);
}
function BookedWithToJSONTyped3(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    name: value["name"],
    email: value["email"],
    phone: value["phone"],
    logo: value["logo"]
  };
}

// src/rentals-api/models/BookingSummaryHirer.ts
function instanceOfBookingSummaryHirer(value) {
  if (!("firstName" in value) || value["firstName"] === void 0) return false;
  if (!("lastName" in value) || value["lastName"] === void 0) return false;
  if (!("email" in value) || value["email"] === void 0) return false;
  return true;
}
function BookingSummaryHirerFromJSON2(json) {
  return BookingSummaryHirerFromJSONTyped3(json, false);
}
function BookingSummaryHirerFromJSONTyped3(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    firstName: json["firstName"],
    lastName: json["lastName"],
    email: json["email"],
    checkedIn: json["checkedIn"] == null ? void 0 : json["checkedIn"]
  };
}
function BookingSummaryHirerToJSON2(json) {
  return BookingSummaryHirerToJSONTyped2(json, false);
}
function BookingSummaryHirerToJSONTyped2(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    firstName: value["firstName"],
    lastName: value["lastName"],
    email: value["email"],
    checkedIn: value["checkedIn"]
  };
}

// src/rentals-api/models/BranchDetails.ts
function instanceOfBranchDetails(value) {
  return true;
}
function BranchDetailsFromJSON(json) {
  return BranchDetailsFromJSONTyped(json, false);
}
function BranchDetailsFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    address: json["address"] == null ? void 0 : json["address"],
    city: json["city"] == null ? void 0 : json["city"],
    fax: json["fax"] == null ? void 0 : json["fax"],
    phone: json["phone"] == null ? void 0 : json["phone"],
    postCode: json["postCode"] == null ? void 0 : json["postCode"]
  };
}
function BranchDetailsToJSON(json) {
  return BranchDetailsToJSONTyped(json, false);
}
function BranchDetailsToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    address: value["address"],
    city: value["city"],
    fax: value["fax"],
    phone: value["phone"],
    postCode: value["postCode"]
  };
}

// src/rentals-api/models/CardOnFile.ts
function instanceOfCardOnFile(value) {
  if (!("added" in value) || value["added"] === void 0) return false;
  if (!("number" in value) || value["number"] === void 0) return false;
  if (!("expiry" in value) || value["expiry"] === void 0) return false;
  if (!("name" in value) || value["name"] === void 0) return false;
  return true;
}
function CardOnFileFromJSON(json) {
  return CardOnFileFromJSONTyped(json, false);
}
function CardOnFileFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    added: stringToDate2(json["added"]),
    number: json["number"],
    expiry: json["expiry"],
    name: json["name"],
    type: json["type"] == null ? void 0 : json["type"]
  };
}
function CardOnFileToJSON(json) {
  return CardOnFileToJSONTyped(json, false);
}
function CardOnFileToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    added: dateToString2(value["added"])?.substring(0, 10),
    number: value["number"],
    expiry: value["expiry"],
    name: value["name"],
    type: value["type"]
  };
}

// src/rentals-api/models/Customer.ts
function instanceOfCustomer(value) {
  if (!("firstName" in value) || value["firstName"] === void 0) return false;
  if (!("lastName" in value) || value["lastName"] === void 0) return false;
  if (!("email" in value) || value["email"] === void 0) return false;
  if (!("dateOfBirth" in value) || value["dateOfBirth"] === void 0) return false;
  if (!("driversLicense" in value) || value["driversLicense"] === void 0) return false;
  if (!("address" in value) || value["address"] === void 0) return false;
  return true;
}
function CustomerFromJSON(json) {
  return CustomerFromJSONTyped(json, false);
}
function CustomerFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    id: json["id"] == null ? void 0 : json["id"],
    firstName: json["firstName"],
    lastName: json["lastName"],
    email: json["email"],
    mobilePhone: json["mobilePhone"] == null ? void 0 : json["mobilePhone"],
    optIn: json["optIn"] == null ? void 0 : json["optIn"],
    dateOfBirth: stringToDate2(json["dateOfBirth"]),
    lucyClubNumber: json["lucyClubNumber"] == null ? void 0 : json["lucyClubNumber"],
    driversLicense: CustomerDriversLicenseFromJSON(json["driversLicense"]),
    address: CustomerAddressFromJSON(json["address"])
  };
}
function CustomerToJSON(json) {
  return CustomerToJSONTyped(json, false);
}
function CustomerToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    id: value["id"],
    firstName: value["firstName"],
    lastName: value["lastName"],
    email: value["email"],
    mobilePhone: value["mobilePhone"],
    optIn: value["optIn"],
    dateOfBirth: dateToString2(value["dateOfBirth"])?.substring(0, 10),
    lucyClubNumber: value["lucyClubNumber"],
    driversLicense: CustomerDriversLicenseToJSON(value["driversLicense"]),
    address: CustomerAddressToJSON(value["address"])
  };
}

// src/rentals-api/models/CheckInRequest.ts
function instanceOfCheckInRequest(value) {
  if (!("reservationReference" in value) || value["reservationReference"] === void 0) return false;
  if (!("hirer" in value) || value["hirer"] === void 0) return false;
  return true;
}
function CheckInRequestFromJSON(json) {
  return CheckInRequestFromJSONTyped(json, false);
}
function CheckInRequestFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    reservationReference: json["reservationReference"],
    numberTravelling: json["numberTravelling"] == null ? void 0 : json["numberTravelling"],
    departureFlightNumber: json["departureFlightNumber"] == null ? void 0 : json["departureFlightNumber"],
    arrivalFlightNumber: json["arrivalFlightNumber"] == null ? void 0 : json["arrivalFlightNumber"],
    hirer: CustomerFromJSON(json["hirer"]),
    additionalDrivers: json["additionalDrivers"] == null ? void 0 : json["additionalDrivers"].map(AdditionalDriverFromJSON)
  };
}
function CheckInRequestToJSON(json) {
  return CheckInRequestToJSONTyped(json, false);
}
function CheckInRequestToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    reservationReference: value["reservationReference"],
    numberTravelling: value["numberTravelling"],
    departureFlightNumber: value["departureFlightNumber"],
    arrivalFlightNumber: value["arrivalFlightNumber"],
    hirer: CustomerToJSON(value["hirer"]),
    additionalDrivers: value["additionalDrivers"] == null ? void 0 : value["additionalDrivers"].map(AdditionalDriverToJSON)
  };
}

// src/rentals-api/models/CompanyDetails.ts
function instanceOfCompanyDetails(value) {
  return true;
}
function CompanyDetailsFromJSON(json) {
  return CompanyDetailsFromJSONTyped(json, false);
}
function CompanyDetailsFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    email: json["email"] == null ? void 0 : json["email"],
    name: json["name"] == null ? void 0 : json["name"],
    phone: json["phone"] == null ? void 0 : json["phone"],
    logo: json["logo"] == null ? void 0 : json["logo"],
    terms: json["terms"] == null ? void 0 : json["terms"],
    website: json["website"] == null ? void 0 : json["website"],
    taxId: json["taxId"] == null ? void 0 : json["taxId"]
  };
}
function CompanyDetailsToJSON(json) {
  return CompanyDetailsToJSONTyped(json, false);
}
function CompanyDetailsToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    email: value["email"],
    name: value["name"],
    phone: value["phone"],
    logo: value["logo"],
    terms: value["terms"],
    website: value["website"],
    taxId: value["taxId"]
  };
}

// src/rentals-api/models/ConfirmQuoteResponseData.ts
function instanceOfConfirmQuoteResponseData(value) {
  return true;
}
function ConfirmQuoteResponseDataFromJSON(json) {
  return ConfirmQuoteResponseDataFromJSONTyped(json, false);
}
function ConfirmQuoteResponseDataFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    paymentURL: json["PaymentURL"] == null ? void 0 : json["PaymentURL"]
  };
}
function ConfirmQuoteResponseDataToJSON(json) {
  return ConfirmQuoteResponseDataToJSONTyped(json, false);
}
function ConfirmQuoteResponseDataToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    PaymentURL: value["paymentURL"]
  };
}

// src/rentals-api/models/ConfirmQuoteResponse.ts
var ConfirmQuoteResponseResponseTypeEnum = {
  Success: "Success",
  Error: "Error",
  ValidationError: "Validation Error",
  UnexpectedError: "Unexpected Error"
};
function instanceOfConfirmQuoteResponse(value) {
  return true;
}
function ConfirmQuoteResponseFromJSON(json) {
  return ConfirmQuoteResponseFromJSONTyped(json, false);
}
function ConfirmQuoteResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    responseType: json["ResponseType"] == null ? void 0 : json["ResponseType"],
    message: json["Message"] == null ? void 0 : json["Message"],
    data: json["Data"] == null ? void 0 : ConfirmQuoteResponseDataFromJSON(json["Data"])
  };
}
function ConfirmQuoteResponseToJSON(json) {
  return ConfirmQuoteResponseToJSONTyped(json, false);
}
function ConfirmQuoteResponseToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    ResponseType: value["responseType"],
    Message: value["message"],
    Data: ConfirmQuoteResponseDataToJSON(value["data"])
  };
}

// src/rentals-api/models/ConfirmReservationResponse.ts
function instanceOfConfirmReservationResponse(value) {
  return true;
}
function ConfirmReservationResponseFromJSON(json) {
  return ConfirmReservationResponseFromJSONTyped(json, false);
}
function ConfirmReservationResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    paymentUrl: json["paymentUrl"] == null ? void 0 : json["paymentUrl"]
  };
}
function ConfirmReservationResponseToJSON(json) {
  return ConfirmReservationResponseToJSONTyped(json, false);
}
function ConfirmReservationResponseToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    paymentUrl: value["paymentUrl"]
  };
}

// src/rentals-api/models/FleetCategoryDailyRate.ts
function instanceOfFleetCategoryDailyRate(value) {
  if (!("currencyCode" in value) || value["currencyCode"] === void 0) return false;
  if (!("value" in value) || value["value"] === void 0) return false;
  return true;
}
function FleetCategoryDailyRateFromJSON(json) {
  return FleetCategoryDailyRateFromJSONTyped(json, false);
}
function FleetCategoryDailyRateFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    currencyCode: json["CurrencyCode"],
    value: json["Value"]
  };
}
function FleetCategoryDailyRateToJSON(json) {
  return FleetCategoryDailyRateToJSONTyped(json, false);
}
function FleetCategoryDailyRateToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    CurrencyCode: value["currencyCode"],
    Value: value["value"]
  };
}

// src/rentals-api/models/FleetCategoryMandatoryFeesPrice.ts
function instanceOfFleetCategoryMandatoryFeesPrice(value) {
  if (!("currencyCode" in value) || value["currencyCode"] === void 0) return false;
  if (!("value" in value) || value["value"] === void 0) return false;
  return true;
}
function FleetCategoryMandatoryFeesPriceFromJSON(json) {
  return FleetCategoryMandatoryFeesPriceFromJSONTyped(json, false);
}
function FleetCategoryMandatoryFeesPriceFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    currencyCode: json["CurrencyCode"],
    value: json["Value"]
  };
}
function FleetCategoryMandatoryFeesPriceToJSON(json) {
  return FleetCategoryMandatoryFeesPriceToJSONTyped(json, false);
}
function FleetCategoryMandatoryFeesPriceToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    CurrencyCode: value["currencyCode"],
    Value: value["value"]
  };
}

// src/rentals-api/models/FleetCategoryMandatoryFees.ts
function instanceOfFleetCategoryMandatoryFees(value) {
  return true;
}
function FleetCategoryMandatoryFeesFromJSON(json) {
  return FleetCategoryMandatoryFeesFromJSONTyped(json, false);
}
function FleetCategoryMandatoryFeesFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    name: json["Name"] == null ? void 0 : json["Name"],
    price: json["Price"] == null ? void 0 : FleetCategoryMandatoryFeesPriceFromJSON(json["Price"])
  };
}
function FleetCategoryMandatoryFeesToJSON(json) {
  return FleetCategoryMandatoryFeesToJSONTyped(json, false);
}
function FleetCategoryMandatoryFeesToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    Name: value["name"],
    Price: FleetCategoryMandatoryFeesPriceToJSON(value["price"])
  };
}

// src/rentals-api/models/FleetCategoryTotal.ts
function instanceOfFleetCategoryTotal(value) {
  if (!("currencyCode" in value) || value["currencyCode"] === void 0) return false;
  if (!("value" in value) || value["value"] === void 0) return false;
  return true;
}
function FleetCategoryTotalFromJSON(json) {
  return FleetCategoryTotalFromJSONTyped(json, false);
}
function FleetCategoryTotalFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    currencyCode: json["CurrencyCode"],
    value: json["Value"]
  };
}
function FleetCategoryTotalToJSON(json) {
  return FleetCategoryTotalToJSONTyped(json, false);
}
function FleetCategoryTotalToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    CurrencyCode: value["currencyCode"],
    Value: value["value"]
  };
}

// src/rentals-api/models/FleetCategory.ts
var FleetCategoryAvailabilityEnum = {
  FreeSell: "FreeSell",
  OnRequest: "OnRequest",
  StopSell: "StopSell"
};
function instanceOfFleetCategory(value) {
  if (!("name" in value) || value["name"] === void 0) return false;
  if (!("description" in value) || value["description"] === void 0) return false;
  if (!("categoryCode" in value) || value["categoryCode"] === void 0) return false;
  if (!("id" in value) || value["id"] === void 0) return false;
  if (!("type" in value) || value["type"] === void 0) return false;
  if (!("typeSlug" in value) || value["typeSlug"] === void 0) return false;
  if (!("availability" in value) || value["availability"] === void 0) return false;
  if (!("dailyRate" in value) || value["dailyRate"] === void 0) return false;
  if (!("total" in value) || value["total"] === void 0) return false;
  if (!("rentalDays" in value) || value["rentalDays"] === void 0) return false;
  return true;
}
function FleetCategoryFromJSON2(json) {
  return FleetCategoryFromJSONTyped6(json, false);
}
function FleetCategoryFromJSONTyped6(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    priceDifference: json["PriceDifference"] == null ? void 0 : json["PriceDifference"],
    name: json["Name"],
    description: json["Description"],
    categoryCode: json["CategoryCode"],
    campaignCode: json["CampaignCode"] == null ? void 0 : json["CampaignCode"],
    campaignId: json["CampaignId"] == null ? void 0 : json["CampaignId"],
    dealText: json["DealText"] == null ? void 0 : json["DealText"],
    freeDays: json["FreeDays"] == null ? void 0 : json["FreeDays"],
    id: json["Id"],
    type: json["Type"],
    typeSlug: json["TypeSlug"],
    availability: json["Availability"],
    dailyRate: FleetCategoryDailyRateFromJSON(json["DailyRate"]),
    total: FleetCategoryTotalFromJSON(json["Total"]),
    discountPercentageApplied: json["DiscountPercentageApplied"] == null ? void 0 : json["DiscountPercentageApplied"],
    mandatoryFees: json["MandatoryFees"] == null ? void 0 : json["MandatoryFees"].map(FleetCategoryMandatoryFeesFromJSON),
    rentalDays: json["RentalDays"],
    minimumHirePeriod: json["MinimumHirePeriod"] == null ? void 0 : json["MinimumHirePeriod"],
    isAfterHourPickUp: json["IsAfterHourPickUp"] == null ? void 0 : json["IsAfterHourPickUp"],
    agentCommissionRate: json["AgentCommissionRate"] == null ? void 0 : json["AgentCommissionRate"],
    agentPaymentType: json["AgentPaymentType"] == null ? void 0 : json["AgentPaymentType"]
  };
}
function FleetCategoryToJSON2(json) {
  return FleetCategoryToJSONTyped5(json, false);
}
function FleetCategoryToJSONTyped5(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    PriceDifference: value["priceDifference"],
    Name: value["name"],
    Description: value["description"],
    CategoryCode: value["categoryCode"],
    CampaignCode: value["campaignCode"],
    CampaignId: value["campaignId"],
    DealText: value["dealText"],
    FreeDays: value["freeDays"],
    Id: value["id"],
    Type: value["type"],
    TypeSlug: value["typeSlug"],
    Availability: value["availability"],
    DailyRate: FleetCategoryDailyRateToJSON(value["dailyRate"]),
    Total: FleetCategoryTotalToJSON(value["total"]),
    DiscountPercentageApplied: value["discountPercentageApplied"],
    MandatoryFees: value["mandatoryFees"] == null ? void 0 : value["mandatoryFees"].map(FleetCategoryMandatoryFeesToJSON),
    RentalDays: value["rentalDays"],
    MinimumHirePeriod: value["minimumHirePeriod"],
    IsAfterHourPickUp: value["isAfterHourPickUp"],
    AgentCommissionRate: value["agentCommissionRate"],
    AgentPaymentType: value["agentPaymentType"]
  };
}

// src/rentals-api/models/HirerDetailsHomeAddress.ts
function instanceOfHirerDetailsHomeAddress(value) {
  return true;
}
function HirerDetailsHomeAddressFromJSON(json) {
  return HirerDetailsHomeAddressFromJSONTyped(json, false);
}
function HirerDetailsHomeAddressFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    street: json["Street"] == null ? void 0 : json["Street"],
    state: json["State"] == null ? void 0 : json["State"],
    city: json["City"] == null ? void 0 : json["City"],
    postCode: json["PostCode"] == null ? void 0 : json["PostCode"],
    country: json["Country"] == null ? void 0 : json["Country"]
  };
}
function HirerDetailsHomeAddressToJSON(json) {
  return HirerDetailsHomeAddressToJSONTyped(json, false);
}
function HirerDetailsHomeAddressToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    Street: value["street"],
    State: value["state"],
    City: value["city"],
    PostCode: value["postCode"],
    Country: value["country"]
  };
}

// src/rentals-api/models/HirerDetails.ts
function instanceOfHirerDetails(value) {
  return true;
}
function HirerDetailsFromJSON2(json) {
  return HirerDetailsFromJSONTyped5(json, false);
}
function HirerDetailsFromJSONTyped5(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    firstName: json["FirstName"] == null ? void 0 : json["FirstName"],
    lastName: json["LastName"] == null ? void 0 : json["LastName"],
    email: json["Email"] == null ? void 0 : json["Email"],
    mobileNumber: json["MobileNumber"] == null ? void 0 : json["MobileNumber"],
    dateOfBirth: json["DateOfBirth"] == null ? void 0 : json["DateOfBirth"],
    driversLicenceExpiry: json["DriversLicenceExpiry"] == null ? void 0 : json["DriversLicenceExpiry"],
    driversLicenceCountry: json["DriversLicenceCountry"] == null ? void 0 : json["DriversLicenceCountry"],
    driversLicenceNumber: json["DriversLicenceNumber"] == null ? void 0 : json["DriversLicenceNumber"],
    lucyClubNumber: json["LucyClubNumber"] == null ? void 0 : json["LucyClubNumber"],
    arrivalFlightNumber: json["ArrivalFlightNumber"] == null ? void 0 : json["ArrivalFlightNumber"],
    departureFlightNumber: json["DepartureFlightNumber"] == null ? void 0 : json["DepartureFlightNumber"],
    optIn: json["OptIn"] == null ? void 0 : json["OptIn"],
    numberOfPeople: json["NumberOfPeople"] == null ? void 0 : json["NumberOfPeople"],
    homeAddress: json["HomeAddress"] == null ? void 0 : HirerDetailsHomeAddressFromJSON(json["HomeAddress"])
  };
}
function HirerDetailsToJSON2(json) {
  return HirerDetailsToJSONTyped4(json, false);
}
function HirerDetailsToJSONTyped4(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    FirstName: value["firstName"],
    LastName: value["lastName"],
    Email: value["email"],
    MobileNumber: value["mobileNumber"],
    DateOfBirth: value["dateOfBirth"],
    DriversLicenceExpiry: value["driversLicenceExpiry"],
    DriversLicenceCountry: value["driversLicenceCountry"],
    DriversLicenceNumber: value["driversLicenceNumber"],
    LucyClubNumber: value["lucyClubNumber"],
    ArrivalFlightNumber: value["arrivalFlightNumber"],
    DepartureFlightNumber: value["departureFlightNumber"],
    OptIn: value["optIn"],
    NumberOfPeople: value["numberOfPeople"],
    HomeAddress: HirerDetailsHomeAddressToJSON(value["homeAddress"])
  };
}

// src/rentals-api/models/ExcessType.ts
var ExcessType = {
  Basic: "Basic",
  Standard: "Standard",
  Full: "Full"
};
function instanceOfExcessType(value) {
  for (const key in ExcessType) {
    if (Object.prototype.hasOwnProperty.call(ExcessType, key)) {
      if (ExcessType[key] === value) {
        return true;
      }
    }
  }
  return false;
}
function ExcessTypeFromJSON(json) {
  return ExcessTypeFromJSONTyped(json, false);
}
function ExcessTypeFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}
function ExcessTypeToJSON(value) {
  return value;
}
function ExcessTypeToJSONTyped(value, ignoreDiscriminator) {
  return value;
}

// src/rentals-api/models/ProductType.ts
var ProductType = {
  Fixed: "Fixed",
  Daily: "Daily",
  Percentage: "Percentage"
};
function instanceOfProductType(value) {
  for (const key in ProductType) {
    if (Object.prototype.hasOwnProperty.call(ProductType, key)) {
      if (ProductType[key] === value) {
        return true;
      }
    }
  }
  return false;
}
function ProductTypeFromJSON(json) {
  return ProductTypeFromJSONTyped(json, false);
}
function ProductTypeFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}
function ProductTypeToJSON(value) {
  return value;
}
function ProductTypeToJSONTyped(value, ignoreDiscriminator) {
  return value;
}

// src/rentals-api/models/RentalAdditionalProductPrice.ts
function instanceOfRentalAdditionalProductPrice(value) {
  if (!("currencyCode" in value) || value["currencyCode"] === void 0) return false;
  if (!("value" in value) || value["value"] === void 0) return false;
  return true;
}
function RentalAdditionalProductPriceFromJSON(json) {
  return RentalAdditionalProductPriceFromJSONTyped(json, false);
}
function RentalAdditionalProductPriceFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    currencyCode: json["CurrencyCode"],
    value: json["Value"]
  };
}
function RentalAdditionalProductPriceToJSON(json) {
  return RentalAdditionalProductPriceToJSONTyped(json, false);
}
function RentalAdditionalProductPriceToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    CurrencyCode: value["currencyCode"],
    Value: value["value"]
  };
}

// src/rentals-api/models/RentalExcessReductionAllOfBond.ts
function instanceOfRentalExcessReductionAllOfBond(value) {
  if (!("currencyCode" in value) || value["currencyCode"] === void 0) return false;
  if (!("value" in value) || value["value"] === void 0) return false;
  return true;
}
function RentalExcessReductionAllOfBondFromJSON(json) {
  return RentalExcessReductionAllOfBondFromJSONTyped(json, false);
}
function RentalExcessReductionAllOfBondFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    currencyCode: json["CurrencyCode"],
    value: json["Value"]
  };
}
function RentalExcessReductionAllOfBondToJSON(json) {
  return RentalExcessReductionAllOfBondToJSONTyped(json, false);
}
function RentalExcessReductionAllOfBondToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    CurrencyCode: value["currencyCode"],
    Value: value["value"]
  };
}

// src/rentals-api/models/RentalExcessReductionAllOfExcess.ts
function instanceOfRentalExcessReductionAllOfExcess(value) {
  if (!("currencyCode" in value) || value["currencyCode"] === void 0) return false;
  if (!("value" in value) || value["value"] === void 0) return false;
  return true;
}
function RentalExcessReductionAllOfExcessFromJSON(json) {
  return RentalExcessReductionAllOfExcessFromJSONTyped(json, false);
}
function RentalExcessReductionAllOfExcessFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    currencyCode: json["CurrencyCode"],
    value: json["Value"]
  };
}
function RentalExcessReductionAllOfExcessToJSON(json) {
  return RentalExcessReductionAllOfExcessToJSONTyped(json, false);
}
function RentalExcessReductionAllOfExcessToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    CurrencyCode: value["currencyCode"],
    Value: value["value"]
  };
}

// src/rentals-api/models/RentalExcessReduction.ts
function instanceOfRentalExcessReduction(value) {
  if (!("excessType" in value) || value["excessType"] === void 0) return false;
  if (!("excess" in value) || value["excess"] === void 0) return false;
  if (!("bond" in value) || value["bond"] === void 0) return false;
  return true;
}
function RentalExcessReductionFromJSON(json) {
  return RentalExcessReductionFromJSONTyped(json, false);
}
function RentalExcessReductionFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    productId: json["ProductId"] == null ? void 0 : json["ProductId"],
    productCode: json["ProductCode"] == null ? void 0 : json["ProductCode"],
    name: json["Name"] == null ? void 0 : json["Name"],
    category: json["Category"] == null ? void 0 : json["Category"],
    isMandatory: json["IsMandatory"] == null ? void 0 : json["IsMandatory"],
    description: json["Description"] == null ? void 0 : json["Description"],
    price: json["Price"] == null ? void 0 : RentalAdditionalProductPriceFromJSON(json["Price"]),
    productType: json["ProductType"] == null ? void 0 : ProductTypeFromJSON(json["ProductType"]),
    allowMultiple: json["AllowMultiple"] == null ? void 0 : json["AllowMultiple"],
    excessType: ExcessTypeFromJSON(json["ExcessType"]),
    excess: RentalExcessReductionAllOfExcessFromJSON(json["Excess"]),
    bond: RentalExcessReductionAllOfBondFromJSON(json["Bond"])
  };
}
function RentalExcessReductionToJSON(json) {
  return RentalExcessReductionToJSONTyped(json, false);
}
function RentalExcessReductionToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    ProductId: value["productId"],
    ProductCode: value["productCode"],
    Name: value["name"],
    Category: value["category"],
    IsMandatory: value["isMandatory"],
    Description: value["description"],
    Price: RentalAdditionalProductPriceToJSON(value["price"]),
    ProductType: ProductTypeToJSON(value["productType"]),
    AllowMultiple: value["allowMultiple"],
    ExcessType: ExcessTypeToJSON(value["excessType"]),
    Excess: RentalExcessReductionAllOfExcessToJSON(value["excess"]),
    Bond: RentalExcessReductionAllOfBondToJSON(value["bond"])
  };
}

// src/rentals-api/models/InsuranceProducts.ts
function instanceOfInsuranceProducts(value) {
  return true;
}
function InsuranceProductsFromJSON(json) {
  return InsuranceProductsFromJSONTyped(json, false);
}
function InsuranceProductsFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    group: json["Group"] == null ? void 0 : json["Group"],
    webCategory: json["WebCategory"] == null ? void 0 : json["WebCategory"],
    required: json["Required"] == null ? void 0 : json["Required"],
    allowMultiple: json["AllowMultiple"] == null ? void 0 : json["AllowMultiple"],
    description: json["Description"] == null ? void 0 : json["Description"],
    items: json["Items"] == null ? void 0 : json["Items"].map(RentalExcessReductionFromJSON)
  };
}
function InsuranceProductsToJSON(json) {
  return InsuranceProductsToJSONTyped(json, false);
}
function InsuranceProductsToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    Group: value["group"],
    WebCategory: value["webCategory"],
    Required: value["required"],
    AllowMultiple: value["allowMultiple"],
    Description: value["description"],
    Items: value["items"] == null ? void 0 : value["items"].map(RentalExcessReductionToJSON)
  };
}

// src/rentals-api/models/QuoteAgentBalanceDue.ts
function instanceOfQuoteAgentBalanceDue(value) {
  if (!("currencyCode" in value) || value["currencyCode"] === void 0) return false;
  if (!("value" in value) || value["value"] === void 0) return false;
  return true;
}
function QuoteAgentBalanceDueFromJSON(json) {
  return QuoteAgentBalanceDueFromJSONTyped(json, false);
}
function QuoteAgentBalanceDueFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    currencyCode: json["CurrencyCode"],
    value: json["Value"]
  };
}
function QuoteAgentBalanceDueToJSON(json) {
  return QuoteAgentBalanceDueToJSONTyped(json, false);
}
function QuoteAgentBalanceDueToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    CurrencyCode: value["currencyCode"],
    Value: value["value"]
  };
}

// src/rentals-api/models/QuoteAgentCollects.ts
function instanceOfQuoteAgentCollects(value) {
  if (!("currencyCode" in value) || value["currencyCode"] === void 0) return false;
  if (!("value" in value) || value["value"] === void 0) return false;
  return true;
}
function QuoteAgentCollectsFromJSON(json) {
  return QuoteAgentCollectsFromJSONTyped(json, false);
}
function QuoteAgentCollectsFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    currencyCode: json["CurrencyCode"],
    value: json["Value"]
  };
}
function QuoteAgentCollectsToJSON(json) {
  return QuoteAgentCollectsToJSONTyped(json, false);
}
function QuoteAgentCollectsToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    CurrencyCode: value["currencyCode"],
    Value: value["value"]
  };
}

// src/rentals-api/models/QuoteAgentCommission.ts
function instanceOfQuoteAgentCommission(value) {
  if (!("currencyCode" in value) || value["currencyCode"] === void 0) return false;
  if (!("value" in value) || value["value"] === void 0) return false;
  return true;
}
function QuoteAgentCommissionFromJSON(json) {
  return QuoteAgentCommissionFromJSONTyped(json, false);
}
function QuoteAgentCommissionFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    currencyCode: json["CurrencyCode"],
    value: json["Value"]
  };
}
function QuoteAgentCommissionToJSON(json) {
  return QuoteAgentCommissionToJSONTyped(json, false);
}
function QuoteAgentCommissionToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    CurrencyCode: value["currencyCode"],
    Value: value["value"]
  };
}

// src/rentals-api/models/QuoteBalanceDue.ts
function instanceOfQuoteBalanceDue(value) {
  if (!("currencyCode" in value) || value["currencyCode"] === void 0) return false;
  if (!("value" in value) || value["value"] === void 0) return false;
  return true;
}
function QuoteBalanceDueFromJSON(json) {
  return QuoteBalanceDueFromJSONTyped(json, false);
}
function QuoteBalanceDueFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    currencyCode: json["CurrencyCode"],
    value: json["Value"]
  };
}
function QuoteBalanceDueToJSON(json) {
  return QuoteBalanceDueToJSONTyped(json, false);
}
function QuoteBalanceDueToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    CurrencyCode: value["currencyCode"],
    Value: value["value"]
  };
}

// src/rentals-api/models/QuoteBalanceDueWithCCFee.ts
function instanceOfQuoteBalanceDueWithCCFee(value) {
  if (!("currencyCode" in value) || value["currencyCode"] === void 0) return false;
  if (!("value" in value) || value["value"] === void 0) return false;
  return true;
}
function QuoteBalanceDueWithCCFeeFromJSON(json) {
  return QuoteBalanceDueWithCCFeeFromJSONTyped(json, false);
}
function QuoteBalanceDueWithCCFeeFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    currencyCode: json["CurrencyCode"],
    value: json["Value"]
  };
}
function QuoteBalanceDueWithCCFeeToJSON(json) {
  return QuoteBalanceDueWithCCFeeToJSONTyped(json, false);
}
function QuoteBalanceDueWithCCFeeToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    CurrencyCode: value["currencyCode"],
    Value: value["value"]
  };
}

// src/rentals-api/models/QuoteTotalPrice.ts
function instanceOfQuoteTotalPrice(value) {
  if (!("currencyCode" in value) || value["currencyCode"] === void 0) return false;
  if (!("value" in value) || value["value"] === void 0) return false;
  return true;
}
function QuoteTotalPriceFromJSON(json) {
  return QuoteTotalPriceFromJSONTyped(json, false);
}
function QuoteTotalPriceFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    currencyCode: json["CurrencyCode"],
    value: json["Value"]
  };
}
function QuoteTotalPriceToJSON(json) {
  return QuoteTotalPriceToJSONTyped(json, false);
}
function QuoteTotalPriceToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    CurrencyCode: value["currencyCode"],
    Value: value["value"]
  };
}

// src/rentals-api/models/V2Money.ts
function instanceOfV2Money(value) {
  if (!("currencyCode" in value) || value["currencyCode"] === void 0) return false;
  if (!("value" in value) || value["value"] === void 0) return false;
  return true;
}
function V2MoneyFromJSON(json) {
  return V2MoneyFromJSONTyped(json, false);
}
function V2MoneyFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    currencyCode: json["CurrencyCode"],
    value: json["Value"]
  };
}
function V2MoneyToJSON(json) {
  return V2MoneyToJSONTyped(json, false);
}
function V2MoneyToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    CurrencyCode: value["currencyCode"],
    Value: value["value"]
  };
}

// src/rentals-api/models/RentalLineItem.ts
function instanceOfRentalLineItem(value) {
  if (!("excessType" in value) || value["excessType"] === void 0) return false;
  if (!("excess" in value) || value["excess"] === void 0) return false;
  if (!("bond" in value) || value["bond"] === void 0) return false;
  if (!("isExcluded" in value) || value["isExcluded"] === void 0) return false;
  if (!("apiOrder" in value) || value["apiOrder"] === void 0) return false;
  return true;
}
function RentalLineItemFromJSON(json) {
  return RentalLineItemFromJSONTyped(json, false);
}
function RentalLineItemFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    productId: json["ProductId"] == null ? void 0 : json["ProductId"],
    productCode: json["ProductCode"] == null ? void 0 : json["ProductCode"],
    name: json["Name"] == null ? void 0 : json["Name"],
    category: json["Category"] == null ? void 0 : json["Category"],
    isMandatory: json["IsMandatory"] == null ? void 0 : json["IsMandatory"],
    description: json["Description"] == null ? void 0 : json["Description"],
    price: json["Price"] == null ? void 0 : RentalAdditionalProductPriceFromJSON(json["Price"]),
    productType: json["ProductType"] == null ? void 0 : ProductTypeFromJSON(json["ProductType"]),
    allowMultiple: json["AllowMultiple"] == null ? void 0 : json["AllowMultiple"],
    excessType: ExcessTypeFromJSON(json["ExcessType"]),
    excess: RentalExcessReductionAllOfExcessFromJSON(json["Excess"]),
    bond: RentalExcessReductionAllOfBondFromJSON(json["Bond"]),
    isExcluded: json["IsExcluded"],
    apiOrder: json["ApiOrder"],
    numberOfDaysCalculated: json["NumberOfDaysCalculated"] == null ? void 0 : json["NumberOfDaysCalculated"],
    qty: json["Qty"] == null ? void 0 : json["Qty"],
    rentalDays: json["RentalDays"] == null ? void 0 : json["RentalDays"],
    total: json["Total"] == null ? void 0 : V2MoneyFromJSON(json["Total"])
  };
}
function RentalLineItemToJSON(json) {
  return RentalLineItemToJSONTyped(json, false);
}
function RentalLineItemToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    ProductId: value["productId"],
    ProductCode: value["productCode"],
    Name: value["name"],
    Category: value["category"],
    IsMandatory: value["isMandatory"],
    Description: value["description"],
    Price: RentalAdditionalProductPriceToJSON(value["price"]),
    ProductType: ProductTypeToJSON(value["productType"]),
    AllowMultiple: value["allowMultiple"],
    ExcessType: ExcessTypeToJSON(value["excessType"]),
    Excess: RentalExcessReductionAllOfExcessToJSON(value["excess"]),
    Bond: RentalExcessReductionAllOfBondToJSON(value["bond"]),
    IsExcluded: value["isExcluded"],
    ApiOrder: value["apiOrder"],
    NumberOfDaysCalculated: value["numberOfDaysCalculated"],
    Qty: value["qty"],
    RentalDays: value["rentalDays"],
    Total: V2MoneyToJSON(value["total"])
  };
}

// src/rentals-api/models/RentalAdditionalProduct.ts
function instanceOfRentalAdditionalProduct(value) {
  return true;
}
function RentalAdditionalProductFromJSON(json) {
  return RentalAdditionalProductFromJSONTyped(json, false);
}
function RentalAdditionalProductFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    productId: json["ProductId"] == null ? void 0 : json["ProductId"],
    productCode: json["ProductCode"] == null ? void 0 : json["ProductCode"],
    name: json["Name"] == null ? void 0 : json["Name"],
    category: json["Category"] == null ? void 0 : json["Category"],
    isMandatory: json["IsMandatory"] == null ? void 0 : json["IsMandatory"],
    description: json["Description"] == null ? void 0 : json["Description"],
    price: json["Price"] == null ? void 0 : RentalAdditionalProductPriceFromJSON(json["Price"]),
    productType: json["ProductType"] == null ? void 0 : ProductTypeFromJSON(json["ProductType"]),
    allowMultiple: json["AllowMultiple"] == null ? void 0 : json["AllowMultiple"]
  };
}
function RentalAdditionalProductToJSON(json) {
  return RentalAdditionalProductToJSONTyped(json, false);
}
function RentalAdditionalProductToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    ProductId: value["productId"],
    ProductCode: value["productCode"],
    Name: value["name"],
    Category: value["category"],
    IsMandatory: value["isMandatory"],
    Description: value["description"],
    Price: RentalAdditionalProductPriceToJSON(value["price"]),
    ProductType: ProductTypeToJSON(value["productType"]),
    AllowMultiple: value["allowMultiple"]
  };
}

// src/rentals-api/models/SecondaryProducts.ts
function instanceOfSecondaryProducts(value) {
  return true;
}
function SecondaryProductsFromJSON(json) {
  return SecondaryProductsFromJSONTyped(json, false);
}
function SecondaryProductsFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    group: json["Group"] == null ? void 0 : json["Group"],
    webCategory: json["WebCategory"] == null ? void 0 : json["WebCategory"],
    required: json["Required"] == null ? void 0 : json["Required"],
    allowMultiple: json["AllowMultiple"] == null ? void 0 : json["AllowMultiple"],
    description: json["Description"] == null ? void 0 : json["Description"],
    items: json["Items"] == null ? void 0 : json["Items"].map(RentalAdditionalProductFromJSON)
  };
}
function SecondaryProductsToJSON(json) {
  return SecondaryProductsToJSONTyped(json, false);
}
function SecondaryProductsToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    Group: value["group"],
    WebCategory: value["webCategory"],
    Required: value["required"],
    AllowMultiple: value["allowMultiple"],
    Description: value["description"],
    Items: value["items"] == null ? void 0 : value["items"].map(RentalAdditionalProductToJSON)
  };
}

// src/rentals-api/models/QuoteWithProductOptions.ts
function instanceOfQuoteWithProductOptions(value) {
  return true;
}
function QuoteWithProductOptionsFromJSON(json) {
  return QuoteWithProductOptionsFromJSONTyped(json, false);
}
function QuoteWithProductOptionsFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    quoteId: json["QuoteId"] == null ? void 0 : json["QuoteId"],
    reservationId: json["ReservationId"] == null ? void 0 : json["ReservationId"],
    reservationReference: json["ReservationReference"] == null ? void 0 : json["ReservationReference"],
    onlineLogin: json["OnlineLogin"] == null ? void 0 : json["OnlineLogin"],
    quoteName: json["QuoteName"] == null ? void 0 : json["QuoteName"],
    bookingStatus: json["BookingStatus"] == null ? void 0 : json["BookingStatus"],
    status: json["Status"] == null ? void 0 : json["Status"],
    pickUpLocationId: json["PickUpLocationId"] == null ? void 0 : json["PickUpLocationId"],
    pickUpLocationCode: json["PickUpLocationCode"] == null ? void 0 : json["PickUpLocationCode"],
    dropOffLocationId: json["DropOffLocationId"] == null ? void 0 : json["DropOffLocationId"],
    dropOffLocationCode: json["DropOffLocationCode"] == null ? void 0 : json["DropOffLocationCode"],
    pickUpDate: json["PickUpDate"] == null ? void 0 : stringToDate2(json["PickUpDate"]),
    dropOffDate: json["DropOffDate"] == null ? void 0 : stringToDate2(json["DropOffDate"]),
    rentalDays: json["RentalDays"] == null ? void 0 : json["RentalDays"],
    fleetCategory: json["FleetCategory"] == null ? void 0 : FleetCategoryFromJSON2(json["FleetCategory"]),
    totalPrice: json["TotalPrice"] == null ? void 0 : QuoteTotalPriceFromJSON(json["TotalPrice"]),
    agentCommission: json["AgentCommission"] == null ? void 0 : QuoteAgentCommissionFromJSON(json["AgentCommission"]),
    agentCollects: json["AgentCollects"] == null ? void 0 : QuoteAgentCollectsFromJSON(json["AgentCollects"]),
    agentBalanceDue: json["AgentBalanceDue"] == null ? void 0 : QuoteAgentBalanceDueFromJSON(json["AgentBalanceDue"]),
    agentName: json["AgentName"] == null ? void 0 : json["AgentName"],
    balanceDue: json["BalanceDue"] == null ? void 0 : QuoteBalanceDueFromJSON(json["BalanceDue"]),
    balanceDueWithCCFee: json["BalanceDueWithCCFee"] == null ? void 0 : QuoteBalanceDueWithCCFeeFromJSON(json["BalanceDueWithCCFee"]),
    cCSurchargePercentage: json["CCSurchargePercentage"] == null ? void 0 : json["CCSurchargePercentage"],
    lines: json["Lines"] == null ? void 0 : json["Lines"].map(RentalLineItemFromJSON),
    hirerDetails: json["HirerDetails"] == null ? void 0 : HirerDetailsFromJSON2(json["HirerDetails"]),
    insuranceProducts: json["InsuranceProducts"] == null ? void 0 : json["InsuranceProducts"].map(InsuranceProductsFromJSON),
    secondaryProducts: json["SecondaryProducts"] == null ? void 0 : json["SecondaryProducts"].map(SecondaryProductsFromJSON)
  };
}
function QuoteWithProductOptionsToJSON(json) {
  return QuoteWithProductOptionsToJSONTyped(json, false);
}
function QuoteWithProductOptionsToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    QuoteId: value["quoteId"],
    ReservationId: value["reservationId"],
    ReservationReference: value["reservationReference"],
    OnlineLogin: value["onlineLogin"],
    QuoteName: value["quoteName"],
    BookingStatus: value["bookingStatus"],
    Status: value["status"],
    PickUpLocationId: value["pickUpLocationId"],
    PickUpLocationCode: value["pickUpLocationCode"],
    DropOffLocationId: value["dropOffLocationId"],
    DropOffLocationCode: value["dropOffLocationCode"],
    PickUpDate: value["pickUpDate"] == null ? void 0 : dateToString2(value["pickUpDate"]),
    DropOffDate: value["dropOffDate"] == null ? void 0 : dateToString2(value["dropOffDate"]),
    RentalDays: value["rentalDays"],
    FleetCategory: FleetCategoryToJSON2(value["fleetCategory"]),
    TotalPrice: QuoteTotalPriceToJSON(value["totalPrice"]),
    AgentCommission: QuoteAgentCommissionToJSON(value["agentCommission"]),
    AgentCollects: QuoteAgentCollectsToJSON(value["agentCollects"]),
    AgentBalanceDue: QuoteAgentBalanceDueToJSON(value["agentBalanceDue"]),
    AgentName: value["agentName"],
    BalanceDue: QuoteBalanceDueToJSON(value["balanceDue"]),
    BalanceDueWithCCFee: QuoteBalanceDueWithCCFeeToJSON(value["balanceDueWithCCFee"]),
    CCSurchargePercentage: value["cCSurchargePercentage"],
    Lines: value["lines"] == null ? void 0 : value["lines"].map(RentalLineItemToJSON),
    HirerDetails: HirerDetailsToJSON2(value["hirerDetails"]),
    InsuranceProducts: value["insuranceProducts"] == null ? void 0 : value["insuranceProducts"].map(InsuranceProductsToJSON),
    SecondaryProducts: value["secondaryProducts"] == null ? void 0 : value["secondaryProducts"].map(SecondaryProductsToJSON)
  };
}

// src/rentals-api/models/CreateQuoteResponse.ts
var CreateQuoteResponseResponseTypeEnum = {
  Success: "Success",
  Error: "Error",
  ValidationError: "Validation Error",
  UnexpectedError: "Unexpected Error"
};
function instanceOfCreateQuoteResponse(value) {
  return true;
}
function CreateQuoteResponseFromJSON(json) {
  return CreateQuoteResponseFromJSONTyped(json, false);
}
function CreateQuoteResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    responseType: json["ResponseType"] == null ? void 0 : json["ResponseType"],
    message: json["Message"] == null ? void 0 : json["Message"],
    data: json["Data"] == null ? void 0 : QuoteWithProductOptionsFromJSON(json["Data"])
  };
}
function CreateQuoteResponseToJSON(json) {
  return CreateQuoteResponseToJSONTyped(json, false);
}
function CreateQuoteResponseToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    ResponseType: value["responseType"],
    Message: value["message"],
    Data: QuoteWithProductOptionsToJSON(value["data"])
  };
}

// src/rentals-api/models/CustomerData.ts
function instanceOfCustomerData(value) {
  return true;
}
function CustomerDataFromJSON(json) {
  return CustomerDataFromJSONTyped(json, false);
}
function CustomerDataFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    id: json["id"] == null ? void 0 : json["id"],
    firstName: json["firstName"] == null ? void 0 : json["firstName"],
    lastName: json["lastName"] == null ? void 0 : json["lastName"],
    email: json["email"] == null ? void 0 : json["email"],
    mobilePhone: json["mobilePhone"] == null ? void 0 : json["mobilePhone"],
    optIn: json["optIn"] == null ? void 0 : json["optIn"],
    dateOfBirth: json["dateOfBirth"] == null ? void 0 : stringToDate2(json["dateOfBirth"]),
    lucyClubNumber: json["lucyClubNumber"] == null ? void 0 : json["lucyClubNumber"],
    driversLicense: json["driversLicense"] == null ? void 0 : CustomerDriversLicenseFromJSON(json["driversLicense"]),
    address: json["address"] == null ? void 0 : CustomerAddressFromJSON(json["address"])
  };
}
function CustomerDataToJSON(json) {
  return CustomerDataToJSONTyped(json, false);
}
function CustomerDataToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    id: value["id"],
    firstName: value["firstName"],
    lastName: value["lastName"],
    email: value["email"],
    mobilePhone: value["mobilePhone"],
    optIn: value["optIn"],
    dateOfBirth: value["dateOfBirth"] == null ? void 0 : dateToString2(value["dateOfBirth"])?.substring(0, 10),
    lucyClubNumber: value["lucyClubNumber"],
    driversLicense: CustomerDriversLicenseToJSON(value["driversLicense"]),
    address: CustomerAddressToJSON(value["address"])
  };
}

// src/rentals-api/models/DealActions.ts
function instanceOfDealActions(value) {
  return true;
}
function DealActionsFromJSON(json) {
  return DealActionsFromJSONTyped(json, false);
}
function DealActionsFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    bundledProductNames: json["bundledProductNames"] == null ? void 0 : json["bundledProductNames"],
    dailyRate: json["dailyRate"] == null ? void 0 : json["dailyRate"],
    dailyDiscountRate: json["dailyDiscountRate"] == null ? void 0 : json["dailyDiscountRate"],
    dailyDiscountPercent: json["dailyDiscountPercent"] == null ? void 0 : json["dailyDiscountPercent"],
    velocityMultiplier: json["velocityMultiplier"] == null ? void 0 : json["velocityMultiplier"],
    velocityBonus: json["velocityBonus"] == null ? void 0 : json["velocityBonus"]
  };
}
function DealActionsToJSON(json) {
  return DealActionsToJSONTyped(json, false);
}
function DealActionsToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    bundledProductNames: value["bundledProductNames"],
    dailyRate: value["dailyRate"],
    dailyDiscountRate: value["dailyDiscountRate"],
    dailyDiscountPercent: value["dailyDiscountPercent"],
    velocityMultiplier: value["velocityMultiplier"],
    velocityBonus: value["velocityBonus"]
  };
}

// src/rentals-api/models/DealConditionsBlackoutDates.ts
function instanceOfDealConditionsBlackoutDates(value) {
  return true;
}
function DealConditionsBlackoutDatesFromJSON(json) {
  return DealConditionsBlackoutDatesFromJSONTyped(json, false);
}
function DealConditionsBlackoutDatesFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    from: json["from"] == null ? void 0 : stringToDate2(json["from"]),
    to: json["to"] == null ? void 0 : stringToDate2(json["to"])
  };
}
function DealConditionsBlackoutDatesToJSON(json) {
  return DealConditionsBlackoutDatesToJSONTyped(json, false);
}
function DealConditionsBlackoutDatesToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    from: value["from"] == null ? void 0 : dateToString2(value["from"]),
    to: value["to"] == null ? void 0 : dateToString2(value["to"])
  };
}

// src/rentals-api/models/DealConditionsEndDateBetween.ts
function instanceOfDealConditionsEndDateBetween(value) {
  return true;
}
function DealConditionsEndDateBetweenFromJSON(json) {
  return DealConditionsEndDateBetweenFromJSONTyped(json, false);
}
function DealConditionsEndDateBetweenFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    from: json["from"] == null ? void 0 : stringToDate2(json["from"]),
    to: json["to"] == null ? void 0 : stringToDate2(json["to"])
  };
}
function DealConditionsEndDateBetweenToJSON(json) {
  return DealConditionsEndDateBetweenToJSONTyped(json, false);
}
function DealConditionsEndDateBetweenToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    from: value["from"] == null ? void 0 : dateToString2(value["from"]),
    to: value["to"] == null ? void 0 : dateToString2(value["to"])
  };
}

// src/rentals-api/models/DealConditionsStartDateBetween.ts
function instanceOfDealConditionsStartDateBetween(value) {
  return true;
}
function DealConditionsStartDateBetweenFromJSON(json) {
  return DealConditionsStartDateBetweenFromJSONTyped(json, false);
}
function DealConditionsStartDateBetweenFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    from: json["from"] == null ? void 0 : stringToDate2(json["from"]),
    to: json["to"] == null ? void 0 : stringToDate2(json["to"])
  };
}
function DealConditionsStartDateBetweenToJSON(json) {
  return DealConditionsStartDateBetweenToJSONTyped(json, false);
}
function DealConditionsStartDateBetweenToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    from: value["from"] == null ? void 0 : dateToString2(value["from"]),
    to: value["to"] == null ? void 0 : dateToString2(value["to"])
  };
}

// src/rentals-api/models/DealConditions.ts
function instanceOfDealConditions(value) {
  return true;
}
function DealConditionsFromJSON(json) {
  return DealConditionsFromJSONTyped(json, false);
}
function DealConditionsFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    productCodeOneOf: json["productCodeOneOf"] == null ? void 0 : json["productCodeOneOf"],
    maxHireDays: json["maxHireDays"] == null ? void 0 : json["maxHireDays"],
    minHireDays: json["minHireDays"] == null ? void 0 : json["minHireDays"],
    startDateBetween: json["startDateBetween"] == null ? void 0 : DealConditionsStartDateBetweenFromJSON(json["startDateBetween"]),
    endDateBetween: json["endDateBetween"] == null ? void 0 : DealConditionsEndDateBetweenFromJSON(json["endDateBetween"]),
    startDateOnOrAfter: json["startDateOnOrAfter"] == null ? void 0 : stringToDate2(json["startDateOnOrAfter"]),
    endDateOnOrBefore: json["endDateOnOrBefore"] == null ? void 0 : stringToDate2(json["endDateOnOrBefore"]),
    startLocationAt: json["startLocationAt"] == null ? void 0 : json["startLocationAt"],
    endLocationAt: json["endLocationAt"] == null ? void 0 : json["endLocationAt"],
    blackoutDates: json["blackoutDates"] == null ? void 0 : json["blackoutDates"].map(DealConditionsBlackoutDatesFromJSON)
  };
}
function DealConditionsToJSON(json) {
  return DealConditionsToJSONTyped(json, false);
}
function DealConditionsToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    productCodeOneOf: value["productCodeOneOf"],
    maxHireDays: value["maxHireDays"],
    minHireDays: value["minHireDays"],
    startDateBetween: DealConditionsStartDateBetweenToJSON(value["startDateBetween"]),
    endDateBetween: DealConditionsEndDateBetweenToJSON(value["endDateBetween"]),
    startDateOnOrAfter: value["startDateOnOrAfter"] == null ? void 0 : dateToString2(value["startDateOnOrAfter"]),
    endDateOnOrBefore: value["endDateOnOrBefore"] == null ? void 0 : dateToString2(value["endDateOnOrBefore"]),
    startLocationAt: value["startLocationAt"],
    endLocationAt: value["endLocationAt"],
    blackoutDates: value["blackoutDates"] == null ? void 0 : value["blackoutDates"].map(DealConditionsBlackoutDatesToJSON)
  };
}

// src/rentals-api/models/Deal.ts
var DealTypeEnum = {
  VehicleRelocation: "vehicle-relocation",
  Promotion: "promotion"
};
function instanceOfDeal(value) {
  return true;
}
function DealFromJSON(json) {
  return DealFromJSONTyped(json, false);
}
function DealFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    id: json["id"] == null ? void 0 : json["id"],
    code: json["code"] == null ? void 0 : json["code"],
    priority: json["priority"] == null ? void 0 : json["priority"],
    type: json["type"] == null ? void 0 : json["type"],
    badgeText: json["badgeText"] == null ? void 0 : json["badgeText"],
    title: json["title"] == null ? void 0 : json["title"],
    location: json["location"] == null ? void 0 : json["location"],
    summary: json["summary"] == null ? void 0 : json["summary"],
    availableCount: json["availableCount"] == null ? void 0 : json["availableCount"],
    tags: json["tags"] == null ? void 0 : json["tags"],
    region: json["region"] == null ? void 0 : json["region"],
    marketingUrl: json["marketingUrl"] == null ? void 0 : json["marketingUrl"],
    imgUrl: json["imgUrl"] == null ? void 0 : json["imgUrl"],
    validFrom: json["validFrom"] == null ? void 0 : stringToDate2(json["validFrom"]),
    validTo: json["validTo"] == null ? void 0 : stringToDate2(json["validTo"]),
    marketingStartDate: json["marketingStartDate"] == null ? void 0 : stringToDate2(json["marketingStartDate"]),
    marketingEndDate: json["marketingEndDate"] == null ? void 0 : stringToDate2(json["marketingEndDate"]),
    conditions: json["conditions"] == null ? void 0 : DealConditionsFromJSON(json["conditions"]),
    actions: json["actions"] == null ? void 0 : DealActionsFromJSON(json["actions"])
  };
}
function DealToJSON(json) {
  return DealToJSONTyped(json, false);
}
function DealToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    id: value["id"],
    code: value["code"],
    priority: value["priority"],
    type: value["type"],
    badgeText: value["badgeText"],
    title: value["title"],
    location: value["location"],
    summary: value["summary"],
    availableCount: value["availableCount"],
    tags: value["tags"],
    region: value["region"],
    marketingUrl: value["marketingUrl"],
    imgUrl: value["imgUrl"],
    validFrom: value["validFrom"] == null ? void 0 : dateToString2(value["validFrom"]),
    validTo: value["validTo"] == null ? void 0 : dateToString2(value["validTo"]),
    marketingStartDate: value["marketingStartDate"] == null ? void 0 : dateToString2(value["marketingStartDate"]),
    marketingEndDate: value["marketingEndDate"] == null ? void 0 : dateToString2(value["marketingEndDate"]),
    conditions: DealConditionsToJSON(value["conditions"]),
    actions: DealActionsToJSON(value["actions"])
  };
}

// src/rentals-api/models/DealSummary.ts
var DealSummaryTypeEnum = {
  VehicleRelocation: "vehicle-relocation",
  Promotion: "promotion"
};
function instanceOfDealSummary(value) {
  return true;
}
function DealSummaryFromJSON(json) {
  return DealSummaryFromJSONTyped(json, false);
}
function DealSummaryFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    id: json["id"] == null ? void 0 : json["id"],
    code: json["code"] == null ? void 0 : json["code"],
    priority: json["priority"] == null ? void 0 : json["priority"],
    type: json["type"] == null ? void 0 : json["type"],
    badgeText: json["badgeText"] == null ? void 0 : json["badgeText"],
    title: json["title"] == null ? void 0 : json["title"],
    location: json["location"] == null ? void 0 : json["location"],
    summary: json["summary"] == null ? void 0 : json["summary"],
    availableCount: json["availableCount"] == null ? void 0 : json["availableCount"],
    tags: json["tags"] == null ? void 0 : json["tags"],
    region: json["region"] == null ? void 0 : json["region"],
    marketingUrl: json["marketingUrl"] == null ? void 0 : json["marketingUrl"],
    imgUrl: json["imgUrl"] == null ? void 0 : json["imgUrl"],
    validFrom: json["validFrom"] == null ? void 0 : stringToDate2(json["validFrom"]),
    validTo: json["validTo"] == null ? void 0 : stringToDate2(json["validTo"]),
    marketingStartDate: json["marketingStartDate"] == null ? void 0 : stringToDate2(json["marketingStartDate"]),
    marketingEndDate: json["marketingEndDate"] == null ? void 0 : stringToDate2(json["marketingEndDate"])
  };
}
function DealSummaryToJSON(json) {
  return DealSummaryToJSONTyped(json, false);
}
function DealSummaryToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    id: value["id"],
    code: value["code"],
    priority: value["priority"],
    type: value["type"],
    badgeText: value["badgeText"],
    title: value["title"],
    location: value["location"],
    summary: value["summary"],
    availableCount: value["availableCount"],
    tags: value["tags"],
    region: value["region"],
    marketingUrl: value["marketingUrl"],
    imgUrl: value["imgUrl"],
    validFrom: value["validFrom"] == null ? void 0 : dateToString2(value["validFrom"]),
    validTo: value["validTo"] == null ? void 0 : dateToString2(value["validTo"]),
    marketingStartDate: value["marketingStartDate"] == null ? void 0 : dateToString2(value["marketingStartDate"]),
    marketingEndDate: value["marketingEndDate"] == null ? void 0 : dateToString2(value["marketingEndDate"])
  };
}

// src/rentals-api/models/DefaultTime.ts
function instanceOfDefaultTime(value) {
  return true;
}
function DefaultTimeFromJSON(json) {
  return DefaultTimeFromJSONTyped(json, false);
}
function DefaultTimeFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    pickUp: json["PickUp"] == null ? void 0 : json["PickUp"],
    dropOff: json["DropOff"] == null ? void 0 : json["DropOff"]
  };
}
function DefaultTimeToJSON(json) {
  return DefaultTimeToJSONTyped(json, false);
}
function DefaultTimeToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    PickUp: value["pickUp"],
    DropOff: value["dropOff"]
  };
}

// src/rentals-api/models/DriverDetails.ts
function instanceOfDriverDetails(value) {
  if (!("accountKey" in value) || value["accountKey"] === void 0) return false;
  if (!("registration" in value) || value["registration"] === void 0) return false;
  if (!("infringementUtcDate" in value) || value["infringementUtcDate"] === void 0) return false;
  return true;
}
function DriverDetailsFromJSON(json) {
  return DriverDetailsFromJSONTyped(json, false);
}
function DriverDetailsFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    accountKey: json["accountKey"],
    registration: json["Registration"],
    infringementUtcDate: stringToDate2(json["InfringementUtcDate"])
  };
}
function DriverDetailsToJSON(json) {
  return DriverDetailsToJSONTyped(json, false);
}
function DriverDetailsToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    accountKey: value["accountKey"],
    Registration: value["registration"],
    InfringementUtcDate: dateToString2(value["infringementUtcDate"])
  };
}

// src/rentals-api/models/DynamicPricingResponseInnerProduct.ts
function instanceOfDynamicPricingResponseInnerProduct(value) {
  return true;
}
function DynamicPricingResponseInnerProductFromJSON(json) {
  return DynamicPricingResponseInnerProductFromJSONTyped(json, false);
}
function DynamicPricingResponseInnerProductFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    id: json["id"] == null ? void 0 : json["id"],
    name: json["name"] == null ? void 0 : json["name"]
  };
}
function DynamicPricingResponseInnerProductToJSON(json) {
  return DynamicPricingResponseInnerProductToJSONTyped(json, false);
}
function DynamicPricingResponseInnerProductToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    id: value["id"],
    name: value["name"]
  };
}

// src/rentals-api/models/DynamicPricingResponseInner.ts
function instanceOfDynamicPricingResponseInner(value) {
  return true;
}
function DynamicPricingResponseInnerFromJSON(json) {
  return DynamicPricingResponseInnerFromJSONTyped(json, false);
}
function DynamicPricingResponseInnerFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    price: json["price"] == null ? void 0 : MoneyFromJSON(json["price"]),
    product: json["product"] == null ? void 0 : DynamicPricingResponseInnerProductFromJSON(json["product"])
  };
}
function DynamicPricingResponseInnerToJSON(json) {
  return DynamicPricingResponseInnerToJSONTyped(json, false);
}
function DynamicPricingResponseInnerToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    price: MoneyToJSON(value["price"]),
    product: DynamicPricingResponseInnerProductToJSON(value["product"])
  };
}

// src/rentals-api/models/ExceptionRequest.ts
function instanceOfExceptionRequest(value) {
  if (!("body" in value) || value["body"] === void 0) return false;
  return true;
}
function ExceptionRequestFromJSON(json) {
  return ExceptionRequestFromJSONTyped(json, false);
}
function ExceptionRequestFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    body: json["body"]
  };
}
function ExceptionRequestToJSON(json) {
  return ExceptionRequestToJSONTyped(json, false);
}
function ExceptionRequestToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    body: value["body"]
  };
}

// src/rentals-api/models/GetAllFleetTypesResponseLocationsAvailableAt.ts
function instanceOfGetAllFleetTypesResponseLocationsAvailableAt(value) {
  return true;
}
function GetAllFleetTypesResponseLocationsAvailableAtFromJSON(json) {
  return GetAllFleetTypesResponseLocationsAvailableAtFromJSONTyped(json, false);
}
function GetAllFleetTypesResponseLocationsAvailableAtFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    id: json["Id"] == null ? void 0 : json["Id"],
    name: json["Name"] == null ? void 0 : json["Name"],
    siteCode: json["SiteCode"] == null ? void 0 : json["SiteCode"],
    country: json["Country"] == null ? void 0 : json["Country"],
    businessUnit: json["BusinessUnit"] == null ? void 0 : json["BusinessUnit"]
  };
}
function GetAllFleetTypesResponseLocationsAvailableAtToJSON(json) {
  return GetAllFleetTypesResponseLocationsAvailableAtToJSONTyped(json, false);
}
function GetAllFleetTypesResponseLocationsAvailableAtToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    Id: value["id"],
    Name: value["name"],
    SiteCode: value["siteCode"],
    Country: value["country"],
    BusinessUnit: value["businessUnit"]
  };
}

// src/rentals-api/models/FleetType.ts
var FleetTypeHireDaysTypeEnum = {
  Calendar: "calendar",
  Hourly: "hourly"
};
function instanceOfFleetType(value) {
  return true;
}
function FleetTypeFromJSON(json) {
  return FleetTypeFromJSONTyped(json, false);
}
function FleetTypeFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    id: json["Id"] == null ? void 0 : json["Id"],
    name: json["Name"] == null ? void 0 : json["Name"],
    slug: json["Slug"] == null ? void 0 : json["Slug"],
    leadTimeHours: json["LeadTimeHours"] == null ? void 0 : json["LeadTimeHours"],
    depositPercent: json["DepositPercent"] == null ? void 0 : json["DepositPercent"],
    minHireDays: json["MinHireDays"] == null ? void 0 : json["MinHireDays"],
    minimumDriverAge: json["MinimumDriverAge"] == null ? void 0 : json["MinimumDriverAge"],
    hireDaysType: json["HireDaysType"] == null ? void 0 : json["HireDaysType"],
    locationsAvailableAt: json["LocationsAvailableAt"] == null ? void 0 : json["LocationsAvailableAt"].map(GetAllFleetTypesResponseLocationsAvailableAtFromJSON)
  };
}
function FleetTypeToJSON(json) {
  return FleetTypeToJSONTyped(json, false);
}
function FleetTypeToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    Id: value["id"],
    Name: value["name"],
    Slug: value["slug"],
    LeadTimeHours: value["leadTimeHours"],
    DepositPercent: value["depositPercent"],
    MinHireDays: value["minHireDays"],
    MinimumDriverAge: value["minimumDriverAge"],
    HireDaysType: value["hireDaysType"],
    LocationsAvailableAt: value["locationsAvailableAt"] == null ? void 0 : value["locationsAvailableAt"].map(GetAllFleetTypesResponseLocationsAvailableAtToJSON)
  };
}

// src/rentals-api/models/GeoLocation.ts
function instanceOfGeoLocation(value) {
  return true;
}
function GeoLocationFromJSON(json) {
  return GeoLocationFromJSONTyped(json, false);
}
function GeoLocationFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    lat: json["Lat"] == null ? void 0 : json["Lat"],
    lng: json["Lng"] == null ? void 0 : json["Lng"],
    zoom: json["zoom"] == null ? void 0 : json["zoom"]
  };
}
function GeoLocationToJSON(json) {
  return GeoLocationToJSONTyped(json, false);
}
function GeoLocationToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    Lat: value["lat"],
    Lng: value["lng"],
    zoom: value["zoom"]
  };
}

// src/rentals-api/models/GetAllFleetTypesResponse.ts
var GetAllFleetTypesResponseResponseTypeEnum = {
  Success: "Success",
  Error: "Error",
  ValidationError: "Validation Error",
  UnexpectedError: "Unexpected Error"
};
function instanceOfGetAllFleetTypesResponse(value) {
  return true;
}
function GetAllFleetTypesResponseFromJSON(json) {
  return GetAllFleetTypesResponseFromJSONTyped(json, false);
}
function GetAllFleetTypesResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    responseType: json["ResponseType"] == null ? void 0 : json["ResponseType"],
    message: json["Message"] == null ? void 0 : json["Message"],
    data: json["Data"] == null ? void 0 : json["Data"].map(FleetTypeFromJSON)
  };
}
function GetAllFleetTypesResponseToJSON(json) {
  return GetAllFleetTypesResponseToJSONTyped(json, false);
}
function GetAllFleetTypesResponseToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    ResponseType: value["responseType"],
    Message: value["message"],
    Data: value["data"] == null ? void 0 : value["data"].map(FleetTypeToJSON)
  };
}

// src/rentals-api/models/GetFleetTypeCategoriesResponseData.ts
function instanceOfGetFleetTypeCategoriesResponseData(value) {
  return true;
}
function GetFleetTypeCategoriesResponseDataFromJSON(json) {
  return GetFleetTypeCategoriesResponseDataFromJSONTyped(json, false);
}
function GetFleetTypeCategoriesResponseDataFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    categories: json["Categories"] == null ? void 0 : json["Categories"].map(FleetCategoryFromJSON2)
  };
}
function GetFleetTypeCategoriesResponseDataToJSON(json) {
  return GetFleetTypeCategoriesResponseDataToJSONTyped(json, false);
}
function GetFleetTypeCategoriesResponseDataToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    Categories: value["categories"] == null ? void 0 : value["categories"].map(FleetCategoryToJSON2)
  };
}

// src/rentals-api/models/GetFleetTypeCategoriesResponse.ts
var GetFleetTypeCategoriesResponseResponseTypeEnum = {
  Success: "Success",
  Error: "Error",
  ValidationError: "Validation Error",
  UnexpectedError: "Unexpected Error"
};
function instanceOfGetFleetTypeCategoriesResponse(value) {
  return true;
}
function GetFleetTypeCategoriesResponseFromJSON(json) {
  return GetFleetTypeCategoriesResponseFromJSONTyped(json, false);
}
function GetFleetTypeCategoriesResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    responseType: json["ResponseType"] == null ? void 0 : json["ResponseType"],
    message: json["Message"] == null ? void 0 : json["Message"],
    data: json["Data"] == null ? void 0 : json["Data"].map(GetFleetTypeCategoriesResponseDataFromJSON)
  };
}
function GetFleetTypeCategoriesResponseToJSON(json) {
  return GetFleetTypeCategoriesResponseToJSONTyped(json, false);
}
function GetFleetTypeCategoriesResponseToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    ResponseType: value["responseType"],
    Message: value["message"],
    Data: value["data"] == null ? void 0 : value["data"].map(GetFleetTypeCategoriesResponseDataToJSON)
  };
}

// src/rentals-api/models/SiteAddress1.ts
function instanceOfSiteAddress1(value) {
  return true;
}
function SiteAddress1FromJSON2(json) {
  return SiteAddress1FromJSONTyped3(json, false);
}
function SiteAddress1FromJSONTyped3(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    name: json["Name"] == null ? void 0 : json["Name"],
    line1: json["Line1"] == null ? void 0 : json["Line1"],
    line2: json["Line2"] == null ? void 0 : json["Line2"],
    line3: json["Line3"] == null ? void 0 : json["Line3"],
    latitude: json["Latitude"] == null ? void 0 : json["Latitude"],
    longitude: json["Longitude"] == null ? void 0 : json["Longitude"],
    state: json["State"] == null ? void 0 : json["State"],
    city: json["City"] == null ? void 0 : json["City"],
    postcode: json["Postcode"] == null ? void 0 : json["Postcode"],
    county: json["County"] == null ? void 0 : json["County"],
    country: json["Country"] == null ? void 0 : json["Country"]
  };
}
function SiteAddress1ToJSON2(json) {
  return SiteAddress1ToJSONTyped2(json, false);
}
function SiteAddress1ToJSONTyped2(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    Name: value["name"],
    Line1: value["line1"],
    Line2: value["line2"],
    Line3: value["line3"],
    Latitude: value["latitude"],
    Longitude: value["longitude"],
    State: value["state"],
    City: value["city"],
    Postcode: value["postcode"],
    County: value["county"],
    Country: value["country"]
  };
}

// src/rentals-api/models/SiteDefaultTimes.ts
function instanceOfSiteDefaultTimes(value) {
  return true;
}
function SiteDefaultTimesFromJSON2(json) {
  return SiteDefaultTimesFromJSONTyped3(json, false);
}
function SiteDefaultTimesFromJSONTyped3(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    monday: json["Monday"] == null ? void 0 : DefaultTimeFromJSON(json["Monday"]),
    tuesday: json["Tuesday"] == null ? void 0 : DefaultTimeFromJSON(json["Tuesday"]),
    wednesday: json["Wednesday"] == null ? void 0 : DefaultTimeFromJSON(json["Wednesday"]),
    thursday: json["Thursday"] == null ? void 0 : DefaultTimeFromJSON(json["Thursday"]),
    friday: json["Friday"] == null ? void 0 : DefaultTimeFromJSON(json["Friday"]),
    saturday: json["Saturday"] == null ? void 0 : DefaultTimeFromJSON(json["Saturday"]),
    sunday: json["Sunday"] == null ? void 0 : DefaultTimeFromJSON(json["Sunday"])
  };
}
function SiteDefaultTimesToJSON2(json) {
  return SiteDefaultTimesToJSONTyped2(json, false);
}
function SiteDefaultTimesToJSONTyped2(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    Monday: DefaultTimeToJSON(value["monday"]),
    Tuesday: DefaultTimeToJSON(value["tuesday"]),
    Wednesday: DefaultTimeToJSON(value["wednesday"]),
    Thursday: DefaultTimeToJSON(value["thursday"]),
    Friday: DefaultTimeToJSON(value["friday"]),
    Saturday: DefaultTimeToJSON(value["saturday"]),
    Sunday: DefaultTimeToJSON(value["sunday"])
  };
}

// src/rentals-api/models/SiteHolidays.ts
function instanceOfSiteHolidays(value) {
  return true;
}
function SiteHolidaysFromJSON(json) {
  return SiteHolidaysFromJSONTyped(json, false);
}
function SiteHolidaysFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    name: json["Name"] == null ? void 0 : json["Name"],
    startDateTime: json["StartDateTime"] == null ? void 0 : stringToDate2(json["StartDateTime"]),
    endDateTime: json["EndDateTime"] == null ? void 0 : stringToDate2(json["EndDateTime"]),
    dOW: json["DOW"] == null ? void 0 : json["DOW"]
  };
}
function SiteHolidaysToJSON(json) {
  return SiteHolidaysToJSONTyped(json, false);
}
function SiteHolidaysToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    Name: value["name"],
    StartDateTime: value["startDateTime"] == null ? void 0 : dateToString2(value["startDateTime"]),
    EndDateTime: value["endDateTime"] == null ? void 0 : dateToString2(value["endDateTime"]),
    DOW: value["dOW"]
  };
}

// src/rentals-api/models/OfficeTime.ts
function instanceOfOfficeTime(value) {
  return true;
}
function OfficeTimeFromJSON(json) {
  return OfficeTimeFromJSONTyped(json, false);
}
function OfficeTimeFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    open: json["Open"] == null ? void 0 : json["Open"],
    close: json["Close"] == null ? void 0 : json["Close"]
  };
}
function OfficeTimeToJSON(json) {
  return OfficeTimeToJSONTyped(json, false);
}
function OfficeTimeToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    Open: value["open"],
    Close: value["close"]
  };
}

// src/rentals-api/models/SiteServiceHours.ts
function instanceOfSiteServiceHours(value) {
  return true;
}
function SiteServiceHoursFromJSON(json) {
  return SiteServiceHoursFromJSONTyped(json, false);
}
function SiteServiceHoursFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    monday: json["Monday"] == null ? void 0 : OfficeTimeFromJSON(json["Monday"]),
    tuesday: json["Tuesday"] == null ? void 0 : OfficeTimeFromJSON(json["Tuesday"]),
    wednesday: json["Wednesday"] == null ? void 0 : OfficeTimeFromJSON(json["Wednesday"]),
    thursday: json["Thursday"] == null ? void 0 : OfficeTimeFromJSON(json["Thursday"]),
    friday: json["Friday"] == null ? void 0 : OfficeTimeFromJSON(json["Friday"]),
    saturday: json["Saturday"] == null ? void 0 : OfficeTimeFromJSON(json["Saturday"]),
    sunday: json["Sunday"] == null ? void 0 : OfficeTimeFromJSON(json["Sunday"])
  };
}
function SiteServiceHoursToJSON(json) {
  return SiteServiceHoursToJSONTyped(json, false);
}
function SiteServiceHoursToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    Monday: OfficeTimeToJSON(value["monday"]),
    Tuesday: OfficeTimeToJSON(value["tuesday"]),
    Wednesday: OfficeTimeToJSON(value["wednesday"]),
    Thursday: OfficeTimeToJSON(value["thursday"]),
    Friday: OfficeTimeToJSON(value["friday"]),
    Saturday: OfficeTimeToJSON(value["saturday"]),
    Sunday: OfficeTimeToJSON(value["sunday"])
  };
}

// src/rentals-api/models/SiteSiteSettings.ts
function instanceOfSiteSiteSettings(value) {
  return true;
}
function SiteSiteSettingsFromJSON(json) {
  return SiteSiteSettingsFromJSONTyped(json, false);
}
function SiteSiteSettingsFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    fleetTypeId: json["FleetTypeId"] == null ? void 0 : json["FleetTypeId"],
    fleetTypeName: json["FleetTypeName"] == null ? void 0 : json["FleetTypeName"],
    serviceHours: json["ServiceHours"] == null ? void 0 : SiteServiceHoursFromJSON(json["ServiceHours"]),
    isAfterHoursPickUpAvailable: json["IsAfterHoursPickUpAvailable"] == null ? void 0 : json["IsAfterHoursPickUpAvailable"],
    isAfterHoursDropOffAvailable: json["IsAfterHoursDropOffAvailable"] == null ? void 0 : json["IsAfterHoursDropOffAvailable"]
  };
}
function SiteSiteSettingsToJSON(json) {
  return SiteSiteSettingsToJSONTyped(json, false);
}
function SiteSiteSettingsToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    FleetTypeId: value["fleetTypeId"],
    FleetTypeName: value["fleetTypeName"],
    ServiceHours: SiteServiceHoursToJSON(value["serviceHours"]),
    IsAfterHoursPickUpAvailable: value["isAfterHoursPickUpAvailable"],
    IsAfterHoursDropOffAvailable: value["isAfterHoursDropOffAvailable"]
  };
}

// src/rentals-api/models/Site.ts
function instanceOfSite(value) {
  return true;
}
function SiteFromJSON2(json) {
  return SiteFromJSONTyped2(json, false);
}
function SiteFromJSONTyped2(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    geoLocation: json["GeoLocation"] == null ? void 0 : GeoLocationFromJSON(json["GeoLocation"]),
    domesticPickUp: json["DomesticPickUp"] == null ? void 0 : json["DomesticPickUp"],
    internationalPickUp: json["InternationalPickUp"] == null ? void 0 : json["InternationalPickUp"],
    directionsLink: json["DirectionsLink"] == null ? void 0 : json["DirectionsLink"],
    address1: json["Address1"] == null ? void 0 : SiteAddress1FromJSON2(json["Address1"]),
    id: json["Id"] == null ? void 0 : json["Id"],
    name: json["Name"] == null ? void 0 : json["Name"],
    siteCode: json["SiteCode"] == null ? void 0 : json["SiteCode"],
    timeZone: json["TimeZone"] == null ? void 0 : json["TimeZone"],
    country: json["Country"] == null ? void 0 : json["Country"],
    countryCode: json["CountryCode"] == null ? void 0 : json["CountryCode"],
    minAges: json["MinAges"] == null ? void 0 : json["MinAges"],
    siteSettings: json["SiteSettings"] == null ? void 0 : json["SiteSettings"].map(SiteSiteSettingsFromJSON),
    holidays: json["Holidays"] == null ? void 0 : json["Holidays"].map(SiteHolidaysFromJSON),
    defaultTimes: json["DefaultTimes"] == null ? void 0 : SiteDefaultTimesFromJSON2(json["DefaultTimes"]),
    businessUnit: json["BusinessUnit"] == null ? void 0 : json["BusinessUnit"],
    phone: json["Phone"] == null ? void 0 : json["Phone"],
    reservationsPhone: json["ReservationsPhone"] == null ? void 0 : json["ReservationsPhone"]
  };
}
function SiteToJSON2(json) {
  return SiteToJSONTyped(json, false);
}
function SiteToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    GeoLocation: GeoLocationToJSON(value["geoLocation"]),
    DomesticPickUp: value["domesticPickUp"],
    InternationalPickUp: value["internationalPickUp"],
    DirectionsLink: value["directionsLink"],
    Address1: SiteAddress1ToJSON2(value["address1"]),
    Id: value["id"],
    Name: value["name"],
    SiteCode: value["siteCode"],
    TimeZone: value["timeZone"],
    Country: value["country"],
    CountryCode: value["countryCode"],
    MinAges: value["minAges"],
    SiteSettings: value["siteSettings"] == null ? void 0 : value["siteSettings"].map(SiteSiteSettingsToJSON),
    Holidays: value["holidays"] == null ? void 0 : value["holidays"].map(SiteHolidaysToJSON),
    DefaultTimes: SiteDefaultTimesToJSON2(value["defaultTimes"]),
    BusinessUnit: value["businessUnit"],
    Phone: value["phone"],
    ReservationsPhone: value["reservationsPhone"]
  };
}

// src/rentals-api/models/GetSitesResponse.ts
var GetSitesResponseResponseTypeEnum = {
  Success: "Success",
  Error: "Error",
  ValidationError: "Validation Error",
  UnexpectedError: "Unexpected Error"
};
function instanceOfGetSitesResponse(value) {
  return true;
}
function GetSitesResponseFromJSON(json) {
  return GetSitesResponseFromJSONTyped(json, false);
}
function GetSitesResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    responseType: json["ResponseType"] == null ? void 0 : json["ResponseType"],
    message: json["Message"] == null ? void 0 : json["Message"],
    data: json["Data"] == null ? void 0 : json["Data"].map(SiteFromJSON2)
  };
}
function GetSitesResponseToJSON(json) {
  return GetSitesResponseToJSONTyped(json, false);
}
function GetSitesResponseToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    ResponseType: value["responseType"],
    Message: value["message"],
    Data: value["data"] == null ? void 0 : value["data"].map(SiteToJSON2)
  };
}

// src/rentals-api/models/HirePeriodSettings.ts
function instanceOfHirePeriodSettings(value) {
  return true;
}
function HirePeriodSettingsFromJSON(json) {
  return HirePeriodSettingsFromJSONTyped(json, false);
}
function HirePeriodSettingsFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    fleetTypeSlug: json["fleetTypeSlug"] == null ? void 0 : json["fleetTypeSlug"],
    pickUpSiteCode: json["pickUpSiteCode"] == null ? void 0 : json["pickUpSiteCode"],
    dropOffSiteCode: json["dropOffSiteCode"] == null ? void 0 : json["dropOffSiteCode"],
    startDate: json["startDate"] == null ? void 0 : stringToDate2(json["startDate"]),
    endDate: json["endDate"] == null ? void 0 : stringToDate2(json["endDate"]),
    minHireDays: json["minHireDays"] == null ? void 0 : json["minHireDays"]
  };
}
function HirePeriodSettingsToJSON(json) {
  return HirePeriodSettingsToJSONTyped(json, false);
}
function HirePeriodSettingsToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    fleetTypeSlug: value["fleetTypeSlug"],
    pickUpSiteCode: value["pickUpSiteCode"],
    dropOffSiteCode: value["dropOffSiteCode"],
    startDate: value["startDate"] == null ? void 0 : dateToString2(value["startDate"]),
    endDate: value["endDate"] == null ? void 0 : dateToString2(value["endDate"]),
    minHireDays: value["minHireDays"]
  };
}

// src/rentals-api/models/IdConfirmBody.ts
function instanceOfIdConfirmBody(value) {
  if (!("firstName" in value) || value["firstName"] === void 0) return false;
  if (!("lastName" in value) || value["lastName"] === void 0) return false;
  if (!("email" in value) || value["email"] === void 0) return false;
  if (!("mobilePhone" in value) || value["mobilePhone"] === void 0) return false;
  return true;
}
function IdConfirmBodyFromJSON(json) {
  return IdConfirmBodyFromJSONTyped(json, false);
}
function IdConfirmBodyFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    firstName: json["firstName"],
    lastName: json["lastName"],
    email: json["email"],
    mobilePhone: json["mobilePhone"],
    country: json["country"] == null ? void 0 : json["country"],
    dateOfBirth: json["dateOfBirth"] == null ? void 0 : json["dateOfBirth"],
    licenseNumber: json["licenseNumber"] == null ? void 0 : json["licenseNumber"],
    licenseCountry: json["licenseCountry"] == null ? void 0 : json["licenseCountry"],
    licenseExpiry: json["licenseExpiry"] == null ? void 0 : json["licenseExpiry"],
    numberOfPeople: json["numberOfPeople"] == null ? void 0 : json["numberOfPeople"],
    arrivalFlight: json["arrivalFlight"] == null ? void 0 : json["arrivalFlight"],
    deposit: json["deposit"] == null ? void 0 : json["deposit"],
    marketingAllowed: json["marketingAllowed"] == null ? void 0 : json["marketingAllowed"]
  };
}
function IdConfirmBodyToJSON(json) {
  return IdConfirmBodyToJSONTyped(json, false);
}
function IdConfirmBodyToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    firstName: value["firstName"],
    lastName: value["lastName"],
    email: value["email"],
    mobilePhone: value["mobilePhone"],
    country: value["country"],
    dateOfBirth: value["dateOfBirth"],
    licenseNumber: value["licenseNumber"],
    licenseCountry: value["licenseCountry"],
    licenseExpiry: value["licenseExpiry"],
    numberOfPeople: value["numberOfPeople"],
    arrivalFlight: value["arrivalFlight"],
    deposit: value["deposit"],
    marketingAllowed: value["marketingAllowed"]
  };
}

// src/rentals-api/models/InfringementNote.ts
function instanceOfInfringementNote(value) {
  if (!("accountKey" in value) || value["accountKey"] === void 0) return false;
  if (!("reservationNumber" in value) || value["reservationNumber"] === void 0) return false;
  if (!("noticeIssuer" in value) || value["noticeIssuer"] === void 0) return false;
  if (!("noticeNumber" in value) || value["noticeNumber"] === void 0) return false;
  if (!("declatationDispatchDate" in value) || value["declatationDispatchDate"] === void 0) return false;
  if (!("noticeDateTime" in value) || value["noticeDateTime"] === void 0) return false;
  return true;
}
function InfringementNoteFromJSON(json) {
  return InfringementNoteFromJSONTyped(json, false);
}
function InfringementNoteFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    accountKey: json["accountKey"],
    reservationNumber: json["reservationNumber"],
    noticeIssuer: json["noticeIssuer"],
    noticeNumber: json["noticeNumber"],
    declatationDispatchDate: stringToDate2(json["declatationDispatchDate"]),
    noticeDateTime: stringToDate2(json["noticeDateTime"]),
    noticeUrl: json["noticeUrl"] == null ? void 0 : json["noticeUrl"],
    statuatoryDeclarationUrl: json["statuatoryDeclarationUrl"] == null ? void 0 : json["statuatoryDeclarationUrl"]
  };
}
function InfringementNoteToJSON(json) {
  return InfringementNoteToJSONTyped(json, false);
}
function InfringementNoteToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    accountKey: value["accountKey"],
    reservationNumber: value["reservationNumber"],
    noticeIssuer: value["noticeIssuer"],
    noticeNumber: value["noticeNumber"],
    declatationDispatchDate: dateToString2(value["declatationDispatchDate"]),
    noticeDateTime: dateToString2(value["noticeDateTime"]),
    noticeUrl: value["noticeUrl"],
    statuatoryDeclarationUrl: value["statuatoryDeclarationUrl"]
  };
}

// src/rentals-api/models/License.ts
function instanceOfLicense(value) {
  return true;
}
function LicenseFromJSON(json) {
  return LicenseFromJSONTyped(json, false);
}
function LicenseFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    licenseNumber: json["LicenseNumber"] == null ? void 0 : json["LicenseNumber"],
    licenseCountry: json["LicenseCountry"] == null ? void 0 : json["LicenseCountry"],
    licenseExpiry: json["LicenseExpiry"] == null ? void 0 : stringToDate2(json["LicenseExpiry"])
  };
}
function LicenseToJSON(json) {
  return LicenseToJSONTyped(json, false);
}
function LicenseToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    LicenseNumber: value["licenseNumber"],
    LicenseCountry: value["licenseCountry"],
    LicenseExpiry: value["licenseExpiry"] == null ? void 0 : dateToString2(value["licenseExpiry"])
  };
}

// src/rentals-api/models/ListDealsResponse.ts
function instanceOfListDealsResponse(value) {
  return true;
}
function ListDealsResponseFromJSON(json) {
  return ListDealsResponseFromJSONTyped(json, false);
}
function ListDealsResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    items: json["items"] == null ? void 0 : json["items"].map(DealFromJSON),
    itemCount: json["itemCount"] == null ? void 0 : json["itemCount"],
    pageIndex: json["pageIndex"] == null ? void 0 : json["pageIndex"],
    pageCount: json["pageCount"] == null ? void 0 : json["pageCount"],
    pageSize: json["pageSize"] == null ? void 0 : json["pageSize"]
  };
}
function ListDealsResponseToJSON(json) {
  return ListDealsResponseToJSONTyped(json, false);
}
function ListDealsResponseToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    items: value["items"] == null ? void 0 : value["items"].map(DealToJSON),
    itemCount: value["itemCount"],
    pageIndex: value["pageIndex"],
    pageCount: value["pageCount"],
    pageSize: value["pageSize"]
  };
}

// src/rentals-api/models/ReservationSummaryHirer.ts
function instanceOfReservationSummaryHirer(value) {
  return true;
}
function ReservationSummaryHirerFromJSON(json) {
  return ReservationSummaryHirerFromJSONTyped(json, false);
}
function ReservationSummaryHirerFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    firstName: json["firstName"] == null ? void 0 : json["firstName"],
    lastName: json["lastName"] == null ? void 0 : json["lastName"],
    checkedIn: json["checkedIn"] == null ? void 0 : json["checkedIn"]
  };
}
function ReservationSummaryHirerToJSON(json) {
  return ReservationSummaryHirerToJSONTyped(json, false);
}
function ReservationSummaryHirerToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    firstName: value["firstName"],
    lastName: value["lastName"],
    checkedIn: value["checkedIn"]
  };
}

// src/rentals-api/models/ReservationSummary.ts
var ReservationSummaryStatusEnum = {
  Quote: "quote",
  Confirmed: "confirmed",
  Cancelled: "cancelled"
};
var ReservationSummaryAvailabilityEnum = {
  Freesell: "freesell",
  Onrequest: "onrequest",
  Stopsell: "stopsell"
};
function instanceOfReservationSummary(value) {
  if (!("id" in value) || value["id"] === void 0) return false;
  if (!("bookingNo" in value) || value["bookingNo"] === void 0) return false;
  if (!("ref" in value) || value["ref"] === void 0) return false;
  if (!("created" in value) || value["created"] === void 0) return false;
  if (!("updated" in value) || value["updated"] === void 0) return false;
  if (!("status" in value) || value["status"] === void 0) return false;
  if (!("availability" in value) || value["availability"] === void 0) return false;
  if (!("pickUpDate" in value) || value["pickUpDate"] === void 0) return false;
  if (!("dropOffDate" in value) || value["dropOffDate"] === void 0) return false;
  if (!("pickUpSiteCode" in value) || value["pickUpSiteCode"] === void 0) return false;
  if (!("dropOffSiteCode" in value) || value["dropOffSiteCode"] === void 0) return false;
  return true;
}
function ReservationSummaryFromJSON(json) {
  return ReservationSummaryFromJSONTyped(json, false);
}
function ReservationSummaryFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    id: json["id"],
    bookingNo: json["bookingNo"],
    ref: json["ref"],
    created: stringToDate2(json["created"]),
    updated: stringToDate2(json["updated"]),
    status: json["status"],
    voucherRef: json["voucherRef"] == null ? void 0 : json["voucherRef"],
    availability: json["availability"],
    pickUpDate: stringToDate2(json["pickUpDate"]),
    dropOffDate: stringToDate2(json["dropOffDate"]),
    pickUpSiteCode: json["pickUpSiteCode"],
    dropOffSiteCode: json["dropOffSiteCode"],
    hirer: json["hirer"] == null ? void 0 : ReservationSummaryHirerFromJSON(json["hirer"])
  };
}
function ReservationSummaryToJSON(json) {
  return ReservationSummaryToJSONTyped(json, false);
}
function ReservationSummaryToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    id: value["id"],
    bookingNo: value["bookingNo"],
    ref: value["ref"],
    created: dateToString2(value["created"]),
    updated: dateToString2(value["updated"]),
    status: value["status"],
    voucherRef: value["voucherRef"],
    availability: value["availability"],
    pickUpDate: dateToString2(value["pickUpDate"]),
    dropOffDate: dateToString2(value["dropOffDate"]),
    pickUpSiteCode: value["pickUpSiteCode"],
    dropOffSiteCode: value["dropOffSiteCode"],
    hirer: ReservationSummaryHirerToJSON(value["hirer"])
  };
}

// src/rentals-api/models/ListReservationsResponse.ts
function instanceOfListReservationsResponse(value) {
  return true;
}
function ListReservationsResponseFromJSON(json) {
  return ListReservationsResponseFromJSONTyped(json, false);
}
function ListReservationsResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    items: json["items"] == null ? void 0 : json["items"].map(ReservationSummaryFromJSON),
    itemCount: json["itemCount"] == null ? void 0 : json["itemCount"],
    page: json["page"] == null ? void 0 : json["page"],
    pageCount: json["pageCount"] == null ? void 0 : json["pageCount"],
    itemsPerPage: json["itemsPerPage"] == null ? void 0 : json["itemsPerPage"]
  };
}
function ListReservationsResponseToJSON(json) {
  return ListReservationsResponseToJSONTyped(json, false);
}
function ListReservationsResponseToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    items: value["items"] == null ? void 0 : value["items"].map(ReservationSummaryToJSON),
    itemCount: value["itemCount"],
    page: value["page"],
    pageCount: value["pageCount"],
    itemsPerPage: value["itemsPerPage"]
  };
}

// src/rentals-api/models/LiteAddress.ts
function instanceOfLiteAddress(value) {
  return true;
}
function LiteAddressFromJSON(json) {
  return LiteAddressFromJSONTyped(json, false);
}
function LiteAddressFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    address: json["Address"] == null ? void 0 : json["Address"],
    state: json["State"] == null ? void 0 : json["State"],
    city: json["City"] == null ? void 0 : json["City"],
    postcode: json["Postcode"] == null ? void 0 : json["Postcode"],
    country: json["Country"] == null ? void 0 : json["Country"]
  };
}
function LiteAddressToJSON(json) {
  return LiteAddressToJSONTyped(json, false);
}
function LiteAddressToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    Address: value["address"],
    State: value["state"],
    City: value["city"],
    Postcode: value["postcode"],
    Country: value["country"]
  };
}

// src/rentals-api/models/LiteAdditionalDriver.ts
function instanceOfLiteAdditionalDriver(value) {
  return true;
}
function LiteAdditionalDriverFromJSON(json) {
  return LiteAdditionalDriverFromJSONTyped(json, false);
}
function LiteAdditionalDriverFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    additionalDriverId: json["AdditionalDriverId"] == null ? void 0 : json["AdditionalDriverId"],
    isDelete: json["IsDelete"] == null ? void 0 : json["IsDelete"],
    firstName: json["FirstName"] == null ? void 0 : json["FirstName"],
    lastName: json["LastName"] == null ? void 0 : json["LastName"],
    dateOfBirth: json["DateOfBirth"] == null ? void 0 : stringToDate2(json["DateOfBirth"]),
    license: json["License"] == null ? void 0 : LicenseFromJSON(json["License"]),
    address: json["Address"] == null ? void 0 : LiteAddressFromJSON(json["Address"]),
    email: json["Email"] == null ? void 0 : json["Email"],
    mobilePhone: json["MobilePhone"] == null ? void 0 : json["MobilePhone"],
    isMarketingAllowed: json["IsMarketingAllowed"] == null ? void 0 : json["IsMarketingAllowed"]
  };
}
function LiteAdditionalDriverToJSON(json) {
  return LiteAdditionalDriverToJSONTyped(json, false);
}
function LiteAdditionalDriverToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    AdditionalDriverId: value["additionalDriverId"],
    IsDelete: value["isDelete"],
    FirstName: value["firstName"],
    LastName: value["lastName"],
    DateOfBirth: value["dateOfBirth"] == null ? void 0 : dateToString2(value["dateOfBirth"]),
    License: LicenseToJSON(value["license"]),
    Address: LiteAddressToJSON(value["address"]),
    Email: value["email"],
    MobilePhone: value["mobilePhone"],
    IsMarketingAllowed: value["isMarketingAllowed"]
  };
}

// src/rentals-api/models/LiteContact.ts
function instanceOfLiteContact(value) {
  return true;
}
function LiteContactFromJSON(json) {
  return LiteContactFromJSONTyped(json, false);
}
function LiteContactFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    firstName: json["FirstName"] == null ? void 0 : json["FirstName"],
    lastName: json["LastName"] == null ? void 0 : json["LastName"],
    dateOfBirth: json["DateOfBirth"] == null ? void 0 : stringToDate2(json["DateOfBirth"]),
    license: json["License"] == null ? void 0 : LicenseFromJSON(json["License"]),
    address: json["Address"] == null ? void 0 : LiteAddressFromJSON(json["Address"]),
    email: json["Email"] == null ? void 0 : json["Email"],
    mobilePhone: json["MobilePhone"] == null ? void 0 : json["MobilePhone"],
    isMarketingAllowed: json["IsMarketingAllowed"] == null ? void 0 : json["IsMarketingAllowed"]
  };
}
function LiteContactToJSON(json) {
  return LiteContactToJSONTyped(json, false);
}
function LiteContactToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    FirstName: value["firstName"],
    LastName: value["lastName"],
    DateOfBirth: value["dateOfBirth"] == null ? void 0 : dateToString2(value["dateOfBirth"]),
    License: LicenseToJSON(value["license"]),
    Address: LiteAddressToJSON(value["address"]),
    Email: value["email"],
    MobilePhone: value["mobilePhone"],
    IsMarketingAllowed: value["isMarketingAllowed"]
  };
}

// src/rentals-api/models/PagedResponse.ts
function instanceOfPagedResponse(value) {
  return true;
}
function PagedResponseFromJSON(json) {
  return PagedResponseFromJSONTyped(json, false);
}
function PagedResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    items: json["items"] == null ? void 0 : json["items"],
    itemCount: json["itemCount"] == null ? void 0 : json["itemCount"],
    pageIndex: json["pageIndex"] == null ? void 0 : json["pageIndex"],
    pageCount: json["pageCount"] == null ? void 0 : json["pageCount"],
    pageSize: json["pageSize"] == null ? void 0 : json["pageSize"]
  };
}
function PagedResponseToJSON(json) {
  return PagedResponseToJSONTyped(json, false);
}
function PagedResponseToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    items: value["items"],
    itemCount: value["itemCount"],
    pageIndex: value["pageIndex"],
    pageCount: value["pageCount"],
    pageSize: value["pageSize"]
  };
}

// src/rentals-api/models/Payment.ts
function instanceOfPayment(value) {
  if (!("amount" in value) || value["amount"] === void 0) return false;
  if (!("date" in value) || value["date"] === void 0) return false;
  if (!("type" in value) || value["type"] === void 0) return false;
  return true;
}
function PaymentFromJSON(json) {
  return PaymentFromJSONTyped(json, false);
}
function PaymentFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    amount: MoneyFromJSON(json["amount"]),
    date: stringToDate2(json["date"]),
    type: json["type"],
    merchantFeeName: json["merchantFeeName"] == null ? void 0 : json["merchantFeeName"],
    merchantFeeAmount: json["merchantFeeAmount"] == null ? void 0 : MoneyFromJSON(json["merchantFeeAmount"]),
    total: json["total"] == null ? void 0 : MoneyFromJSON(json["total"])
  };
}
function PaymentToJSON(json) {
  return PaymentToJSONTyped(json, false);
}
function PaymentToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    amount: MoneyToJSON(value["amount"]),
    date: dateToString2(value["date"])?.substring(0, 10),
    type: value["type"],
    merchantFeeName: value["merchantFeeName"],
    merchantFeeAmount: MoneyToJSON(value["merchantFeeAmount"]),
    total: MoneyToJSON(value["total"])
  };
}

// src/rentals-api/models/PricingResponseProductPrices.ts
var PricingResponseProductPricesTypeEnum = {
  Fixed: "fixed",
  Daily: "daily",
  Percentage: "percentage"
};
function instanceOfPricingResponseProductPrices(value) {
  return true;
}
function PricingResponseProductPricesFromJSON(json) {
  return PricingResponseProductPricesFromJSONTyped(json, false);
}
function PricingResponseProductPricesFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    id: json["id"] == null ? void 0 : json["id"],
    code: json["code"] == null ? void 0 : json["code"],
    name: json["name"] == null ? void 0 : json["name"],
    category: json["category"] == null ? void 0 : json["category"],
    mandatory: json["mandatory"] == null ? void 0 : json["mandatory"],
    description: json["description"] == null ? void 0 : json["description"],
    price: json["price"] == null ? void 0 : V2MoneyFromJSON(json["price"]),
    type: json["type"] == null ? void 0 : json["type"],
    allowMultiple: json["allowMultiple"] == null ? void 0 : json["allowMultiple"],
    rentalDays: json["rentalDays"] == null ? void 0 : json["rentalDays"],
    numberOfDaysCalculated: json["numberOfDaysCalculated"] == null ? void 0 : json["numberOfDaysCalculated"],
    maxPrice: json["maxPrice"] == null ? void 0 : V2MoneyFromJSON(json["maxPrice"]),
    total: json["total"] == null ? void 0 : V2MoneyFromJSON(json["total"]),
    isExcluded: json["isExcluded"] == null ? void 0 : json["isExcluded"],
    sortOrder: json["sortOrder"] == null ? void 0 : json["sortOrder"]
  };
}
function PricingResponseProductPricesToJSON(json) {
  return PricingResponseProductPricesToJSONTyped(json, false);
}
function PricingResponseProductPricesToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    id: value["id"],
    code: value["code"],
    name: value["name"],
    category: value["category"],
    mandatory: value["mandatory"],
    description: value["description"],
    price: V2MoneyToJSON(value["price"]),
    type: value["type"],
    allowMultiple: value["allowMultiple"],
    rentalDays: value["rentalDays"],
    numberOfDaysCalculated: value["numberOfDaysCalculated"],
    maxPrice: V2MoneyToJSON(value["maxPrice"]),
    total: V2MoneyToJSON(value["total"]),
    isExcluded: value["isExcluded"],
    sortOrder: value["sortOrder"]
  };
}

// src/rentals-api/models/PricingResponse.ts
function instanceOfPricingResponse(value) {
  return true;
}
function PricingResponseFromJSON(json) {
  return PricingResponseFromJSONTyped(json, false);
}
function PricingResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    reservationTotal: json["reservationTotal"] == null ? void 0 : V2MoneyFromJSON(json["reservationTotal"]),
    productPrices: json["productPrices"] == null ? void 0 : json["productPrices"].map(PricingResponseProductPricesFromJSON)
  };
}
function PricingResponseToJSON(json) {
  return PricingResponseToJSONTyped(json, false);
}
function PricingResponseToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    reservationTotal: V2MoneyToJSON(value["reservationTotal"]),
    productPrices: value["productPrices"] == null ? void 0 : value["productPrices"].map(PricingResponseProductPricesToJSON)
  };
}

// src/rentals-api/models/ProductGroup.ts
function instanceOfProductGroup(value) {
  return true;
}
function ProductGroupFromJSON(json) {
  return ProductGroupFromJSONTyped(json, false);
}
function ProductGroupFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    group: json["Group"] == null ? void 0 : json["Group"],
    webCategory: json["WebCategory"] == null ? void 0 : json["WebCategory"],
    required: json["Required"] == null ? void 0 : json["Required"],
    allowMultiple: json["AllowMultiple"] == null ? void 0 : json["AllowMultiple"],
    description: json["Description"] == null ? void 0 : json["Description"]
  };
}
function ProductGroupToJSON(json) {
  return ProductGroupToJSONTyped(json, false);
}
function ProductGroupToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    Group: value["group"],
    WebCategory: value["webCategory"],
    Required: value["required"],
    AllowMultiple: value["allowMultiple"],
    Description: value["description"]
  };
}

// src/rentals-api/models/Quote.ts
function instanceOfQuote(value) {
  return true;
}
function QuoteFromJSON(json) {
  return QuoteFromJSONTyped(json, false);
}
function QuoteFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    quoteId: json["QuoteId"] == null ? void 0 : json["QuoteId"],
    reservationId: json["ReservationId"] == null ? void 0 : json["ReservationId"],
    reservationReference: json["ReservationReference"] == null ? void 0 : json["ReservationReference"],
    onlineLogin: json["OnlineLogin"] == null ? void 0 : json["OnlineLogin"],
    quoteName: json["QuoteName"] == null ? void 0 : json["QuoteName"],
    bookingStatus: json["BookingStatus"] == null ? void 0 : json["BookingStatus"],
    status: json["Status"] == null ? void 0 : json["Status"],
    pickUpLocationId: json["PickUpLocationId"] == null ? void 0 : json["PickUpLocationId"],
    pickUpLocationCode: json["PickUpLocationCode"] == null ? void 0 : json["PickUpLocationCode"],
    dropOffLocationId: json["DropOffLocationId"] == null ? void 0 : json["DropOffLocationId"],
    dropOffLocationCode: json["DropOffLocationCode"] == null ? void 0 : json["DropOffLocationCode"],
    pickUpDate: json["PickUpDate"] == null ? void 0 : stringToDate2(json["PickUpDate"]),
    dropOffDate: json["DropOffDate"] == null ? void 0 : stringToDate2(json["DropOffDate"]),
    rentalDays: json["RentalDays"] == null ? void 0 : json["RentalDays"],
    fleetCategory: json["FleetCategory"] == null ? void 0 : FleetCategoryFromJSON2(json["FleetCategory"]),
    totalPrice: json["TotalPrice"] == null ? void 0 : QuoteTotalPriceFromJSON(json["TotalPrice"]),
    agentCommission: json["AgentCommission"] == null ? void 0 : QuoteAgentCommissionFromJSON(json["AgentCommission"]),
    agentCollects: json["AgentCollects"] == null ? void 0 : QuoteAgentCollectsFromJSON(json["AgentCollects"]),
    agentBalanceDue: json["AgentBalanceDue"] == null ? void 0 : QuoteAgentBalanceDueFromJSON(json["AgentBalanceDue"]),
    agentName: json["AgentName"] == null ? void 0 : json["AgentName"],
    balanceDue: json["BalanceDue"] == null ? void 0 : QuoteBalanceDueFromJSON(json["BalanceDue"]),
    balanceDueWithCCFee: json["BalanceDueWithCCFee"] == null ? void 0 : QuoteBalanceDueWithCCFeeFromJSON(json["BalanceDueWithCCFee"]),
    cCSurchargePercentage: json["CCSurchargePercentage"] == null ? void 0 : json["CCSurchargePercentage"],
    lines: json["Lines"] == null ? void 0 : json["Lines"].map(RentalLineItemFromJSON)
  };
}
function QuoteToJSON(json) {
  return QuoteToJSONTyped(json, false);
}
function QuoteToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    QuoteId: value["quoteId"],
    ReservationId: value["reservationId"],
    ReservationReference: value["reservationReference"],
    OnlineLogin: value["onlineLogin"],
    QuoteName: value["quoteName"],
    BookingStatus: value["bookingStatus"],
    Status: value["status"],
    PickUpLocationId: value["pickUpLocationId"],
    PickUpLocationCode: value["pickUpLocationCode"],
    DropOffLocationId: value["dropOffLocationId"],
    DropOffLocationCode: value["dropOffLocationCode"],
    PickUpDate: value["pickUpDate"] == null ? void 0 : dateToString2(value["pickUpDate"]),
    DropOffDate: value["dropOffDate"] == null ? void 0 : dateToString2(value["dropOffDate"]),
    RentalDays: value["rentalDays"],
    FleetCategory: FleetCategoryToJSON2(value["fleetCategory"]),
    TotalPrice: QuoteTotalPriceToJSON(value["totalPrice"]),
    AgentCommission: QuoteAgentCommissionToJSON(value["agentCommission"]),
    AgentCollects: QuoteAgentCollectsToJSON(value["agentCollects"]),
    AgentBalanceDue: QuoteAgentBalanceDueToJSON(value["agentBalanceDue"]),
    AgentName: value["agentName"],
    BalanceDue: QuoteBalanceDueToJSON(value["balanceDue"]),
    BalanceDueWithCCFee: QuoteBalanceDueWithCCFeeToJSON(value["balanceDueWithCCFee"]),
    CCSurchargePercentage: value["cCSurchargePercentage"],
    Lines: value["lines"] == null ? void 0 : value["lines"].map(RentalLineItemToJSON)
  };
}

// src/rentals-api/models/QuoteWithHirer.ts
function instanceOfQuoteWithHirer(value) {
  return true;
}
function QuoteWithHirerFromJSON(json) {
  return QuoteWithHirerFromJSONTyped(json, false);
}
function QuoteWithHirerFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    quoteId: json["QuoteId"] == null ? void 0 : json["QuoteId"],
    reservationId: json["ReservationId"] == null ? void 0 : json["ReservationId"],
    reservationReference: json["ReservationReference"] == null ? void 0 : json["ReservationReference"],
    onlineLogin: json["OnlineLogin"] == null ? void 0 : json["OnlineLogin"],
    quoteName: json["QuoteName"] == null ? void 0 : json["QuoteName"],
    bookingStatus: json["BookingStatus"] == null ? void 0 : json["BookingStatus"],
    status: json["Status"] == null ? void 0 : json["Status"],
    pickUpLocationId: json["PickUpLocationId"] == null ? void 0 : json["PickUpLocationId"],
    pickUpLocationCode: json["PickUpLocationCode"] == null ? void 0 : json["PickUpLocationCode"],
    dropOffLocationId: json["DropOffLocationId"] == null ? void 0 : json["DropOffLocationId"],
    dropOffLocationCode: json["DropOffLocationCode"] == null ? void 0 : json["DropOffLocationCode"],
    pickUpDate: json["PickUpDate"] == null ? void 0 : stringToDate2(json["PickUpDate"]),
    dropOffDate: json["DropOffDate"] == null ? void 0 : stringToDate2(json["DropOffDate"]),
    rentalDays: json["RentalDays"] == null ? void 0 : json["RentalDays"],
    fleetCategory: json["FleetCategory"] == null ? void 0 : FleetCategoryFromJSON2(json["FleetCategory"]),
    totalPrice: json["TotalPrice"] == null ? void 0 : QuoteTotalPriceFromJSON(json["TotalPrice"]),
    agentCommission: json["AgentCommission"] == null ? void 0 : QuoteAgentCommissionFromJSON(json["AgentCommission"]),
    agentCollects: json["AgentCollects"] == null ? void 0 : QuoteAgentCollectsFromJSON(json["AgentCollects"]),
    agentBalanceDue: json["AgentBalanceDue"] == null ? void 0 : QuoteAgentBalanceDueFromJSON(json["AgentBalanceDue"]),
    agentName: json["AgentName"] == null ? void 0 : json["AgentName"],
    balanceDue: json["BalanceDue"] == null ? void 0 : QuoteBalanceDueFromJSON(json["BalanceDue"]),
    balanceDueWithCCFee: json["BalanceDueWithCCFee"] == null ? void 0 : QuoteBalanceDueWithCCFeeFromJSON(json["BalanceDueWithCCFee"]),
    cCSurchargePercentage: json["CCSurchargePercentage"] == null ? void 0 : json["CCSurchargePercentage"],
    lines: json["Lines"] == null ? void 0 : json["Lines"].map(RentalLineItemFromJSON),
    hirerDetails: json["HirerDetails"] == null ? void 0 : HirerDetailsFromJSON2(json["HirerDetails"])
  };
}
function QuoteWithHirerToJSON(json) {
  return QuoteWithHirerToJSONTyped(json, false);
}
function QuoteWithHirerToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    QuoteId: value["quoteId"],
    ReservationId: value["reservationId"],
    ReservationReference: value["reservationReference"],
    OnlineLogin: value["onlineLogin"],
    QuoteName: value["quoteName"],
    BookingStatus: value["bookingStatus"],
    Status: value["status"],
    PickUpLocationId: value["pickUpLocationId"],
    PickUpLocationCode: value["pickUpLocationCode"],
    DropOffLocationId: value["dropOffLocationId"],
    DropOffLocationCode: value["dropOffLocationCode"],
    PickUpDate: value["pickUpDate"] == null ? void 0 : dateToString2(value["pickUpDate"]),
    DropOffDate: value["dropOffDate"] == null ? void 0 : dateToString2(value["dropOffDate"]),
    RentalDays: value["rentalDays"],
    FleetCategory: FleetCategoryToJSON2(value["fleetCategory"]),
    TotalPrice: QuoteTotalPriceToJSON(value["totalPrice"]),
    AgentCommission: QuoteAgentCommissionToJSON(value["agentCommission"]),
    AgentCollects: QuoteAgentCollectsToJSON(value["agentCollects"]),
    AgentBalanceDue: QuoteAgentBalanceDueToJSON(value["agentBalanceDue"]),
    AgentName: value["agentName"],
    BalanceDue: QuoteBalanceDueToJSON(value["balanceDue"]),
    BalanceDueWithCCFee: QuoteBalanceDueWithCCFeeToJSON(value["balanceDueWithCCFee"]),
    CCSurchargePercentage: value["cCSurchargePercentage"],
    Lines: value["lines"] == null ? void 0 : value["lines"].map(RentalLineItemToJSON),
    HirerDetails: HirerDetailsToJSON2(value["hirerDetails"])
  };
}

// src/rentals-api/models/RateInfoDaily.ts
function instanceOfRateInfoDaily(value) {
  return true;
}
function RateInfoDailyFromJSON(json) {
  return RateInfoDailyFromJSONTyped(json, false);
}
function RateInfoDailyFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    afterDiscount: json["afterDiscount"] == null ? void 0 : MoneyFromJSON(json["afterDiscount"]),
    beforeDiscount: json["beforeDiscount"] == null ? void 0 : MoneyFromJSON(json["beforeDiscount"])
  };
}
function RateInfoDailyToJSON(json) {
  return RateInfoDailyToJSONTyped(json, false);
}
function RateInfoDailyToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    afterDiscount: MoneyToJSON(value["afterDiscount"]),
    beforeDiscount: MoneyToJSON(value["beforeDiscount"])
  };
}

// src/rentals-api/models/RateInfoDiscount.ts
function instanceOfRateInfoDiscount(value) {
  return true;
}
function RateInfoDiscountFromJSON(json) {
  return RateInfoDiscountFromJSONTyped(json, false);
}
function RateInfoDiscountFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    dailyRate: json["dailyRate"] == null ? void 0 : MoneyFromJSON(json["dailyRate"]),
    name: json["name"] == null ? void 0 : json["name"],
    description: json["description"] == null ? void 0 : json["description"],
    percentage: json["percentage"] == null ? void 0 : json["percentage"]
  };
}
function RateInfoDiscountToJSON(json) {
  return RateInfoDiscountToJSONTyped(json, false);
}
function RateInfoDiscountToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    dailyRate: MoneyToJSON(value["dailyRate"]),
    name: value["name"],
    description: value["description"],
    percentage: value["percentage"]
  };
}

// src/rentals-api/models/RateInfoMonthly.ts
function instanceOfRateInfoMonthly(value) {
  return true;
}
function RateInfoMonthlyFromJSON(json) {
  return RateInfoMonthlyFromJSONTyped(json, false);
}
function RateInfoMonthlyFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    afterDiscount: json["afterDiscount"] == null ? void 0 : MoneyFromJSON(json["afterDiscount"]),
    extraDaysRateAfterDiscount: json["extraDaysRateAfterDiscount"] == null ? void 0 : MoneyFromJSON(json["extraDaysRateAfterDiscount"]),
    numberOfMonths: json["numberOfMonths"] == null ? void 0 : json["numberOfMonths"],
    numberOfExtraDays: json["numberOfExtraDays"] == null ? void 0 : json["numberOfExtraDays"]
  };
}
function RateInfoMonthlyToJSON(json) {
  return RateInfoMonthlyToJSONTyped(json, false);
}
function RateInfoMonthlyToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    afterDiscount: MoneyToJSON(value["afterDiscount"]),
    extraDaysRateAfterDiscount: MoneyToJSON(value["extraDaysRateAfterDiscount"]),
    numberOfMonths: value["numberOfMonths"],
    numberOfExtraDays: value["numberOfExtraDays"]
  };
}

// src/rentals-api/models/RateInfoSeason.ts
function instanceOfRateInfoSeason(value) {
  return true;
}
function RateInfoSeasonFromJSON(json) {
  return RateInfoSeasonFromJSONTyped(json, false);
}
function RateInfoSeasonFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    name: json["name"] == null ? void 0 : json["name"],
    subTotal: json["subTotal"] == null ? void 0 : MoneyFromJSON(json["subTotal"])
  };
}
function RateInfoSeasonToJSON(json) {
  return RateInfoSeasonToJSONTyped(json, false);
}
function RateInfoSeasonToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    name: value["name"],
    subTotal: MoneyToJSON(value["subTotal"])
  };
}

// src/rentals-api/models/RateInfo.ts
function instanceOfRateInfo(value) {
  return true;
}
function RateInfoFromJSON(json) {
  return RateInfoFromJSONTyped(json, false);
}
function RateInfoFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    daily: json["daily"] == null ? void 0 : RateInfoDailyFromJSON(json["daily"]),
    monthly: json["monthly"] == null ? void 0 : RateInfoMonthlyFromJSON(json["monthly"]),
    discount: json["discount"] == null ? void 0 : RateInfoDiscountFromJSON(json["discount"]),
    numberOfDays: json["numberOfDays"] == null ? void 0 : json["numberOfDays"],
    numberOfHours: json["numberOfHours"] == null ? void 0 : json["numberOfHours"],
    subTotal: json["subTotal"] == null ? void 0 : MoneyFromJSON(json["subTotal"]),
    season: json["season"] == null ? void 0 : RateInfoSeasonFromJSON(json["season"])
  };
}
function RateInfoToJSON(json) {
  return RateInfoToJSONTyped(json, false);
}
function RateInfoToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    daily: RateInfoDailyToJSON(value["daily"]),
    monthly: RateInfoMonthlyToJSON(value["monthly"]),
    discount: RateInfoDiscountToJSON(value["discount"]),
    numberOfDays: value["numberOfDays"],
    numberOfHours: value["numberOfHours"],
    subTotal: MoneyToJSON(value["subTotal"]),
    season: RateInfoSeasonToJSON(value["season"])
  };
}

// src/rentals-api/models/RentalCatalogItem.ts
function instanceOfRentalCatalogItem(value) {
  return true;
}
function RentalCatalogItemFromJSON(json) {
  return RentalCatalogItemFromJSONTyped(json, false);
}
function RentalCatalogItemFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    id: json["id"] == null ? void 0 : json["id"],
    name: json["name"] == null ? void 0 : json["name"],
    code: json["code"] == null ? void 0 : json["code"],
    fleetType: json["fleetType"] == null ? void 0 : json["fleetType"],
    fleetTypeId: json["fleetTypeId"] == null ? void 0 : json["fleetTypeId"],
    altId: json["altId"] == null ? void 0 : json["altId"],
    altFleetTypeId: json["altFleetTypeId"] == null ? void 0 : json["altFleetTypeId"],
    region: json["region"] == null ? void 0 : json["region"],
    description: json["description"] == null ? void 0 : json["description"],
    highlightedFeatures: json["highlightedFeatures"] == null ? void 0 : json["highlightedFeatures"],
    vehicleDescription: json["vehicleDescription"] == null ? void 0 : json["vehicleDescription"],
    seatCount: json["seatCount"] == null ? void 0 : json["seatCount"],
    sleepCount: json["sleepCount"] == null ? void 0 : json["sleepCount"],
    sleepDescription: json["sleepDescription"] == null ? void 0 : json["sleepDescription"],
    transmission: json["transmission"] == null ? void 0 : json["transmission"],
    engineSize: json["engineSize"] == null ? void 0 : json["engineSize"],
    bodyStyle: json["bodyStyle"] == null ? void 0 : json["bodyStyle"],
    weight: json["weight"] == null ? void 0 : json["weight"],
    length: json["length"] == null ? void 0 : json["length"],
    width: json["width"] == null ? void 0 : json["width"],
    luggageSmall: json["luggageSmall"] == null ? void 0 : json["luggageSmall"],
    luggageLarge: json["luggageLarge"] == null ? void 0 : json["luggageLarge"],
    image: json["image"] == null ? void 0 : json["image"],
    url: json["url"] == null ? void 0 : json["url"]
  };
}
function RentalCatalogItemToJSON(json) {
  return RentalCatalogItemToJSONTyped(json, false);
}
function RentalCatalogItemToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    id: value["id"],
    name: value["name"],
    code: value["code"],
    fleetType: value["fleetType"],
    fleetTypeId: value["fleetTypeId"],
    altId: value["altId"],
    altFleetTypeId: value["altFleetTypeId"],
    region: value["region"],
    description: value["description"],
    highlightedFeatures: value["highlightedFeatures"],
    vehicleDescription: value["vehicleDescription"],
    seatCount: value["seatCount"],
    sleepCount: value["sleepCount"],
    sleepDescription: value["sleepDescription"],
    transmission: value["transmission"],
    engineSize: value["engineSize"],
    bodyStyle: value["bodyStyle"],
    weight: value["weight"],
    length: value["length"],
    width: value["width"],
    luggageSmall: value["luggageSmall"],
    luggageLarge: value["luggageLarge"],
    image: value["image"],
    url: value["url"]
  };
}

// src/rentals-api/models/RentalCatalogResponse.ts
function instanceOfRentalCatalogResponse(value) {
  return true;
}
function RentalCatalogResponseFromJSON2(json) {
  return RentalCatalogResponseFromJSONTyped2(json, false);
}
function RentalCatalogResponseFromJSONTyped2(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    products: json["products"] == null ? void 0 : json["products"].map(RentalCatalogItemFromJSON)
  };
}
function RentalCatalogResponseToJSON2(json) {
  return RentalCatalogResponseToJSONTyped(json, false);
}
function RentalCatalogResponseToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    products: value["products"] == null ? void 0 : value["products"].map(RentalCatalogItemToJSON)
  };
}

// src/rentals-api/models/RentalDocumentDocumentLink.ts
function instanceOfRentalDocumentDocumentLink(value) {
  return true;
}
function RentalDocumentDocumentLinkFromJSON(json) {
  return RentalDocumentDocumentLinkFromJSONTyped(json, false);
}
function RentalDocumentDocumentLinkFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    id: json["id"] == null ? void 0 : json["id"],
    setupId: json["setupId"] == null ? void 0 : json["setupId"]
  };
}
function RentalDocumentDocumentLinkToJSON(json) {
  return RentalDocumentDocumentLinkToJSONTyped(json, false);
}
function RentalDocumentDocumentLinkToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    id: value["id"],
    setupId: value["setupId"]
  };
}

// src/rentals-api/models/RentalSignatureCustomer.ts
function instanceOfRentalSignatureCustomer(value) {
  return true;
}
function RentalSignatureCustomerFromJSON(json) {
  return RentalSignatureCustomerFromJSONTyped(json, false);
}
function RentalSignatureCustomerFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    id: json["id"] == null ? void 0 : json["id"],
    firstName: json["firstName"] == null ? void 0 : json["firstName"],
    lastName: json["lastName"] == null ? void 0 : json["lastName"]
  };
}
function RentalSignatureCustomerToJSON(json) {
  return RentalSignatureCustomerToJSONTyped(json, false);
}
function RentalSignatureCustomerToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    id: value["id"],
    firstName: value["firstName"],
    lastName: value["lastName"]
  };
}

// src/rentals-api/models/RentalDocument.ts
function instanceOfRentalDocument(value) {
  return true;
}
function RentalDocumentFromJSON(json) {
  return RentalDocumentFromJSONTyped(json, false);
}
function RentalDocumentFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    documentLink: json["documentLink"] == null ? void 0 : RentalDocumentDocumentLinkFromJSON(json["documentLink"]),
    seqNo: json["seqNo"] == null ? void 0 : json["seqNo"],
    title: json["title"] == null ? void 0 : json["title"],
    text: json["text"] == null ? void 0 : json["text"],
    isPrimary: json["isPrimary"] == null ? void 0 : json["isPrimary"],
    isUploaded: json["isUploaded"] == null ? void 0 : json["isUploaded"],
    allDrivers: json["allDrivers"] == null ? void 0 : json["allDrivers"],
    isOverCounterOnly: json["isOverCounterOnly"] == null ? void 0 : json["isOverCounterOnly"],
    isOptional: json["isOptional"] == null ? void 0 : json["isOptional"],
    allowMultiple: json["allowMultiple"] == null ? void 0 : json["allowMultiple"],
    order: json["order"] == null ? void 0 : json["order"],
    docType: json["docType"] == null ? void 0 : json["docType"],
    maxImageWidth: json["maxImageWidth"] == null ? void 0 : json["maxImageWidth"],
    url: json["url"] == null ? void 0 : json["url"],
    workflowStepId: json["workflowStepId"] == null ? void 0 : json["workflowStepId"],
    containsSensitiveData: json["containsSensitiveData"] == null ? void 0 : json["containsSensitiveData"],
    isVehicleUpload: json["isVehicleUpload"] == null ? void 0 : json["isVehicleUpload"],
    vehicleId: json["vehicleId"] == null ? void 0 : json["vehicleId"],
    notes: json["notes"] == null ? void 0 : json["notes"],
    storageProvider: json["storageProvider"] == null ? void 0 : json["storageProvider"],
    customer: json["customer"] == null ? void 0 : RentalSignatureCustomerFromJSON(json["customer"])
  };
}
function RentalDocumentToJSON(json) {
  return RentalDocumentToJSONTyped(json, false);
}
function RentalDocumentToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    documentLink: RentalDocumentDocumentLinkToJSON(value["documentLink"]),
    seqNo: value["seqNo"],
    title: value["title"],
    text: value["text"],
    isPrimary: value["isPrimary"],
    isUploaded: value["isUploaded"],
    allDrivers: value["allDrivers"],
    isOverCounterOnly: value["isOverCounterOnly"],
    isOptional: value["isOptional"],
    allowMultiple: value["allowMultiple"],
    order: value["order"],
    docType: value["docType"],
    maxImageWidth: value["maxImageWidth"],
    url: value["url"],
    workflowStepId: value["workflowStepId"],
    containsSensitiveData: value["containsSensitiveData"],
    isVehicleUpload: value["isVehicleUpload"],
    vehicleId: value["vehicleId"],
    notes: value["notes"],
    storageProvider: value["storageProvider"],
    customer: RentalSignatureCustomerToJSON(value["customer"])
  };
}

// src/rentals-api/models/RentalDocumentUpload.ts
function instanceOfRentalDocumentUpload(value) {
  return true;
}
function RentalDocumentUploadFromJSON(json) {
  return RentalDocumentUploadFromJSONTyped(json, false);
}
function RentalDocumentUploadFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    documentLinkSetupId: json["documentLinkSetupId"] == null ? void 0 : json["documentLinkSetupId"],
    url: json["url"] == null ? void 0 : json["url"],
    customerId: json["customerId"] == null ? void 0 : json["customerId"],
    vehicleId: json["vehicleId"] == null ? void 0 : json["vehicleId"],
    description: json["description"] == null ? void 0 : json["description"],
    docType: json["docType"] == null ? void 0 : json["docType"],
    workflowCode: json["workflowCode"] == null ? void 0 : json["workflowCode"],
    originalName: json["originalName"] == null ? void 0 : json["originalName"],
    storageProvider: json["storageProvider"] == null ? void 0 : json["storageProvider"],
    resultsProvider: json["resultsProvider"] == null ? void 0 : json["resultsProvider"],
    seqNo: json["seqNo"] == null ? void 0 : json["seqNo"]
  };
}
function RentalDocumentUploadToJSON(json) {
  return RentalDocumentUploadToJSONTyped(json, false);
}
function RentalDocumentUploadToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    documentLinkSetupId: value["documentLinkSetupId"],
    url: value["url"],
    customerId: value["customerId"],
    vehicleId: value["vehicleId"],
    description: value["description"],
    docType: value["docType"],
    workflowCode: value["workflowCode"],
    originalName: value["originalName"],
    storageProvider: value["storageProvider"],
    resultsProvider: value["resultsProvider"],
    seqNo: value["seqNo"]
  };
}

// src/rentals-api/models/RentalDocumentUploadResponse.ts
function instanceOfRentalDocumentUploadResponse(value) {
  return true;
}
function RentalDocumentUploadResponseFromJSON(json) {
  return RentalDocumentUploadResponseFromJSONTyped(json, false);
}
function RentalDocumentUploadResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    documentLinkId: json["documentLinkId"] == null ? void 0 : json["documentLinkId"]
  };
}
function RentalDocumentUploadResponseToJSON(json) {
  return RentalDocumentUploadResponseToJSONTyped(json, false);
}
function RentalDocumentUploadResponseToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    documentLinkId: value["documentLinkId"]
  };
}

// src/rentals-api/models/RentalProduct.ts
function instanceOfRentalProduct(value) {
  return true;
}
function RentalProductFromJSON(json) {
  return RentalProductFromJSONTyped(json, false);
}
function RentalProductFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    id: json["id"] == null ? void 0 : json["id"],
    name: json["name"] == null ? void 0 : json["name"],
    description: json["description"] == null ? void 0 : json["description"],
    highlightedFeatures: json["highlightedFeatures"] == null ? void 0 : json["highlightedFeatures"],
    vehicleDescription: json["vehicleDescription"] == null ? void 0 : json["vehicleDescription"],
    seatCount: json["seatCount"] == null ? void 0 : json["seatCount"],
    sleepCount: json["sleepCount"] == null ? void 0 : json["sleepCount"],
    sleepDescription: json["sleepDescription"] == null ? void 0 : json["sleepDescription"],
    transmission: json["transmission"] == null ? void 0 : json["transmission"],
    engineSize: json["engineSize"] == null ? void 0 : json["engineSize"],
    bodyStyle: json["bodyStyle"] == null ? void 0 : json["bodyStyle"],
    weight: json["weight"] == null ? void 0 : json["weight"],
    length: json["length"] == null ? void 0 : json["length"],
    width: json["width"] == null ? void 0 : json["width"],
    luggageSmall: json["luggageSmall"] == null ? void 0 : json["luggageSmall"],
    luggageLarge: json["luggageLarge"] == null ? void 0 : json["luggageLarge"],
    image: json["image"] == null ? void 0 : json["image"],
    url: json["url"] == null ? void 0 : json["url"]
  };
}
function RentalProductToJSON(json) {
  return RentalProductToJSONTyped(json, false);
}
function RentalProductToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    id: value["id"],
    name: value["name"],
    description: value["description"],
    highlightedFeatures: value["highlightedFeatures"],
    vehicleDescription: value["vehicleDescription"],
    seatCount: value["seatCount"],
    sleepCount: value["sleepCount"],
    sleepDescription: value["sleepDescription"],
    transmission: value["transmission"],
    engineSize: value["engineSize"],
    bodyStyle: value["bodyStyle"],
    weight: value["weight"],
    length: value["length"],
    width: value["width"],
    luggageSmall: value["luggageSmall"],
    luggageLarge: value["luggageLarge"],
    image: value["image"],
    url: value["url"]
  };
}

// src/rentals-api/models/RentalSignatureSignature.ts
function instanceOfRentalSignatureSignature(value) {
  return true;
}
function RentalSignatureSignatureFromJSON(json) {
  return RentalSignatureSignatureFromJSONTyped(json, false);
}
function RentalSignatureSignatureFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    base64: json["base64"] == null ? void 0 : json["base64"],
    lines: json["lines"] == null ? void 0 : json["lines"]
  };
}
function RentalSignatureSignatureToJSON(json) {
  return RentalSignatureSignatureToJSONTyped(json, false);
}
function RentalSignatureSignatureToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    base64: value["base64"],
    lines: value["lines"]
  };
}

// src/rentals-api/models/RentalSignatureSignatureTemplate.ts
function instanceOfRentalSignatureSignatureTemplate(value) {
  return true;
}
function RentalSignatureSignatureTemplateFromJSON(json) {
  return RentalSignatureSignatureTemplateFromJSONTyped(json, false);
}
function RentalSignatureSignatureTemplateFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    id: json["id"] == null ? void 0 : json["id"],
    title: json["title"] == null ? void 0 : json["title"],
    text: json["text"] == null ? void 0 : json["text"]
  };
}
function RentalSignatureSignatureTemplateToJSON(json) {
  return RentalSignatureSignatureTemplateToJSONTyped(json, false);
}
function RentalSignatureSignatureTemplateToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    id: value["id"],
    title: value["title"],
    text: value["text"]
  };
}

// src/rentals-api/models/RentalSignature.ts
function instanceOfRentalSignature(value) {
  return true;
}
function RentalSignatureFromJSON(json) {
  return RentalSignatureFromJSONTyped(json, false);
}
function RentalSignatureFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    customer: json["customer"] == null ? void 0 : RentalSignatureCustomerFromJSON(json["customer"]),
    isSigned: json["isSigned"] == null ? void 0 : json["isSigned"],
    signature: json["signature"] == null ? void 0 : RentalSignatureSignatureFromJSON(json["signature"]),
    forRego: json["forRego"] == null ? void 0 : json["forRego"],
    signedDate: json["signedDate"] == null ? void 0 : stringToDate2(json["signedDate"]),
    signedIp: json["signedIp"] == null ? void 0 : json["signedIp"],
    signatureTemplate: json["signatureTemplate"] == null ? void 0 : RentalSignatureSignatureTemplateFromJSON(json["signatureTemplate"]),
    isCcAuth: json["isCcAuth"] == null ? void 0 : json["isCcAuth"],
    isOverCounterOnly: json["isOverCounterOnly"] == null ? void 0 : json["isOverCounterOnly"],
    order: json["order"] == null ? void 0 : json["order"]
  };
}
function RentalSignatureToJSON(json) {
  return RentalSignatureToJSONTyped(json, false);
}
function RentalSignatureToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    customer: RentalSignatureCustomerToJSON(value["customer"]),
    isSigned: value["isSigned"],
    signature: RentalSignatureSignatureToJSON(value["signature"]),
    forRego: value["forRego"],
    signedDate: value["signedDate"] == null ? void 0 : dateToString2(value["signedDate"]),
    signedIp: value["signedIp"],
    signatureTemplate: RentalSignatureSignatureTemplateToJSON(value["signatureTemplate"]),
    isCcAuth: value["isCcAuth"],
    isOverCounterOnly: value["isOverCounterOnly"],
    order: value["order"]
  };
}

// src/rentals-api/models/ReservationDetails.ts
function instanceOfReservationDetails(value) {
  if (!("bookingNumber" in value) || value["bookingNumber"] === void 0) return false;
  return true;
}
function ReservationDetailsFromJSON(json) {
  return ReservationDetailsFromJSONTyped(json, false);
}
function ReservationDetailsFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    bookingNumber: json["BookingNumber"],
    pickUpLocation: json["PickUpLocation"] == null ? void 0 : json["PickUpLocation"],
    dropOffLocation: json["DropOffLocation"] == null ? void 0 : json["DropOffLocation"],
    pickUpDateTime: json["PickUpDateTime"] == null ? void 0 : stringToDate2(json["PickUpDateTime"]),
    dropOffDateTime: json["DropOffDateTime"] == null ? void 0 : stringToDate2(json["DropOffDateTime"]),
    categoryCode: json["CategoryCode"] == null ? void 0 : json["CategoryCode"],
    campaignCode: json["CampaignCode"] == null ? void 0 : json["CampaignCode"],
    secondaryProductsAndInsuranceMap: json["SecondaryProductsAndInsuranceMap"] == null ? void 0 : json["SecondaryProductsAndInsuranceMap"],
    numberTravelling: json["NumberTravelling"] == null ? void 0 : json["NumberTravelling"],
    mainContact: json["MainContact"] == null ? void 0 : LiteContactFromJSON(json["MainContact"]),
    additionalDrivers: json["AdditionalDrivers"] == null ? void 0 : json["AdditionalDrivers"].map(LiteAdditionalDriverFromJSON),
    flightNumber: json["FlightNumber"] == null ? void 0 : json["FlightNumber"]
  };
}
function ReservationDetailsToJSON(json) {
  return ReservationDetailsToJSONTyped(json, false);
}
function ReservationDetailsToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    BookingNumber: value["bookingNumber"],
    PickUpLocation: value["pickUpLocation"],
    DropOffLocation: value["dropOffLocation"],
    PickUpDateTime: value["pickUpDateTime"] == null ? void 0 : dateToString2(value["pickUpDateTime"]),
    DropOffDateTime: value["dropOffDateTime"] == null ? void 0 : dateToString2(value["dropOffDateTime"]),
    CategoryCode: value["categoryCode"],
    CampaignCode: value["campaignCode"],
    SecondaryProductsAndInsuranceMap: value["secondaryProductsAndInsuranceMap"],
    NumberTravelling: value["numberTravelling"],
    MainContact: LiteContactToJSON(value["mainContact"]),
    AdditionalDrivers: value["additionalDrivers"] == null ? void 0 : value["additionalDrivers"].map(LiteAdditionalDriverToJSON),
    FlightNumber: value["flightNumber"]
  };
}

// src/rentals-api/models/ReservationOptions.ts
function instanceOfReservationOptions(value) {
  return true;
}
function ReservationOptionsFromJSON(json) {
  return ReservationOptionsFromJSONTyped(json, false);
}
function ReservationOptionsFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    autoPayment: json["autoPayment"] == null ? void 0 : json["autoPayment"]
  };
}
function ReservationOptionsToJSON(json) {
  return ReservationOptionsToJSONTyped(json, false);
}
function ReservationOptionsToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    autoPayment: value["autoPayment"]
  };
}

// src/rentals-api/models/SecondaryProductItem.ts
function instanceOfSecondaryProductItem(value) {
  return true;
}
function SecondaryProductItemFromJSON(json) {
  return SecondaryProductItemFromJSONTyped(json, false);
}
function SecondaryProductItemFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    id: json["id"] == null ? void 0 : json["id"],
    code: json["code"] == null ? void 0 : json["code"],
    associatedProductCode: json["associatedProductCode"] == null ? void 0 : json["associatedProductCode"],
    name: json["name"] == null ? void 0 : json["name"],
    price: json["price"] == null ? void 0 : MoneyFromJSON(json["price"]),
    quantity: json["quantity"] == null ? void 0 : json["quantity"],
    rentalDays: json["rentalDays"] == null ? void 0 : json["rentalDays"],
    maxPrice: json["maxPrice"] == null ? void 0 : MoneyFromJSON(json["maxPrice"]),
    total: json["total"] == null ? void 0 : MoneyFromJSON(json["total"]),
    isExcluded: json["isExcluded"] == null ? void 0 : json["isExcluded"],
    sortOrder: json["sortOrder"] == null ? void 0 : json["sortOrder"]
  };
}
function SecondaryProductItemToJSON(json) {
  return SecondaryProductItemToJSONTyped(json, false);
}
function SecondaryProductItemToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    id: value["id"],
    code: value["code"],
    associatedProductCode: value["associatedProductCode"],
    name: value["name"],
    price: MoneyToJSON(value["price"]),
    quantity: value["quantity"],
    rentalDays: value["rentalDays"],
    maxPrice: MoneyToJSON(value["maxPrice"]),
    total: MoneyToJSON(value["total"]),
    isExcluded: value["isExcluded"],
    sortOrder: value["sortOrder"]
  };
}

// src/rentals-api/models/ReservationPricingEnquiryResponse.ts
function instanceOfReservationPricingEnquiryResponse(value) {
  return true;
}
function ReservationPricingEnquiryResponseFromJSON(json) {
  return ReservationPricingEnquiryResponseFromJSONTyped(json, false);
}
function ReservationPricingEnquiryResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    productsTotal: json["productsTotal"] == null ? void 0 : MoneyFromJSON(json["productsTotal"]),
    productPrices: json["productPrices"] == null ? void 0 : json["productPrices"].map(SecondaryProductItemFromJSON)
  };
}
function ReservationPricingEnquiryResponseToJSON(json) {
  return ReservationPricingEnquiryResponseToJSONTyped(json, false);
}
function ReservationPricingEnquiryResponseToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    productsTotal: MoneyToJSON(value["productsTotal"]),
    productPrices: value["productPrices"] == null ? void 0 : value["productPrices"].map(SecondaryProductItemToJSON)
  };
}

// src/rentals-api/models/SecondaryProductMap.ts
function instanceOfSecondaryProductMap(value) {
  if (!("code" in value) || value["code"] === void 0) return false;
  if (!("qty" in value) || value["qty"] === void 0) return false;
  return true;
}
function SecondaryProductMapFromJSON(json) {
  return SecondaryProductMapFromJSONTyped(json, false);
}
function SecondaryProductMapFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    code: json["code"],
    qty: json["qty"]
  };
}
function SecondaryProductMapToJSON(json) {
  return SecondaryProductMapToJSONTyped(json, false);
}
function SecondaryProductMapToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    code: value["code"],
    qty: value["qty"]
  };
}

// src/rentals-api/models/SelfServiceReservation.ts
function instanceOfSelfServiceReservation(value) {
  return true;
}
function SelfServiceReservationFromJSON(json) {
  return SelfServiceReservationFromJSONTyped(json, false);
}
function SelfServiceReservationFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    quoteId: json["QuoteId"] == null ? void 0 : json["QuoteId"],
    reservationId: json["ReservationId"] == null ? void 0 : json["ReservationId"],
    reservationReference: json["ReservationReference"] == null ? void 0 : json["ReservationReference"],
    onlineLogin: json["OnlineLogin"] == null ? void 0 : json["OnlineLogin"],
    quoteName: json["QuoteName"] == null ? void 0 : json["QuoteName"],
    bookingStatus: json["BookingStatus"] == null ? void 0 : json["BookingStatus"],
    status: json["Status"] == null ? void 0 : json["Status"],
    pickUpLocationId: json["PickUpLocationId"] == null ? void 0 : json["PickUpLocationId"],
    pickUpLocationCode: json["PickUpLocationCode"] == null ? void 0 : json["PickUpLocationCode"],
    dropOffLocationId: json["DropOffLocationId"] == null ? void 0 : json["DropOffLocationId"],
    dropOffLocationCode: json["DropOffLocationCode"] == null ? void 0 : json["DropOffLocationCode"],
    pickUpDate: json["PickUpDate"] == null ? void 0 : stringToDate2(json["PickUpDate"]),
    dropOffDate: json["DropOffDate"] == null ? void 0 : stringToDate2(json["DropOffDate"]),
    rentalDays: json["RentalDays"] == null ? void 0 : json["RentalDays"],
    fleetCategory: json["FleetCategory"] == null ? void 0 : FleetCategoryFromJSON2(json["FleetCategory"]),
    totalPrice: json["TotalPrice"] == null ? void 0 : QuoteTotalPriceFromJSON(json["TotalPrice"]),
    agentCommission: json["AgentCommission"] == null ? void 0 : QuoteAgentCommissionFromJSON(json["AgentCommission"]),
    agentCollects: json["AgentCollects"] == null ? void 0 : QuoteAgentCollectsFromJSON(json["AgentCollects"]),
    agentBalanceDue: json["AgentBalanceDue"] == null ? void 0 : QuoteAgentBalanceDueFromJSON(json["AgentBalanceDue"]),
    agentName: json["AgentName"] == null ? void 0 : json["AgentName"],
    balanceDue: json["BalanceDue"] == null ? void 0 : QuoteBalanceDueFromJSON(json["BalanceDue"]),
    balanceDueWithCCFee: json["BalanceDueWithCCFee"] == null ? void 0 : QuoteBalanceDueWithCCFeeFromJSON(json["BalanceDueWithCCFee"]),
    cCSurchargePercentage: json["CCSurchargePercentage"] == null ? void 0 : json["CCSurchargePercentage"],
    lines: json["Lines"] == null ? void 0 : json["Lines"].map(RentalLineItemFromJSON),
    hirerDetails: json["HirerDetails"] == null ? void 0 : HirerDetailsFromJSON2(json["HirerDetails"]),
    insuranceProducts: json["InsuranceProducts"] == null ? void 0 : json["InsuranceProducts"].map(InsuranceProductsFromJSON),
    secondaryProducts: json["SecondaryProducts"] == null ? void 0 : json["SecondaryProducts"].map(SecondaryProductsFromJSON),
    hirer: json["hirer"] == null ? void 0 : CustomerFromJSON(json["hirer"]),
    additionalDrivers: json["additionalDrivers"] == null ? void 0 : json["additionalDrivers"].map(AdditionalDriverFromJSON),
    isVehicleAllocated: json["isVehicleAllocated"] == null ? void 0 : json["isVehicleAllocated"],
    disableCheckIn: json["DisableCheckIn"] == null ? void 0 : json["DisableCheckIn"],
    isWebAccount: json["isWebAccount"] == null ? void 0 : json["isWebAccount"],
    rentalAgreementUrl: json["rentalAgreementUrl"] == null ? void 0 : json["rentalAgreementUrl"],
    cardOnFile: json["cardOnFile"] == null ? void 0 : CardOnFileFromJSON(json["cardOnFile"]),
    options: json["options"] == null ? void 0 : ReservationOptionsFromJSON(json["options"]),
    checkInState: json["checkInState"] == null ? void 0 : json["checkInState"],
    branchDetails: json["branchDetails"] == null ? void 0 : BranchDetailsFromJSON(json["branchDetails"]),
    companyDetails: json["companyDetails"] == null ? void 0 : CompanyDetailsFromJSON(json["companyDetails"]),
    rateInfo: json["rateInfo"] == null ? void 0 : json["rateInfo"].map(RateInfoFromJSON),
    rego: json["rego"] == null ? void 0 : json["rego"],
    payments: json["payments"] == null ? void 0 : json["payments"].map(PaymentFromJSON),
    arrivalFlightNumber: json["arrivalFlightNumber"] == null ? void 0 : json["arrivalFlightNumber"],
    departureFlightNumber: json["departureFlightNumber"] == null ? void 0 : json["departureFlightNumber"],
    numberOfPeople: json["numberOfPeople"] == null ? void 0 : json["numberOfPeople"],
    hideBalanceDue: json["hideBalanceDue"] == null ? void 0 : json["hideBalanceDue"],
    bookedWith: json["bookedWith"] == null ? void 0 : BookedWithFromJSON2(json["bookedWith"])
  };
}
function SelfServiceReservationToJSON(json) {
  return SelfServiceReservationToJSONTyped(json, false);
}
function SelfServiceReservationToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    QuoteId: value["quoteId"],
    ReservationId: value["reservationId"],
    ReservationReference: value["reservationReference"],
    OnlineLogin: value["onlineLogin"],
    QuoteName: value["quoteName"],
    BookingStatus: value["bookingStatus"],
    Status: value["status"],
    PickUpLocationId: value["pickUpLocationId"],
    PickUpLocationCode: value["pickUpLocationCode"],
    DropOffLocationId: value["dropOffLocationId"],
    DropOffLocationCode: value["dropOffLocationCode"],
    PickUpDate: value["pickUpDate"] == null ? void 0 : dateToString2(value["pickUpDate"]),
    DropOffDate: value["dropOffDate"] == null ? void 0 : dateToString2(value["dropOffDate"]),
    RentalDays: value["rentalDays"],
    FleetCategory: FleetCategoryToJSON2(value["fleetCategory"]),
    TotalPrice: QuoteTotalPriceToJSON(value["totalPrice"]),
    AgentCommission: QuoteAgentCommissionToJSON(value["agentCommission"]),
    AgentCollects: QuoteAgentCollectsToJSON(value["agentCollects"]),
    AgentBalanceDue: QuoteAgentBalanceDueToJSON(value["agentBalanceDue"]),
    AgentName: value["agentName"],
    BalanceDue: QuoteBalanceDueToJSON(value["balanceDue"]),
    BalanceDueWithCCFee: QuoteBalanceDueWithCCFeeToJSON(value["balanceDueWithCCFee"]),
    CCSurchargePercentage: value["cCSurchargePercentage"],
    Lines: value["lines"] == null ? void 0 : value["lines"].map(RentalLineItemToJSON),
    HirerDetails: HirerDetailsToJSON2(value["hirerDetails"]),
    InsuranceProducts: value["insuranceProducts"] == null ? void 0 : value["insuranceProducts"].map(InsuranceProductsToJSON),
    SecondaryProducts: value["secondaryProducts"] == null ? void 0 : value["secondaryProducts"].map(SecondaryProductsToJSON),
    hirer: CustomerToJSON(value["hirer"]),
    additionalDrivers: value["additionalDrivers"] == null ? void 0 : value["additionalDrivers"].map(AdditionalDriverToJSON),
    isVehicleAllocated: value["isVehicleAllocated"],
    DisableCheckIn: value["disableCheckIn"],
    isWebAccount: value["isWebAccount"],
    rentalAgreementUrl: value["rentalAgreementUrl"],
    cardOnFile: CardOnFileToJSON(value["cardOnFile"]),
    options: ReservationOptionsToJSON(value["options"]),
    checkInState: value["checkInState"],
    branchDetails: BranchDetailsToJSON(value["branchDetails"]),
    companyDetails: CompanyDetailsToJSON(value["companyDetails"]),
    rateInfo: value["rateInfo"] == null ? void 0 : value["rateInfo"].map(RateInfoToJSON),
    rego: value["rego"],
    payments: value["payments"] == null ? void 0 : value["payments"].map(PaymentToJSON),
    arrivalFlightNumber: value["arrivalFlightNumber"],
    departureFlightNumber: value["departureFlightNumber"],
    numberOfPeople: value["numberOfPeople"],
    hideBalanceDue: value["hideBalanceDue"],
    bookedWith: BookedWithToJSON2(value["bookedWith"])
  };
}

// src/rentals-api/models/SignRaRequestSignature.ts
function instanceOfSignRaRequestSignature(value) {
  if (!("customerId" in value) || value["customerId"] === void 0) return false;
  return true;
}
function SignRaRequestSignatureFromJSON(json) {
  return SignRaRequestSignatureFromJSONTyped(json, false);
}
function SignRaRequestSignatureFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    customerId: json["customerId"],
    lines: json["lines"] == null ? void 0 : json["lines"],
    base64: json["base64"] == null ? void 0 : json["base64"],
    signedDate: json["signedDate"] == null ? void 0 : stringToDate2(json["signedDate"])
  };
}
function SignRaRequestSignatureToJSON(json) {
  return SignRaRequestSignatureToJSONTyped(json, false);
}
function SignRaRequestSignatureToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    customerId: value["customerId"],
    lines: value["lines"],
    base64: value["base64"],
    signedDate: value["signedDate"] == null ? void 0 : dateToString2(value["signedDate"])
  };
}

// src/rentals-api/models/SignRaRequest.ts
function instanceOfSignRaRequest(value) {
  return true;
}
function SignRaRequestFromJSON(json) {
  return SignRaRequestFromJSONTyped(json, false);
}
function SignRaRequestFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    signature: json["signature"] == null ? void 0 : SignRaRequestSignatureFromJSON(json["signature"])
  };
}
function SignRaRequestToJSON(json) {
  return SignRaRequestToJSONTyped(json, false);
}
function SignRaRequestToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    signature: SignRaRequestSignatureToJSON(value["signature"])
  };
}

// src/rentals-api/models/SignRaResponse.ts
function instanceOfSignRaResponse(value) {
  return true;
}
function SignRaResponseFromJSON(json) {
  return SignRaResponseFromJSONTyped(json, false);
}
function SignRaResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    id: json["id"] == null ? void 0 : json["id"]
  };
}
function SignRaResponseToJSON(json) {
  return SignRaResponseToJSONTyped(json, false);
}
function SignRaResponseToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    id: value["id"]
  };
}

// src/rentals-api/models/SignUploadResponse.ts
function instanceOfSignUploadResponse(value) {
  return true;
}
function SignUploadResponseFromJSON(json) {
  return SignUploadResponseFromJSONTyped(json, false);
}
function SignUploadResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    signature: json["signature"] == null ? void 0 : json["signature"]
  };
}
function SignUploadResponseToJSON(json) {
  return SignUploadResponseToJSONTyped(json, false);
}
function SignUploadResponseToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    signature: value["signature"]
  };
}

// src/rentals-api/models/SitesSettingsPickUpServiceHoursMessages.ts
function instanceOfSitesSettingsPickUpServiceHoursMessages(value) {
  return true;
}
function SitesSettingsPickUpServiceHoursMessagesFromJSON(json) {
  return SitesSettingsPickUpServiceHoursMessagesFromJSONTyped(json, false);
}
function SitesSettingsPickUpServiceHoursMessagesFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    startDow: json["startDow"] == null ? void 0 : json["startDow"],
    startTime: json["startTime"] == null ? void 0 : json["startTime"],
    endDow: json["endDow"] == null ? void 0 : json["endDow"],
    endTime: json["endTime"] == null ? void 0 : json["endTime"],
    message: json["message"] == null ? void 0 : json["message"]
  };
}
function SitesSettingsPickUpServiceHoursMessagesToJSON(json) {
  return SitesSettingsPickUpServiceHoursMessagesToJSONTyped(json, false);
}
function SitesSettingsPickUpServiceHoursMessagesToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    startDow: value["startDow"],
    startTime: value["startTime"],
    endDow: value["endDow"],
    endTime: value["endTime"],
    message: value["message"]
  };
}

// src/rentals-api/models/SitesSettingsServiceHours.ts
function instanceOfSitesSettingsServiceHours(value) {
  return true;
}
function SitesSettingsServiceHoursFromJSON(json) {
  return SitesSettingsServiceHoursFromJSONTyped(json, false);
}
function SitesSettingsServiceHoursFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    dow: json["dow"] == null ? void 0 : json["dow"],
    open: json["open"] == null ? void 0 : json["open"],
    close: json["close"] == null ? void 0 : json["close"]
  };
}
function SitesSettingsServiceHoursToJSON(json) {
  return SitesSettingsServiceHoursToJSONTyped(json, false);
}
function SitesSettingsServiceHoursToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    dow: value["dow"],
    open: value["open"],
    close: value["close"]
  };
}

// src/rentals-api/models/SiteFleetTypeSettings.ts
function instanceOfSiteFleetTypeSettings(value) {
  return true;
}
function SiteFleetTypeSettingsFromJSON(json) {
  return SiteFleetTypeSettingsFromJSONTyped(json, false);
}
function SiteFleetTypeSettingsFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    fleetTypeSlug: json["fleetTypeSlug"] == null ? void 0 : json["fleetTypeSlug"],
    afterHoursPickUp: json["afterHoursPickUp"] == null ? void 0 : json["afterHoursPickUp"],
    afterHoursDropOff: json["afterHoursDropOff"] == null ? void 0 : json["afterHoursDropOff"],
    serviceHours: json["serviceHours"] == null ? void 0 : json["serviceHours"].map(SitesSettingsServiceHoursFromJSON),
    pickUpServiceHours: json["pickUpServiceHours"] == null ? void 0 : json["pickUpServiceHours"].map(SitesSettingsServiceHoursFromJSON),
    dropOffServiceHours: json["dropOffServiceHours"] == null ? void 0 : json["dropOffServiceHours"].map(SitesSettingsServiceHoursFromJSON),
    pickUpServiceHoursMessages: json["pickUpServiceHoursMessages"] == null ? void 0 : json["pickUpServiceHoursMessages"].map(SitesSettingsPickUpServiceHoursMessagesFromJSON),
    dropOffServiceHoursMessages: json["dropOffServiceHoursMessages"] == null ? void 0 : json["dropOffServiceHoursMessages"].map(SitesSettingsPickUpServiceHoursMessagesFromJSON)
  };
}
function SiteFleetTypeSettingsToJSON(json) {
  return SiteFleetTypeSettingsToJSONTyped(json, false);
}
function SiteFleetTypeSettingsToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    fleetTypeSlug: value["fleetTypeSlug"],
    afterHoursPickUp: value["afterHoursPickUp"],
    afterHoursDropOff: value["afterHoursDropOff"],
    serviceHours: value["serviceHours"] == null ? void 0 : value["serviceHours"].map(SitesSettingsServiceHoursToJSON),
    pickUpServiceHours: value["pickUpServiceHours"] == null ? void 0 : value["pickUpServiceHours"].map(SitesSettingsServiceHoursToJSON),
    dropOffServiceHours: value["dropOffServiceHours"] == null ? void 0 : value["dropOffServiceHours"].map(SitesSettingsServiceHoursToJSON),
    pickUpServiceHoursMessages: value["pickUpServiceHoursMessages"] == null ? void 0 : value["pickUpServiceHoursMessages"].map(SitesSettingsPickUpServiceHoursMessagesToJSON),
    dropOffServiceHoursMessages: value["dropOffServiceHoursMessages"] == null ? void 0 : value["dropOffServiceHoursMessages"].map(SitesSettingsPickUpServiceHoursMessagesToJSON)
  };
}

// src/rentals-api/models/SiteServiceHoursMessage.ts
function instanceOfSiteServiceHoursMessage(value) {
  return true;
}
function SiteServiceHoursMessageFromJSON(json) {
  return SiteServiceHoursMessageFromJSONTyped(json, false);
}
function SiteServiceHoursMessageFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    startDow: json["startDow"] == null ? void 0 : json["startDow"],
    startTime: json["startTime"] == null ? void 0 : json["startTime"],
    endDow: json["endDow"] == null ? void 0 : json["endDow"],
    endTime: json["endTime"] == null ? void 0 : json["endTime"],
    message: json["message"] == null ? void 0 : json["message"]
  };
}
function SiteServiceHoursMessageToJSON(json) {
  return SiteServiceHoursMessageToJSONTyped(json, false);
}
function SiteServiceHoursMessageToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    startDow: value["startDow"],
    startTime: value["startTime"],
    endDow: value["endDow"],
    endTime: value["endTime"],
    message: value["message"]
  };
}

// src/rentals-api/models/SitesSettingsHirePeriods.ts
function instanceOfSitesSettingsHirePeriods(value) {
  return true;
}
function SitesSettingsHirePeriodsFromJSON(json) {
  return SitesSettingsHirePeriodsFromJSONTyped(json, false);
}
function SitesSettingsHirePeriodsFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    fleetTypeSlug: json["fleetTypeSlug"] == null ? void 0 : json["fleetTypeSlug"],
    pickUpSiteCode: json["pickUpSiteCode"] == null ? void 0 : json["pickUpSiteCode"],
    dropOffSiteCode: json["dropOffSiteCode"] == null ? void 0 : json["dropOffSiteCode"],
    startDate: json["startDate"] == null ? void 0 : stringToDate2(json["startDate"]),
    endDate: json["endDate"] == null ? void 0 : stringToDate2(json["endDate"]),
    minHireDays: json["minHireDays"] == null ? void 0 : json["minHireDays"]
  };
}
function SitesSettingsHirePeriodsToJSON(json) {
  return SitesSettingsHirePeriodsToJSONTyped(json, false);
}
function SitesSettingsHirePeriodsToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    fleetTypeSlug: value["fleetTypeSlug"],
    pickUpSiteCode: value["pickUpSiteCode"],
    dropOffSiteCode: value["dropOffSiteCode"],
    startDate: value["startDate"] == null ? void 0 : dateToString2(value["startDate"]),
    endDate: value["endDate"] == null ? void 0 : dateToString2(value["endDate"]),
    minHireDays: value["minHireDays"]
  };
}

// src/rentals-api/models/SitesSettingsDefaultTimes.ts
function instanceOfSitesSettingsDefaultTimes(value) {
  return true;
}
function SitesSettingsDefaultTimesFromJSON(json) {
  return SitesSettingsDefaultTimesFromJSONTyped(json, false);
}
function SitesSettingsDefaultTimesFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    pickUp: json["pickUp"] == null ? void 0 : json["pickUp"],
    dropOff: json["dropOff"] == null ? void 0 : json["dropOff"],
    dow: json["dow"] == null ? void 0 : json["dow"]
  };
}
function SitesSettingsDefaultTimesToJSON(json) {
  return SitesSettingsDefaultTimesToJSONTyped(json, false);
}
function SitesSettingsDefaultTimesToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    pickUp: value["pickUp"],
    dropOff: value["dropOff"],
    dow: value["dow"]
  };
}

// src/rentals-api/models/SitesSettingsFleetTypeSettings.ts
function instanceOfSitesSettingsFleetTypeSettings(value) {
  return true;
}
function SitesSettingsFleetTypeSettingsFromJSON(json) {
  return SitesSettingsFleetTypeSettingsFromJSONTyped(json, false);
}
function SitesSettingsFleetTypeSettingsFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    fleetTypeSlug: json["fleetTypeSlug"] == null ? void 0 : json["fleetTypeSlug"],
    afterHoursPickUp: json["afterHoursPickUp"] == null ? void 0 : json["afterHoursPickUp"],
    afterHoursDropOff: json["afterHoursDropOff"] == null ? void 0 : json["afterHoursDropOff"],
    serviceHours: json["serviceHours"] == null ? void 0 : json["serviceHours"].map(SitesSettingsServiceHoursFromJSON),
    pickUpServiceHours: json["pickUpServiceHours"] == null ? void 0 : json["pickUpServiceHours"].map(SitesSettingsServiceHoursFromJSON),
    dropOffServiceHours: json["dropOffServiceHours"] == null ? void 0 : json["dropOffServiceHours"].map(SitesSettingsServiceHoursFromJSON),
    pickUpServiceHoursMessages: json["pickUpServiceHoursMessages"] == null ? void 0 : json["pickUpServiceHoursMessages"].map(SitesSettingsPickUpServiceHoursMessagesFromJSON),
    dropOffServiceHoursMessages: json["dropOffServiceHoursMessages"] == null ? void 0 : json["dropOffServiceHoursMessages"].map(SitesSettingsPickUpServiceHoursMessagesFromJSON)
  };
}
function SitesSettingsFleetTypeSettingsToJSON(json) {
  return SitesSettingsFleetTypeSettingsToJSONTyped(json, false);
}
function SitesSettingsFleetTypeSettingsToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    fleetTypeSlug: value["fleetTypeSlug"],
    afterHoursPickUp: value["afterHoursPickUp"],
    afterHoursDropOff: value["afterHoursDropOff"],
    serviceHours: value["serviceHours"] == null ? void 0 : value["serviceHours"].map(SitesSettingsServiceHoursToJSON),
    pickUpServiceHours: value["pickUpServiceHours"] == null ? void 0 : value["pickUpServiceHours"].map(SitesSettingsServiceHoursToJSON),
    dropOffServiceHours: value["dropOffServiceHours"] == null ? void 0 : value["dropOffServiceHours"].map(SitesSettingsServiceHoursToJSON),
    pickUpServiceHoursMessages: value["pickUpServiceHoursMessages"] == null ? void 0 : value["pickUpServiceHoursMessages"].map(SitesSettingsPickUpServiceHoursMessagesToJSON),
    dropOffServiceHoursMessages: value["dropOffServiceHoursMessages"] == null ? void 0 : value["dropOffServiceHoursMessages"].map(SitesSettingsPickUpServiceHoursMessagesToJSON)
  };
}

// src/rentals-api/models/SitesSettingsHolidays.ts
function instanceOfSitesSettingsHolidays(value) {
  return true;
}
function SitesSettingsHolidaysFromJSON(json) {
  return SitesSettingsHolidaysFromJSONTyped(json, false);
}
function SitesSettingsHolidaysFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    name: json["name"] == null ? void 0 : json["name"],
    start: json["start"] == null ? void 0 : stringToDate2(json["start"]),
    end: json["end"] == null ? void 0 : stringToDate2(json["end"]),
    dow: json["dow"] == null ? void 0 : json["dow"]
  };
}
function SitesSettingsHolidaysToJSON(json) {
  return SitesSettingsHolidaysToJSONTyped(json, false);
}
function SitesSettingsHolidaysToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    name: value["name"],
    start: value["start"] == null ? void 0 : dateToString2(value["start"]),
    end: value["end"] == null ? void 0 : dateToString2(value["end"]),
    dow: value["dow"]
  };
}

// src/rentals-api/models/SitesSettingsProhibitedPickUpSites.ts
function instanceOfSitesSettingsProhibitedPickUpSites(value) {
  return true;
}
function SitesSettingsProhibitedPickUpSitesFromJSON(json) {
  return SitesSettingsProhibitedPickUpSitesFromJSONTyped(json, false);
}
function SitesSettingsProhibitedPickUpSitesFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    code: json["code"] == null ? void 0 : json["code"]
  };
}
function SitesSettingsProhibitedPickUpSitesToJSON(json) {
  return SitesSettingsProhibitedPickUpSitesToJSONTyped(json, false);
}
function SitesSettingsProhibitedPickUpSitesToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    code: value["code"]
  };
}

// src/rentals-api/models/SitesSettingsSites.ts
function instanceOfSitesSettingsSites(value) {
  return true;
}
function SitesSettingsSitesFromJSON(json) {
  return SitesSettingsSitesFromJSONTyped(json, false);
}
function SitesSettingsSitesFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    siteName: json["siteName"] == null ? void 0 : json["siteName"],
    siteCode: json["siteCode"] == null ? void 0 : json["siteCode"],
    fleetTypeSettings: json["fleetTypeSettings"] == null ? void 0 : json["fleetTypeSettings"].map(SitesSettingsFleetTypeSettingsFromJSON),
    holidays: json["holidays"] == null ? void 0 : json["holidays"].map(SitesSettingsHolidaysFromJSON),
    defaultTimes: json["defaultTimes"] == null ? void 0 : json["defaultTimes"].map(SitesSettingsDefaultTimesFromJSON),
    prohibitedPickUpSites: json["prohibitedPickUpSites"] == null ? void 0 : json["prohibitedPickUpSites"].map(SitesSettingsProhibitedPickUpSitesFromJSON),
    prohibitedDropOffSites: json["prohibitedDropOffSites"] == null ? void 0 : json["prohibitedDropOffSites"].map(SitesSettingsProhibitedPickUpSitesFromJSON)
  };
}
function SitesSettingsSitesToJSON(json) {
  return SitesSettingsSitesToJSONTyped(json, false);
}
function SitesSettingsSitesToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    siteName: value["siteName"],
    siteCode: value["siteCode"],
    fleetTypeSettings: value["fleetTypeSettings"] == null ? void 0 : value["fleetTypeSettings"].map(SitesSettingsFleetTypeSettingsToJSON),
    holidays: value["holidays"] == null ? void 0 : value["holidays"].map(SitesSettingsHolidaysToJSON),
    defaultTimes: value["defaultTimes"] == null ? void 0 : value["defaultTimes"].map(SitesSettingsDefaultTimesToJSON),
    prohibitedPickUpSites: value["prohibitedPickUpSites"] == null ? void 0 : value["prohibitedPickUpSites"].map(SitesSettingsProhibitedPickUpSitesToJSON),
    prohibitedDropOffSites: value["prohibitedDropOffSites"] == null ? void 0 : value["prohibitedDropOffSites"].map(SitesSettingsProhibitedPickUpSitesToJSON)
  };
}

// src/rentals-api/models/SiteSettingsResponse.ts
function instanceOfSiteSettingsResponse(value) {
  return true;
}
function SiteSettingsResponseFromJSON(json) {
  return SiteSettingsResponseFromJSONTyped(json, false);
}
function SiteSettingsResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    hirePeriods: json["hirePeriods"] == null ? void 0 : json["hirePeriods"].map(SitesSettingsHirePeriodsFromJSON),
    sites: json["sites"] == null ? void 0 : json["sites"].map(SitesSettingsSitesFromJSON)
  };
}
function SiteSettingsResponseToJSON(json) {
  return SiteSettingsResponseToJSONTyped(json, false);
}
function SiteSettingsResponseToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    hirePeriods: value["hirePeriods"] == null ? void 0 : value["hirePeriods"].map(SitesSettingsHirePeriodsToJSON),
    sites: value["sites"] == null ? void 0 : value["sites"].map(SitesSettingsSitesToJSON)
  };
}

// src/rentals-api/models/SitesSettings.ts
function instanceOfSitesSettings(value) {
  return true;
}
function SitesSettingsFromJSON(json) {
  return SitesSettingsFromJSONTyped(json, false);
}
function SitesSettingsFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    hirePeriods: json["hirePeriods"] == null ? void 0 : json["hirePeriods"].map(SitesSettingsHirePeriodsFromJSON),
    sites: json["sites"] == null ? void 0 : json["sites"].map(SitesSettingsSitesFromJSON)
  };
}
function SitesSettingsToJSON(json) {
  return SitesSettingsToJSONTyped(json, false);
}
function SitesSettingsToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    hirePeriods: value["hirePeriods"] == null ? void 0 : value["hirePeriods"].map(SitesSettingsHirePeriodsToJSON),
    sites: value["sites"] == null ? void 0 : value["sites"].map(SitesSettingsSitesToJSON)
  };
}

// src/rentals-api/models/TollRequest.ts
function instanceOfTollRequest(value) {
  if (!("accountKey" in value) || value["accountKey"] === void 0) return false;
  return true;
}
function TollRequestFromJSON(json) {
  return TollRequestFromJSONTyped(json, false);
}
function TollRequestFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    accountKey: json["accountKey"]
  };
}
function TollRequestToJSON(json) {
  return TollRequestToJSONTyped(json, false);
}
function TollRequestToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    accountKey: value["accountKey"]
  };
}

// src/rentals-api/models/UpdateQuoteResponse.ts
var UpdateQuoteResponseResponseTypeEnum = {
  Success: "Success",
  Error: "Error",
  ValidationError: "Validation Error",
  UnexpectedError: "Unexpected Error"
};
function instanceOfUpdateQuoteResponse(value) {
  return true;
}
function UpdateQuoteResponseFromJSON(json) {
  return UpdateQuoteResponseFromJSONTyped(json, false);
}
function UpdateQuoteResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    responseType: json["ResponseType"] == null ? void 0 : json["ResponseType"],
    message: json["Message"] == null ? void 0 : json["Message"],
    data: json["Data"] == null ? void 0 : QuoteWithProductOptionsFromJSON(json["Data"])
  };
}
function UpdateQuoteResponseToJSON(json) {
  return UpdateQuoteResponseToJSONTyped(json, false);
}
function UpdateQuoteResponseToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    ResponseType: value["responseType"],
    Message: value["message"],
    Data: QuoteWithProductOptionsToJSON(value["data"])
  };
}

// src/rentals-api/apis/AvailabilityApi.ts
var AvailabilityApi = class extends BaseAPI2 {
  /**
   * Returns a list of fleet types and locations the fleet types is available from
   * List available fleet types
   */
  async getAllFleetTypesRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    if (requestParameters["languageCode"] != null) {
      queryParameters["languageCode"] = requestParameters["languageCode"];
    }
    const headerParameters = {};
    let urlPath = `/api/Fleet/1.0/GetAllFleetTypes`;
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse2(response, (jsonValue) => GetAllFleetTypesResponseFromJSON(jsonValue));
  }
  /**
   * Returns a list of fleet types and locations the fleet types is available from
   * List available fleet types
   */
  async getAllFleetTypes(languageCode, initOverrides) {
    const response = await this.getAllFleetTypesRaw({ languageCode }, initOverrides);
    return await response.value();
  }
  /**
   * real time lookup on current pricing and availability for given locations and dates
   * Get availability and pricing
   */
  async getFleetTypeCategoriesRaw(requestParameters, initOverrides) {
    if (requestParameters["accountKey"] == null) {
      throw new RequiredError2(
        "accountKey",
        'Required parameter "accountKey" was null or undefined when calling getFleetTypeCategories().'
      );
    }
    if (requestParameters["typeId"] == null) {
      throw new RequiredError2("typeId", 'Required parameter "typeId" was null or undefined when calling getFleetTypeCategories().');
    }
    if (requestParameters["pickUpLocation"] == null) {
      throw new RequiredError2(
        "pickUpLocation",
        'Required parameter "pickUpLocation" was null or undefined when calling getFleetTypeCategories().'
      );
    }
    if (requestParameters["dropOffLocation"] == null) {
      throw new RequiredError2(
        "dropOffLocation",
        'Required parameter "dropOffLocation" was null or undefined when calling getFleetTypeCategories().'
      );
    }
    if (requestParameters["pickUpDateTime"] == null) {
      throw new RequiredError2(
        "pickUpDateTime",
        'Required parameter "pickUpDateTime" was null or undefined when calling getFleetTypeCategories().'
      );
    }
    if (requestParameters["dropOffDateTime"] == null) {
      throw new RequiredError2(
        "dropOffDateTime",
        'Required parameter "dropOffDateTime" was null or undefined when calling getFleetTypeCategories().'
      );
    }
    const queryParameters = {};
    if (requestParameters["typeId"] != null) {
      queryParameters["typeId"] = requestParameters["typeId"];
    }
    if (requestParameters["pickUpLocation"] != null) {
      queryParameters["pickUpLocation"] = requestParameters["pickUpLocation"];
    }
    if (requestParameters["dropOffLocation"] != null) {
      queryParameters["dropOffLocation"] = requestParameters["dropOffLocation"];
    }
    if (requestParameters["pickUpDateTime"] != null) {
      queryParameters["pickUpDateTime"] = requestParameters["pickUpDateTime"].toISOString();
    }
    if (requestParameters["dropOffDateTime"] != null) {
      queryParameters["dropOffDateTime"] = requestParameters["dropOffDateTime"].toISOString();
    }
    if (requestParameters["driverAge"] != null) {
      queryParameters["driverAge"] = requestParameters["driverAge"];
    }
    if (requestParameters["campaignCode"] != null) {
      queryParameters["campaignCode"] = requestParameters["campaignCode"];
    }
    if (requestParameters["mergeSurchargesFees"] != null) {
      queryParameters["mergeSurchargesFees"] = requestParameters["mergeSurchargesFees"];
    }
    const headerParameters = {};
    let urlPath = `/api/Account/{accountKey}/2.0/GetFleetTypeCategories`;
    urlPath = urlPath.replace(`{${"accountKey"}}`, encodeURIComponent(String(requestParameters["accountKey"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "POST",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse2(response, (jsonValue) => GetFleetTypeCategoriesResponseFromJSON(jsonValue));
  }
  /**
   * real time lookup on current pricing and availability for given locations and dates
   * Get availability and pricing
   */
  async getFleetTypeCategories(accountKey, typeId, pickUpLocation, dropOffLocation, pickUpDateTime, dropOffDateTime, driverAge, campaignCode, mergeSurchargesFees, initOverrides) {
    const response = await this.getFleetTypeCategoriesRaw(
      {
        accountKey,
        typeId,
        pickUpLocation,
        dropOffLocation,
        pickUpDateTime,
        dropOffDateTime,
        driverAge,
        campaignCode,
        mergeSurchargesFees
      },
      initOverrides
    );
    return await response.value();
  }
};
var GetFleetTypeCategoriesMergeSurchargesFeesEnum = {
  True: "true",
  False: "false"
};

// src/rentals-api/apis/CatalogApi.ts
var CatalogApi2 = class extends BaseAPI2 {
  /**
   * Get rental vehicle products
   */
  async getRentalCatalogRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    if (requestParameters["extended"] != null) {
      queryParameters["extended"] = requestParameters["extended"];
    }
    const headerParameters = {};
    let urlPath = `/api/rental-catalog`;
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse2(response, (jsonValue) => RentalCatalogResponseFromJSON2(jsonValue));
  }
  /**
   * Get rental vehicle products
   */
  async getRentalCatalog(extended, initOverrides) {
    const response = await this.getRentalCatalogRaw({ extended }, initOverrides);
    return await response.value();
  }
};

// src/rentals-api/apis/DealsApi.ts
var DealsApi = class extends BaseAPI2 {
  /**
   * Get deal by id
   */
  async getDealByIdRaw(requestParameters, initOverrides) {
    if (requestParameters["accountId"] == null) {
      throw new RequiredError2("accountId", 'Required parameter "accountId" was null or undefined when calling getDealById().');
    }
    if (requestParameters["id"] == null) {
      throw new RequiredError2("id", 'Required parameter "id" was null or undefined when calling getDealById().');
    }
    const queryParameters = {};
    if (requestParameters["accountId"] != null) {
      queryParameters["accountId"] = requestParameters["accountId"];
    }
    const headerParameters = {};
    let urlPath = `/api/deals/{id}`;
    urlPath = urlPath.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters["id"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse2(response, (jsonValue) => DealFromJSON(jsonValue));
  }
  /**
   * Get deal by id
   */
  async getDealById(accountId, id, initOverrides) {
    const response = await this.getDealByIdRaw({ accountId, id }, initOverrides);
    return await response.value();
  }
  /**
   * Find active deals.
   * List deals
   */
  async listDealsRaw(requestParameters, initOverrides) {
    if (requestParameters["accountId"] == null) {
      throw new RequiredError2("accountId", 'Required parameter "accountId" was null or undefined when calling listDeals().');
    }
    const queryParameters = {};
    if (requestParameters["accountId"] != null) {
      queryParameters["accountId"] = requestParameters["accountId"];
    }
    if (requestParameters["tags"] != null) {
      queryParameters["tags"] = requestParameters["tags"];
    }
    if (requestParameters["region"] != null) {
      queryParameters["region"] = requestParameters["region"];
    }
    if (requestParameters["code"] != null) {
      queryParameters["code"] = requestParameters["code"];
    }
    if (requestParameters["type"] != null) {
      queryParameters["type"] = requestParameters["type"];
    }
    const headerParameters = {};
    let urlPath = `/api/deals`;
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse2(response, (jsonValue) => ListDealsResponseFromJSON(jsonValue));
  }
  /**
   * Find active deals.
   * List deals
   */
  async listDeals(accountId, tags, region, code, type, initOverrides) {
    const response = await this.listDealsRaw({ accountId, tags, region, code, type }, initOverrides);
    return await response.value();
  }
};

// src/rentals-api/apis/PricingApi.ts
var PricingApi = class extends BaseAPI2 {
  /**
   * Query dynamic pricing for products. Different products may require different query parameters depending how they calculate their pricing.
   * Query product pricing
   */
  async queryPricingRaw(requestParameters, initOverrides) {
    if (requestParameters["productId"] == null) {
      throw new RequiredError2("productId", 'Required parameter "productId" was null or undefined when calling queryPricing().');
    }
    const queryParameters = {};
    if (requestParameters["productId"] != null) {
      queryParameters["productId"] = requestParameters["productId"];
    }
    const headerParameters = {};
    let urlPath = `/api/pricing`;
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse2(response, (jsonValue) => PricingResponseFromJSON(jsonValue));
  }
  /**
   * Query dynamic pricing for products. Different products may require different query parameters depending how they calculate their pricing.
   * Query product pricing
   */
  async queryPricing(productId, initOverrides) {
    const response = await this.queryPricingRaw({ productId }, initOverrides);
    return await response.value();
  }
};

// src/rentals-api/apis/ReservationsApi.ts
var ReservationsApi2 = class extends BaseAPI2 {
  /**
   * Cancel a reservation
   */
  async cancelReservationRaw(requestParameters, initOverrides) {
    if (requestParameters["accountKey"] == null) {
      throw new RequiredError2("accountKey", 'Required parameter "accountKey" was null or undefined when calling cancelReservation().');
    }
    if (requestParameters["reservationNumber"] == null) {
      throw new RequiredError2(
        "reservationNumber",
        'Required parameter "reservationNumber" was null or undefined when calling cancelReservation().'
      );
    }
    const queryParameters = {};
    if (requestParameters["reservationNumber"] != null) {
      queryParameters["reservationNumber"] = requestParameters["reservationNumber"];
    }
    if (requestParameters["onlineLogin"] != null) {
      queryParameters["onlineLogin"] = requestParameters["onlineLogin"];
    }
    const headerParameters = {};
    let urlPath = `/api/Account/{accountKey}/1.0/CancelReservation`;
    urlPath = urlPath.replace(`{${"accountKey"}}`, encodeURIComponent(String(requestParameters["accountKey"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "POST",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new VoidApiResponse(response);
  }
  /**
   * Cancel a reservation
   */
  async cancelReservation(accountKey, reservationNumber, onlineLogin, initOverrides) {
    await this.cancelReservationRaw({ accountKey, reservationNumber, onlineLogin }, initOverrides);
  }
  /**
   * Confirming a quote coverts the quote to a reservation
   * Confirm a quote
   */
  async confirmQuoteRaw(requestParameters, initOverrides) {
    if (requestParameters["accountKey"] == null) {
      throw new RequiredError2("accountKey", 'Required parameter "accountKey" was null or undefined when calling confirmQuote().');
    }
    if (requestParameters["quoteId"] == null) {
      throw new RequiredError2("quoteId", 'Required parameter "quoteId" was null or undefined when calling confirmQuote().');
    }
    const queryParameters = {};
    if (requestParameters["quoteId"] != null) {
      queryParameters["quoteId"] = requestParameters["quoteId"];
    }
    if (requestParameters["country"] != null) {
      queryParameters["country"] = requestParameters["country"];
    }
    if (requestParameters["dateOfBirth"] != null) {
      queryParameters["dateOfBirth"] = requestParameters["dateOfBirth"].toISOString().substring(0, 10);
    }
    if (requestParameters["licenseNumber"] != null) {
      queryParameters["licenseNumber"] = requestParameters["licenseNumber"];
    }
    if (requestParameters["licenseCountry"] != null) {
      queryParameters["licenseCountry"] = requestParameters["licenseCountry"];
    }
    if (requestParameters["licenseExpiry"] != null) {
      queryParameters["licenseExpiry"] = requestParameters["licenseExpiry"].toISOString().substring(0, 10);
    }
    if (requestParameters["arrivalFlight"] != null) {
      queryParameters["arrivalFlight"] = requestParameters["arrivalFlight"];
    }
    if (requestParameters["agentVoucherReference"] != null) {
      queryParameters["agentVoucherReference"] = requestParameters["agentVoucherReference"];
    }
    if (requestParameters["deposit"] != null) {
      queryParameters["deposit"] = requestParameters["deposit"];
    }
    if (requestParameters["marketingAllowed"] != null) {
      queryParameters["marketingAllowed"] = requestParameters["marketingAllowed"];
    }
    const headerParameters = {};
    let urlPath = `/api/Account/{accountKey}/1.0/ConfirmQuote`;
    urlPath = urlPath.replace(`{${"accountKey"}}`, encodeURIComponent(String(requestParameters["accountKey"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "POST",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse2(response, (jsonValue) => ConfirmQuoteResponseFromJSON(jsonValue));
  }
  /**
   * Confirming a quote coverts the quote to a reservation
   * Confirm a quote
   */
  async confirmQuote(accountKey, quoteId, country, dateOfBirth, licenseNumber, licenseCountry, licenseExpiry, arrivalFlight, agentVoucherReference, deposit, marketingAllowed, initOverrides) {
    const response = await this.confirmQuoteRaw(
      {
        accountKey,
        quoteId,
        country,
        dateOfBirth,
        licenseNumber,
        licenseCountry,
        licenseExpiry,
        arrivalFlight,
        agentVoucherReference,
        deposit,
        marketingAllowed
      },
      initOverrides
    );
    return await response.value();
  }
  /**
   * Confirm reservation
   */
  async confirmReservationRaw(requestParameters, initOverrides) {
    if (requestParameters["accountId"] == null) {
      throw new RequiredError2("accountId", 'Required parameter "accountId" was null or undefined when calling confirmReservation().');
    }
    if (requestParameters["id"] == null) {
      throw new RequiredError2("id", 'Required parameter "id" was null or undefined when calling confirmReservation().');
    }
    const queryParameters = {};
    if (requestParameters["accountId"] != null) {
      queryParameters["accountId"] = requestParameters["accountId"];
    }
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    let urlPath = `/api/rental-reservations/{id}/confirm`;
    urlPath = urlPath.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters["id"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "POST",
        headers: headerParameters,
        query: queryParameters,
        body: IdConfirmBodyToJSON(requestParameters["idConfirmBody"])
      },
      initOverrides
    );
    return new JSONApiResponse2(response, (jsonValue) => ConfirmReservationResponseFromJSON(jsonValue));
  }
  /**
   * Confirm reservation
   */
  async confirmReservation(accountId, id, idConfirmBody, initOverrides) {
    const response = await this.confirmReservationRaw({ accountId, id, idConfirmBody }, initOverrides);
    return await response.value();
  }
  /**
   * Creates a quote for the given fleet category, dates and locations. Replies with additional items and products that can be added to the quote
   * Create a quote
   */
  async createQuoteRaw(requestParameters, initOverrides) {
    if (requestParameters["accountKey"] == null) {
      throw new RequiredError2("accountKey", 'Required parameter "accountKey" was null or undefined when calling createQuote().');
    }
    if (requestParameters["firstName"] == null) {
      throw new RequiredError2("firstName", 'Required parameter "firstName" was null or undefined when calling createQuote().');
    }
    if (requestParameters["lastName"] == null) {
      throw new RequiredError2("lastName", 'Required parameter "lastName" was null or undefined when calling createQuote().');
    }
    if (requestParameters["email"] == null) {
      throw new RequiredError2("email", 'Required parameter "email" was null or undefined when calling createQuote().');
    }
    if (requestParameters["categoryCode"] == null) {
      throw new RequiredError2("categoryCode", 'Required parameter "categoryCode" was null or undefined when calling createQuote().');
    }
    if (requestParameters["pickUpLocationCode"] == null) {
      throw new RequiredError2(
        "pickUpLocationCode",
        'Required parameter "pickUpLocationCode" was null or undefined when calling createQuote().'
      );
    }
    if (requestParameters["dropOffLocationCode"] == null) {
      throw new RequiredError2(
        "dropOffLocationCode",
        'Required parameter "dropOffLocationCode" was null or undefined when calling createQuote().'
      );
    }
    if (requestParameters["pickUpDateTime"] == null) {
      throw new RequiredError2(
        "pickUpDateTime",
        'Required parameter "pickUpDateTime" was null or undefined when calling createQuote().'
      );
    }
    if (requestParameters["dropOffDateTime"] == null) {
      throw new RequiredError2(
        "dropOffDateTime",
        'Required parameter "dropOffDateTime" was null or undefined when calling createQuote().'
      );
    }
    const queryParameters = {};
    if (requestParameters["firstName"] != null) {
      queryParameters["firstName"] = requestParameters["firstName"];
    }
    if (requestParameters["lastName"] != null) {
      queryParameters["lastName"] = requestParameters["lastName"];
    }
    if (requestParameters["email"] != null) {
      queryParameters["email"] = requestParameters["email"];
    }
    if (requestParameters["categoryCode"] != null) {
      queryParameters["categoryCode"] = requestParameters["categoryCode"];
    }
    if (requestParameters["pickUpLocationCode"] != null) {
      queryParameters["pickUpLocationCode"] = requestParameters["pickUpLocationCode"];
    }
    if (requestParameters["dropOffLocationCode"] != null) {
      queryParameters["dropOffLocationCode"] = requestParameters["dropOffLocationCode"];
    }
    if (requestParameters["pickUpDateTime"] != null) {
      queryParameters["pickUpDateTime"] = requestParameters["pickUpDateTime"].toISOString();
    }
    if (requestParameters["dropOffDateTime"] != null) {
      queryParameters["dropOffDateTime"] = requestParameters["dropOffDateTime"].toISOString();
    }
    if (requestParameters["driverAge"] != null) {
      queryParameters["driverAge"] = requestParameters["driverAge"];
    }
    if (requestParameters["mobilePhone"] != null) {
      queryParameters["mobilePhone"] = requestParameters["mobilePhone"];
    }
    if (requestParameters["campaignCode"] != null) {
      queryParameters["campaignCode"] = requestParameters["campaignCode"];
    }
    if (requestParameters["flightNumber"] != null) {
      queryParameters["flightNumber"] = requestParameters["flightNumber"];
    }
    if (requestParameters["languageCode"] != null) {
      queryParameters["languageCode"] = requestParameters["languageCode"];
    }
    if (requestParameters["marketingAllowed"] != null) {
      queryParameters["marketingAllowed"] = requestParameters["marketingAllowed"];
    }
    if (requestParameters["mergeSurchargesFees"] != null) {
      queryParameters["mergeSurchargesFees"] = requestParameters["mergeSurchargesFees"];
    }
    if (requestParameters["agentName"] != null) {
      queryParameters["agentName"] = requestParameters["agentName"];
    }
    if (requestParameters["numberOfPeople"] != null) {
      queryParameters["numberOfPeople"] = requestParameters["numberOfPeople"];
    }
    const headerParameters = {};
    let urlPath = `/api/Account/{accountKey}/2.0/CreateQuote`;
    urlPath = urlPath.replace(`{${"accountKey"}}`, encodeURIComponent(String(requestParameters["accountKey"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "POST",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse2(response, (jsonValue) => CreateQuoteResponseFromJSON(jsonValue));
  }
  /**
   * Creates a quote for the given fleet category, dates and locations. Replies with additional items and products that can be added to the quote
   * Create a quote
   */
  async createQuote(accountKey, firstName, lastName, email, categoryCode, pickUpLocationCode, dropOffLocationCode, pickUpDateTime, dropOffDateTime, driverAge, mobilePhone, campaignCode, flightNumber, languageCode, marketingAllowed, mergeSurchargesFees, agentName, numberOfPeople, initOverrides) {
    const response = await this.createQuoteRaw(
      {
        accountKey,
        firstName,
        lastName,
        email,
        categoryCode,
        pickUpLocationCode,
        dropOffLocationCode,
        pickUpDateTime,
        dropOffDateTime,
        driverAge,
        mobilePhone,
        campaignCode,
        flightNumber,
        languageCode,
        marketingAllowed,
        mergeSurchargesFees,
        agentName,
        numberOfPeople
      },
      initOverrides
    );
    return await response.value();
  }
  /**
   * Get reservation by ref
   */
  async getReservationByRefRaw(requestParameters, initOverrides) {
    if (requestParameters["accountId"] == null) {
      throw new RequiredError2("accountId", 'Required parameter "accountId" was null or undefined when calling getReservationByRef().');
    }
    if (requestParameters["ref"] == null) {
      throw new RequiredError2("ref", 'Required parameter "ref" was null or undefined when calling getReservationByRef().');
    }
    const queryParameters = {};
    if (requestParameters["accountId"] != null) {
      queryParameters["accountId"] = requestParameters["accountId"];
    }
    if (requestParameters["lastName"] != null) {
      queryParameters["lastName"] = requestParameters["lastName"];
    }
    const headerParameters = {};
    let urlPath = `/api/rental-reservations/ref/{ref}`;
    urlPath = urlPath.replace(`{${"ref"}}`, encodeURIComponent(String(requestParameters["ref"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse2(response, (jsonValue) => QuoteWithProductOptionsFromJSON(jsonValue));
  }
  /**
   * Get reservation by ref
   */
  async getReservationByRef(accountId, ref, lastName, initOverrides) {
    const response = await this.getReservationByRefRaw({ accountId, ref, lastName }, initOverrides);
    return await response.value();
  }
  /**
   * Get pricing for adding requested secondary products to a reservaton
   * Get reservation pricing
   */
  async getReservationPricingByRefRaw(requestParameters, initOverrides) {
    if (requestParameters["accountId"] == null) {
      throw new RequiredError2(
        "accountId",
        'Required parameter "accountId" was null or undefined when calling getReservationPricingByRef().'
      );
    }
    if (requestParameters["ref"] == null) {
      throw new RequiredError2("ref", 'Required parameter "ref" was null or undefined when calling getReservationPricingByRef().');
    }
    const queryParameters = {};
    if (requestParameters["accountId"] != null) {
      queryParameters["accountId"] = requestParameters["accountId"];
    }
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    let urlPath = `/api/rental-reservations/ref/{ref}/pricing-enquiries`;
    urlPath = urlPath.replace(`{${"ref"}}`, encodeURIComponent(String(requestParameters["ref"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "POST",
        headers: headerParameters,
        query: queryParameters,
        body: requestParameters["secondaryProductMap"].map(SecondaryProductMapToJSON)
      },
      initOverrides
    );
    return new JSONApiResponse2(response, (jsonValue) => ReservationPricingEnquiryResponseFromJSON(jsonValue));
  }
  /**
   * Get pricing for adding requested secondary products to a reservaton
   * Get reservation pricing
   */
  async getReservationPricingByRef(accountId, ref, secondaryProductMap, initOverrides) {
    const response = await this.getReservationPricingByRefRaw(
      { accountId, ref, secondaryProductMap },
      initOverrides
    );
    return await response.value();
  }
  /**
   * List reservations
   */
  async listReservationsRaw(requestParameters, initOverrides) {
    if (requestParameters["accountId"] == null) {
      throw new RequiredError2("accountId", 'Required parameter "accountId" was null or undefined when calling listReservations().');
    }
    const queryParameters = {};
    if (requestParameters["accountId"] != null) {
      queryParameters["accountId"] = requestParameters["accountId"];
    }
    if (requestParameters["query"] != null) {
      queryParameters["query"] = requestParameters["query"];
    }
    if (requestParameters["page"] != null) {
      queryParameters["page"] = requestParameters["page"];
    }
    if (requestParameters["itemsPerPage"] != null) {
      queryParameters["itemsPerPage"] = requestParameters["itemsPerPage"];
    }
    if (requestParameters["sort"] != null) {
      queryParameters["sort"] = requestParameters["sort"];
    }
    const headerParameters = {};
    let urlPath = `/api/rental-reservations`;
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse2(response, (jsonValue) => ListReservationsResponseFromJSON(jsonValue));
  }
  /**
   * List reservations
   */
  async listReservations(accountId, query, page, itemsPerPage, sort, initOverrides) {
    const response = await this.listReservationsRaw(
      { accountId, query, page, itemsPerPage, sort },
      initOverrides
    );
    return await response.value();
  }
  /**
   * Adding optional extras and insurance to a quote.
   * Add additional products to a quote
   */
  async updateQuoteRaw(requestParameters, initOverrides) {
    if (requestParameters["accountKey"] == null) {
      throw new RequiredError2("accountKey", 'Required parameter "accountKey" was null or undefined when calling updateQuote().');
    }
    if (requestParameters["quoteId"] == null) {
      throw new RequiredError2("quoteId", 'Required parameter "quoteId" was null or undefined when calling updateQuote().');
    }
    if (requestParameters["secondaryProductsAndInsuranceMap"] == null) {
      throw new RequiredError2(
        "secondaryProductsAndInsuranceMap",
        'Required parameter "secondaryProductsAndInsuranceMap" was null or undefined when calling updateQuote().'
      );
    }
    const queryParameters = {};
    if (requestParameters["quoteId"] != null) {
      queryParameters["quoteId"] = requestParameters["quoteId"];
    }
    if (requestParameters["secondaryProductsAndInsuranceMap"] != null) {
      queryParameters["secondaryProductsAndInsuranceMap"] = requestParameters["secondaryProductsAndInsuranceMap"];
    }
    if (requestParameters["agentName"] != null) {
      queryParameters["agentName"] = requestParameters["agentName"];
    }
    if (requestParameters["languageCode"] != null) {
      queryParameters["languageCode"] = requestParameters["languageCode"];
    }
    if (requestParameters["numberOfPeople"] != null) {
      queryParameters["numberOfPeople"] = requestParameters["numberOfPeople"];
    }
    const headerParameters = {};
    let urlPath = `/api/Account/{accountKey}/2.0/UpdateQuote`;
    urlPath = urlPath.replace(`{${"accountKey"}}`, encodeURIComponent(String(requestParameters["accountKey"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "POST",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse2(response, (jsonValue) => UpdateQuoteResponseFromJSON(jsonValue));
  }
  /**
   * Adding optional extras and insurance to a quote.
   * Add additional products to a quote
   */
  async updateQuote(accountKey, quoteId, secondaryProductsAndInsuranceMap, agentName, languageCode, numberOfPeople, initOverrides) {
    const response = await this.updateQuoteRaw(
      {
        accountKey,
        quoteId,
        secondaryProductsAndInsuranceMap,
        agentName,
        languageCode,
        numberOfPeople
      },
      initOverrides
    );
    return await response.value();
  }
};
var CreateQuoteMergeSurchargesFeesEnum = {
  True: "true",
  False: "false"
};

// src/rentals-api/apis/SelfServiceApi.ts
var SelfServiceApi2 = class extends BaseAPI2 {
  /**
   * Update reservation
   */
  async checkInRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/self-service/check-in`;
    const response = await this.request(
      {
        path: urlPath,
        method: "POST",
        headers: headerParameters,
        query: queryParameters,
        body: CheckInRequestToJSON(requestParameters["checkInRequest"])
      },
      initOverrides
    );
    return new JSONApiResponse2(response);
  }
  /**
   * Update reservation
   */
  async checkIn(checkInRequest, initOverrides) {
    const response = await this.checkInRaw({ checkInRequest }, initOverrides);
    return await response.value();
  }
  /**
   * Edit reservation
   */
  async editReservationRaw(requestParameters, initOverrides) {
    if (requestParameters["ref"] == null) {
      throw new RequiredError2("ref", 'Required parameter "ref" was null or undefined when calling editReservation().');
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/self-service/reservation/ref/{ref}/edit`;
    urlPath = urlPath.replace(`{${"ref"}}`, encodeURIComponent(String(requestParameters["ref"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "POST",
        headers: headerParameters,
        query: queryParameters,
        body: requestParameters["secondaryProductMap"].map(SecondaryProductMapToJSON)
      },
      initOverrides
    );
    return new JSONApiResponse2(response, (jsonValue) => UpdateQuoteResponseFromJSON(jsonValue));
  }
  /**
   * Edit reservation
   */
  async editReservation(ref, secondaryProductMap, initOverrides) {
    const response = await this.editReservationRaw({ ref, secondaryProductMap }, initOverrides);
    return await response.value();
  }
  /**
   * Get reservation pricing
   */
  async getReservationPricingRaw(requestParameters, initOverrides) {
    if (requestParameters["ref"] == null) {
      throw new RequiredError2("ref", 'Required parameter "ref" was null or undefined when calling getReservationPricing().');
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/self-service/reservation/ref/{ref}/pricing-enquiries`;
    urlPath = urlPath.replace(`{${"ref"}}`, encodeURIComponent(String(requestParameters["ref"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "POST",
        headers: headerParameters,
        query: queryParameters,
        body: requestParameters["secondaryProductMap"].map(SecondaryProductMapToJSON)
      },
      initOverrides
    );
    return new JSONApiResponse2(response, (jsonValue) => ReservationPricingEnquiryResponseFromJSON(jsonValue));
  }
  /**
   * Get reservation pricing
   */
  async getReservationPricing(ref, secondaryProductMap, initOverrides) {
    const response = await this.getReservationPricingRaw({ ref, secondaryProductMap }, initOverrides);
    return await response.value();
  }
  /**
   * List documents for reservation
   */
  async listReservationDocumentsRaw(requestParameters, initOverrides) {
    if (requestParameters["ref"] == null) {
      throw new RequiredError2("ref", 'Required parameter "ref" was null or undefined when calling listReservationDocuments().');
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/self-service/reservation/ref/{ref}/documents`;
    urlPath = urlPath.replace(`{${"ref"}}`, encodeURIComponent(String(requestParameters["ref"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse2(response, (jsonValue) => jsonValue.map(RentalDocumentFromJSON));
  }
  /**
   * List documents for reservation
   */
  async listReservationDocuments(ref, initOverrides) {
    const response = await this.listReservationDocumentsRaw({ ref }, initOverrides);
    return await response.value();
  }
  /**
   * List signatures for reservation
   */
  async listReservationSignaturesRaw(requestParameters, initOverrides) {
    if (requestParameters["ref"] == null) {
      throw new RequiredError2("ref", 'Required parameter "ref" was null or undefined when calling listReservationSignatures().');
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/self-service/reservation/ref/{ref}/signatures`;
    urlPath = urlPath.replace(`{${"ref"}}`, encodeURIComponent(String(requestParameters["ref"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse2(response, (jsonValue) => jsonValue.map(RentalSignatureFromJSON));
  }
  /**
   * List signatures for reservation
   */
  async listReservationSignatures(ref, initOverrides) {
    const response = await this.listReservationSignaturesRaw({ ref }, initOverrides);
    return await response.value();
  }
  /**
   * Get reservation balance
   */
  async selfServiceGetBalanceDueRaw(requestParameters, initOverrides) {
    if (requestParameters["ref"] == null) {
      throw new RequiredError2("ref", 'Required parameter "ref" was null or undefined when calling selfServiceGetBalanceDue().');
    }
    const queryParameters = {};
    const headerParameters = {};
    let urlPath = `/api/self-service/reservation/ref/{ref}/balance-due`;
    urlPath = urlPath.replace(`{${"ref"}}`, encodeURIComponent(String(requestParameters["ref"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse2(response, (jsonValue) => BalanceDueResponseFromJSON(jsonValue));
  }
  /**
   * Get reservation balance
   */
  async selfServiceGetBalanceDue(ref, initOverrides) {
    const response = await this.selfServiceGetBalanceDueRaw({ ref }, initOverrides);
    return await response.value();
  }
  /**
   * Get reservation
   */
  async selfServiceGetReservationRaw(requestParameters, initOverrides) {
    if (requestParameters["ref"] == null) {
      throw new RequiredError2("ref", 'Required parameter "ref" was null or undefined when calling selfServiceGetReservation().');
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/self-service/reservation/ref/{ref}`;
    urlPath = urlPath.replace(`{${"ref"}}`, encodeURIComponent(String(requestParameters["ref"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse2(response, (jsonValue) => SelfServiceReservationFromJSON(jsonValue));
  }
  /**
   * Get reservation
   */
  async selfServiceGetReservation(ref, initOverrides) {
    const response = await this.selfServiceGetReservationRaw({ ref }, initOverrides);
    return await response.value();
  }
  /**
   * Sign rental agreement
   */
  async signRentalAgreementRaw(requestParameters, initOverrides) {
    if (requestParameters["ref"] == null) {
      throw new RequiredError2("ref", 'Required parameter "ref" was null or undefined when calling signRentalAgreement().');
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/self-service/reservation/ref/{ref}/sign-rental-agreement`;
    urlPath = urlPath.replace(`{${"ref"}}`, encodeURIComponent(String(requestParameters["ref"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "POST",
        headers: headerParameters,
        query: queryParameters,
        body: SignRaRequestToJSON(requestParameters["signRaRequest"])
      },
      initOverrides
    );
    return new JSONApiResponse2(response, (jsonValue) => SignRaResponseFromJSON(jsonValue));
  }
  /**
   * Sign rental agreement
   */
  async signRentalAgreement(ref, signRaRequest, initOverrides) {
    const response = await this.signRentalAgreementRaw({ ref, signRaRequest }, initOverrides);
    return await response.value();
  }
  /**
   * Sign upload for reservation
   */
  async signUploadRaw(requestParameters, initOverrides) {
    if (requestParameters["ref"] == null) {
      throw new RequiredError2("ref", 'Required parameter "ref" was null or undefined when calling signUpload().');
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/self-service/reservation/ref/{ref}/sign-upload`;
    urlPath = urlPath.replace(`{${"ref"}}`, encodeURIComponent(String(requestParameters["ref"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "POST",
        headers: headerParameters,
        query: queryParameters,
        body: requestParameters["body"]
      },
      initOverrides
    );
    return new JSONApiResponse2(response, (jsonValue) => SignUploadResponseFromJSON(jsonValue));
  }
  /**
   * Sign upload for reservation
   */
  async signUpload(ref, body, initOverrides) {
    const response = await this.signUploadRaw({ ref, body }, initOverrides);
    return await response.value();
  }
  /**
   * Upload document
   */
  async uploadDocumentRaw(requestParameters, initOverrides) {
    if (requestParameters["ref"] == null) {
      throw new RequiredError2("ref", 'Required parameter "ref" was null or undefined when calling uploadDocument().');
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/self-service/reservation/ref/{ref}/documents`;
    urlPath = urlPath.replace(`{${"ref"}}`, encodeURIComponent(String(requestParameters["ref"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "POST",
        headers: headerParameters,
        query: queryParameters,
        body: RentalDocumentUploadToJSON(requestParameters["rentalDocumentUpload"])
      },
      initOverrides
    );
    return new JSONApiResponse2(response, (jsonValue) => RentalDocumentUploadResponseFromJSON(jsonValue));
  }
  /**
   * Upload document
   */
  async uploadDocument(ref, rentalDocumentUpload, initOverrides) {
    const response = await this.uploadDocumentRaw({ ref, rentalDocumentUpload }, initOverrides);
    return await response.value();
  }
};

// src/rentals-api/apis/SitesApi.ts
var SitesApi2 = class extends BaseAPI2 {
  /**
   */
  async getCountriesRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    if (requestParameters["country"] != null) {
      queryParameters["country"] = requestParameters["country"];
    }
    if (requestParameters["jucybusinessunit"] != null) {
      queryParameters["jucybusinessunit"] = requestParameters["jucybusinessunit"];
    }
    const headerParameters = {};
    let urlPath = `/api/Sites/2.0/GetCountries`;
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse2(response, (jsonValue) => BaseResponseFromJSON(jsonValue));
  }
  /**
   */
  async getCountries(country, jucybusinessunit, initOverrides) {
    const response = await this.getCountriesRaw({ country, jucybusinessunit }, initOverrides);
    return await response.value();
  }
  /**
   * Get service constraints for all pick up/drop off sites
   * Get rental sites settings
   */
  async getRentalSitesSettingsRaw(initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    let urlPath = `/api/rental-sites/settings`;
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse2(response, (jsonValue) => SiteSettingsResponseFromJSON(jsonValue));
  }
  /**
   * Get service constraints for all pick up/drop off sites
   * Get rental sites settings
   */
  async getRentalSitesSettings(initOverrides) {
    const response = await this.getRentalSitesSettingsRaw(initOverrides);
    return await response.value();
  }
  /**
   * Returns a list of all locations where a vehicle may be picked up from or dropped off to
   * Get rental sites
   */
  async getSitesRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    if (requestParameters["country"] != null) {
      queryParameters["country"] = requestParameters["country"];
    }
    if (requestParameters["jucybusinessunit"] != null) {
      queryParameters["jucybusinessunit"] = requestParameters["jucybusinessunit"];
    }
    const headerParameters = {};
    let urlPath = `/api/Sites/1.0/GetSites`;
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse2(response, (jsonValue) => GetSitesResponseFromJSON(jsonValue));
  }
  /**
   * Returns a list of all locations where a vehicle may be picked up from or dropped off to
   * Get rental sites
   */
  async getSites(country, jucybusinessunit, initOverrides) {
    const response = await this.getSitesRaw({ country, jucybusinessunit }, initOverrides);
    return await response.value();
  }
};

// src/RentalsApiClient.ts
var RentalsApiClient2 = class {
  config;
  availability;
  catalog;
  deals;
  pricing;
  reservations;
  selfService;
  sites;
  v3;
  constructor(config) {
    if (!config.fetchApi) {
      config.fetchApi = fetch;
    }
    this.config = new Configuration2(config);
    this.availability = new AvailabilityApi(this.config);
    this.catalog = new CatalogApi2(this.config);
    this.deals = new DealsApi(this.config);
    this.pricing = new PricingApi(this.config);
    this.reservations = new ReservationsApi2(this.config);
    this.selfService = new SelfServiceApi2(this.config);
    this.sites = new SitesApi2(this.config);
    this.v3 = new RentalsApiClientV3_default(config);
  }
  getReservationStoreCardURL(ref, returnUrl) {
    const result = new URL(`/api/self-service/reservation/ref/${ref}/store-card`, this.config.basePath);
    if (returnUrl) {
      result.searchParams.append("returnUrl", returnUrl);
    }
    return result.toString();
  }
  getReservationPaymentURL(ref, amount, returnUrl) {
    const result = new URL(`/api/self-service/reservation/ref/${ref}/payment`, this.config.basePath);
    if (returnUrl) {
      result.searchParams.append("returnUrl", returnUrl);
    }
    if (amount) {
      result.searchParams.append("amount", `${amount}`);
    }
    return result.toString();
  }
};
var RentalsApiClient_default = RentalsApiClient2;

// src/index.ts
var index_default = RentalsApiClient_default;
export {
  AdditionalDriverFromJSON,
  AdditionalDriverFromJSONTyped,
  AdditionalDriverToJSON,
  AdditionalDriverToJSONTyped,
  AvailabilityApi,
  BASE_PATH2 as BASE_PATH,
  BalanceDueResponseFromJSON,
  BalanceDueResponseFromJSONTyped,
  BalanceDueResponseToJSON,
  BalanceDueResponseToJSONTyped,
  BaseAPI2 as BaseAPI,
  BaseResponseFromJSON,
  BaseResponseFromJSONTyped,
  BaseResponseResponseTypeEnum,
  BaseResponseToJSON,
  BaseResponseToJSONTyped,
  BlobApiResponse,
  BookedWithFromJSON2 as BookedWithFromJSON,
  BookedWithFromJSONTyped4 as BookedWithFromJSONTyped,
  BookedWithToJSON2 as BookedWithToJSON,
  BookedWithToJSONTyped3 as BookedWithToJSONTyped,
  BookingSummaryHirerFromJSON2 as BookingSummaryHirerFromJSON,
  BookingSummaryHirerFromJSONTyped3 as BookingSummaryHirerFromJSONTyped,
  BookingSummaryHirerToJSON2 as BookingSummaryHirerToJSON,
  BookingSummaryHirerToJSONTyped2 as BookingSummaryHirerToJSONTyped,
  BranchDetailsFromJSON,
  BranchDetailsFromJSONTyped,
  BranchDetailsToJSON,
  BranchDetailsToJSONTyped,
  COLLECTION_FORMATS,
  CardOnFileFromJSON,
  CardOnFileFromJSONTyped,
  CardOnFileToJSON,
  CardOnFileToJSONTyped,
  CatalogApi2 as CatalogApi,
  CheckInRequestFromJSON,
  CheckInRequestFromJSONTyped,
  CheckInRequestToJSON,
  CheckInRequestToJSONTyped,
  CompanyDetailsFromJSON,
  CompanyDetailsFromJSONTyped,
  CompanyDetailsToJSON,
  CompanyDetailsToJSONTyped,
  Configuration2 as Configuration,
  ConfirmQuoteResponseDataFromJSON,
  ConfirmQuoteResponseDataFromJSONTyped,
  ConfirmQuoteResponseDataToJSON,
  ConfirmQuoteResponseDataToJSONTyped,
  ConfirmQuoteResponseFromJSON,
  ConfirmQuoteResponseFromJSONTyped,
  ConfirmQuoteResponseResponseTypeEnum,
  ConfirmQuoteResponseToJSON,
  ConfirmQuoteResponseToJSONTyped,
  ConfirmReservationResponseFromJSON,
  ConfirmReservationResponseFromJSONTyped,
  ConfirmReservationResponseToJSON,
  ConfirmReservationResponseToJSONTyped,
  CreateQuoteMergeSurchargesFeesEnum,
  CreateQuoteResponseFromJSON,
  CreateQuoteResponseFromJSONTyped,
  CreateQuoteResponseResponseTypeEnum,
  CreateQuoteResponseToJSON,
  CreateQuoteResponseToJSONTyped,
  CustomerAddressFromJSON,
  CustomerAddressFromJSONTyped,
  CustomerAddressToJSON,
  CustomerAddressToJSONTyped,
  CustomerDataFromJSON,
  CustomerDataFromJSONTyped,
  CustomerDataToJSON,
  CustomerDataToJSONTyped,
  CustomerDriversLicenseFromJSON,
  CustomerDriversLicenseFromJSONTyped,
  CustomerDriversLicenseToJSON,
  CustomerDriversLicenseToJSONTyped,
  CustomerFromJSON,
  CustomerFromJSONTyped,
  CustomerToJSON,
  CustomerToJSONTyped,
  DealActionsFromJSON,
  DealActionsFromJSONTyped,
  DealActionsToJSON,
  DealActionsToJSONTyped,
  DealConditionsBlackoutDatesFromJSON,
  DealConditionsBlackoutDatesFromJSONTyped,
  DealConditionsBlackoutDatesToJSON,
  DealConditionsBlackoutDatesToJSONTyped,
  DealConditionsEndDateBetweenFromJSON,
  DealConditionsEndDateBetweenFromJSONTyped,
  DealConditionsEndDateBetweenToJSON,
  DealConditionsEndDateBetweenToJSONTyped,
  DealConditionsFromJSON,
  DealConditionsFromJSONTyped,
  DealConditionsStartDateBetweenFromJSON,
  DealConditionsStartDateBetweenFromJSONTyped,
  DealConditionsStartDateBetweenToJSON,
  DealConditionsStartDateBetweenToJSONTyped,
  DealConditionsToJSON,
  DealConditionsToJSONTyped,
  DealFromJSON,
  DealFromJSONTyped,
  DealSummaryFromJSON,
  DealSummaryFromJSONTyped,
  DealSummaryToJSON,
  DealSummaryToJSONTyped,
  DealSummaryTypeEnum,
  DealToJSON,
  DealToJSONTyped,
  DealTypeEnum,
  DealsApi,
  DefaultConfig2 as DefaultConfig,
  DefaultTimeFromJSON,
  DefaultTimeFromJSONTyped,
  DefaultTimeToJSON,
  DefaultTimeToJSONTyped,
  DriverDetailsFromJSON,
  DriverDetailsFromJSONTyped,
  DriverDetailsToJSON,
  DriverDetailsToJSONTyped,
  DynamicPricingResponseInnerFromJSON,
  DynamicPricingResponseInnerFromJSONTyped,
  DynamicPricingResponseInnerProductFromJSON,
  DynamicPricingResponseInnerProductFromJSONTyped,
  DynamicPricingResponseInnerProductToJSON,
  DynamicPricingResponseInnerProductToJSONTyped,
  DynamicPricingResponseInnerToJSON,
  DynamicPricingResponseInnerToJSONTyped,
  ExceptionRequestFromJSON,
  ExceptionRequestFromJSONTyped,
  ExceptionRequestToJSON,
  ExceptionRequestToJSONTyped,
  ExcessType,
  ExcessTypeFromJSON,
  ExcessTypeFromJSONTyped,
  ExcessTypeToJSON,
  ExcessTypeToJSONTyped,
  FetchError2 as FetchError,
  FleetCategoryAvailabilityEnum,
  FleetCategoryDailyRateFromJSON,
  FleetCategoryDailyRateFromJSONTyped,
  FleetCategoryDailyRateToJSON,
  FleetCategoryDailyRateToJSONTyped,
  FleetCategoryFromJSON2 as FleetCategoryFromJSON,
  FleetCategoryFromJSONTyped6 as FleetCategoryFromJSONTyped,
  FleetCategoryMandatoryFeesFromJSON,
  FleetCategoryMandatoryFeesFromJSONTyped,
  FleetCategoryMandatoryFeesPriceFromJSON,
  FleetCategoryMandatoryFeesPriceFromJSONTyped,
  FleetCategoryMandatoryFeesPriceToJSON,
  FleetCategoryMandatoryFeesPriceToJSONTyped,
  FleetCategoryMandatoryFeesToJSON,
  FleetCategoryMandatoryFeesToJSONTyped,
  FleetCategoryToJSON2 as FleetCategoryToJSON,
  FleetCategoryToJSONTyped5 as FleetCategoryToJSONTyped,
  FleetCategoryTotalFromJSON,
  FleetCategoryTotalFromJSONTyped,
  FleetCategoryTotalToJSON,
  FleetCategoryTotalToJSONTyped,
  FleetTypeFromJSON,
  FleetTypeFromJSONTyped,
  FleetTypeHireDaysTypeEnum,
  FleetTypeToJSON,
  FleetTypeToJSONTyped,
  GeoLocationFromJSON,
  GeoLocationFromJSONTyped,
  GeoLocationToJSON,
  GeoLocationToJSONTyped,
  GetAllFleetTypesResponseFromJSON,
  GetAllFleetTypesResponseFromJSONTyped,
  GetAllFleetTypesResponseLocationsAvailableAtFromJSON,
  GetAllFleetTypesResponseLocationsAvailableAtFromJSONTyped,
  GetAllFleetTypesResponseLocationsAvailableAtToJSON,
  GetAllFleetTypesResponseLocationsAvailableAtToJSONTyped,
  GetAllFleetTypesResponseResponseTypeEnum,
  GetAllFleetTypesResponseToJSON,
  GetAllFleetTypesResponseToJSONTyped,
  GetFleetTypeCategoriesMergeSurchargesFeesEnum,
  GetFleetTypeCategoriesResponseDataFromJSON,
  GetFleetTypeCategoriesResponseDataFromJSONTyped,
  GetFleetTypeCategoriesResponseDataToJSON,
  GetFleetTypeCategoriesResponseDataToJSONTyped,
  GetFleetTypeCategoriesResponseFromJSON,
  GetFleetTypeCategoriesResponseFromJSONTyped,
  GetFleetTypeCategoriesResponseResponseTypeEnum,
  GetFleetTypeCategoriesResponseToJSON,
  GetFleetTypeCategoriesResponseToJSONTyped,
  GetSitesResponseFromJSON,
  GetSitesResponseFromJSONTyped,
  GetSitesResponseResponseTypeEnum,
  GetSitesResponseToJSON,
  GetSitesResponseToJSONTyped,
  HirePeriodSettingsFromJSON,
  HirePeriodSettingsFromJSONTyped,
  HirePeriodSettingsToJSON,
  HirePeriodSettingsToJSONTyped,
  HirerDetailsFromJSON2 as HirerDetailsFromJSON,
  HirerDetailsFromJSONTyped5 as HirerDetailsFromJSONTyped,
  HirerDetailsHomeAddressFromJSON,
  HirerDetailsHomeAddressFromJSONTyped,
  HirerDetailsHomeAddressToJSON,
  HirerDetailsHomeAddressToJSONTyped,
  HirerDetailsToJSON2 as HirerDetailsToJSON,
  HirerDetailsToJSONTyped4 as HirerDetailsToJSONTyped,
  IdConfirmBodyFromJSON,
  IdConfirmBodyFromJSONTyped,
  IdConfirmBodyToJSON,
  IdConfirmBodyToJSONTyped,
  InfringementNoteFromJSON,
  InfringementNoteFromJSONTyped,
  InfringementNoteToJSON,
  InfringementNoteToJSONTyped,
  InsuranceProductsFromJSON,
  InsuranceProductsFromJSONTyped,
  InsuranceProductsToJSON,
  InsuranceProductsToJSONTyped,
  JSONApiResponse2 as JSONApiResponse,
  LicenseFromJSON,
  LicenseFromJSONTyped,
  LicenseToJSON,
  LicenseToJSONTyped,
  ListDealsResponseFromJSON,
  ListDealsResponseFromJSONTyped,
  ListDealsResponseToJSON,
  ListDealsResponseToJSONTyped,
  ListReservationsResponseFromJSON,
  ListReservationsResponseFromJSONTyped,
  ListReservationsResponseToJSON,
  ListReservationsResponseToJSONTyped,
  LiteAdditionalDriverFromJSON,
  LiteAdditionalDriverFromJSONTyped,
  LiteAdditionalDriverToJSON,
  LiteAdditionalDriverToJSONTyped,
  LiteAddressFromJSON,
  LiteAddressFromJSONTyped,
  LiteAddressToJSON,
  LiteAddressToJSONTyped,
  LiteContactFromJSON,
  LiteContactFromJSONTyped,
  LiteContactToJSON,
  LiteContactToJSONTyped,
  MoneyFromJSON,
  MoneyFromJSONTyped,
  MoneyToJSON,
  MoneyToJSONTyped,
  OfficeTimeFromJSON,
  OfficeTimeFromJSONTyped,
  OfficeTimeToJSON,
  OfficeTimeToJSONTyped,
  PagedResponseFromJSON,
  PagedResponseFromJSONTyped,
  PagedResponseToJSON,
  PagedResponseToJSONTyped,
  PaymentFromJSON,
  PaymentFromJSONTyped,
  PaymentToJSON,
  PaymentToJSONTyped,
  PricingApi,
  PricingResponseFromJSON,
  PricingResponseFromJSONTyped,
  PricingResponseProductPricesFromJSON,
  PricingResponseProductPricesFromJSONTyped,
  PricingResponseProductPricesToJSON,
  PricingResponseProductPricesToJSONTyped,
  PricingResponseProductPricesTypeEnum,
  PricingResponseToJSON,
  PricingResponseToJSONTyped,
  ProductGroupFromJSON,
  ProductGroupFromJSONTyped,
  ProductGroupToJSON,
  ProductGroupToJSONTyped,
  ProductType,
  ProductTypeFromJSON,
  ProductTypeFromJSONTyped,
  ProductTypeToJSON,
  ProductTypeToJSONTyped,
  QuoteAgentBalanceDueFromJSON,
  QuoteAgentBalanceDueFromJSONTyped,
  QuoteAgentBalanceDueToJSON,
  QuoteAgentBalanceDueToJSONTyped,
  QuoteAgentCollectsFromJSON,
  QuoteAgentCollectsFromJSONTyped,
  QuoteAgentCollectsToJSON,
  QuoteAgentCollectsToJSONTyped,
  QuoteAgentCommissionFromJSON,
  QuoteAgentCommissionFromJSONTyped,
  QuoteAgentCommissionToJSON,
  QuoteAgentCommissionToJSONTyped,
  QuoteBalanceDueFromJSON,
  QuoteBalanceDueFromJSONTyped,
  QuoteBalanceDueToJSON,
  QuoteBalanceDueToJSONTyped,
  QuoteBalanceDueWithCCFeeFromJSON,
  QuoteBalanceDueWithCCFeeFromJSONTyped,
  QuoteBalanceDueWithCCFeeToJSON,
  QuoteBalanceDueWithCCFeeToJSONTyped,
  QuoteFromJSON,
  QuoteFromJSONTyped,
  QuoteToJSON,
  QuoteToJSONTyped,
  QuoteTotalPriceFromJSON,
  QuoteTotalPriceFromJSONTyped,
  QuoteTotalPriceToJSON,
  QuoteTotalPriceToJSONTyped,
  QuoteWithHirerFromJSON,
  QuoteWithHirerFromJSONTyped,
  QuoteWithHirerToJSON,
  QuoteWithHirerToJSONTyped,
  QuoteWithProductOptionsFromJSON,
  QuoteWithProductOptionsFromJSONTyped,
  QuoteWithProductOptionsToJSON,
  QuoteWithProductOptionsToJSONTyped,
  RateInfoDailyFromJSON,
  RateInfoDailyFromJSONTyped,
  RateInfoDailyToJSON,
  RateInfoDailyToJSONTyped,
  RateInfoDiscountFromJSON,
  RateInfoDiscountFromJSONTyped,
  RateInfoDiscountToJSON,
  RateInfoDiscountToJSONTyped,
  RateInfoFromJSON,
  RateInfoFromJSONTyped,
  RateInfoMonthlyFromJSON,
  RateInfoMonthlyFromJSONTyped,
  RateInfoMonthlyToJSON,
  RateInfoMonthlyToJSONTyped,
  RateInfoSeasonFromJSON,
  RateInfoSeasonFromJSONTyped,
  RateInfoSeasonToJSON,
  RateInfoSeasonToJSONTyped,
  RateInfoToJSON,
  RateInfoToJSONTyped,
  RentalAdditionalProductFromJSON,
  RentalAdditionalProductFromJSONTyped,
  RentalAdditionalProductPriceFromJSON,
  RentalAdditionalProductPriceFromJSONTyped,
  RentalAdditionalProductPriceToJSON,
  RentalAdditionalProductPriceToJSONTyped,
  RentalAdditionalProductToJSON,
  RentalAdditionalProductToJSONTyped,
  RentalCatalogItemFromJSON,
  RentalCatalogItemFromJSONTyped,
  RentalCatalogItemToJSON,
  RentalCatalogItemToJSONTyped,
  RentalCatalogResponseFromJSON2 as RentalCatalogResponseFromJSON,
  RentalCatalogResponseFromJSONTyped2 as RentalCatalogResponseFromJSONTyped,
  RentalCatalogResponseToJSON2 as RentalCatalogResponseToJSON,
  RentalCatalogResponseToJSONTyped,
  RentalDocumentDocumentLinkFromJSON,
  RentalDocumentDocumentLinkFromJSONTyped,
  RentalDocumentDocumentLinkToJSON,
  RentalDocumentDocumentLinkToJSONTyped,
  RentalDocumentFromJSON,
  RentalDocumentFromJSONTyped,
  RentalDocumentToJSON,
  RentalDocumentToJSONTyped,
  RentalDocumentUploadFromJSON,
  RentalDocumentUploadFromJSONTyped,
  RentalDocumentUploadResponseFromJSON,
  RentalDocumentUploadResponseFromJSONTyped,
  RentalDocumentUploadResponseToJSON,
  RentalDocumentUploadResponseToJSONTyped,
  RentalDocumentUploadToJSON,
  RentalDocumentUploadToJSONTyped,
  RentalExcessReductionAllOfBondFromJSON,
  RentalExcessReductionAllOfBondFromJSONTyped,
  RentalExcessReductionAllOfBondToJSON,
  RentalExcessReductionAllOfBondToJSONTyped,
  RentalExcessReductionAllOfExcessFromJSON,
  RentalExcessReductionAllOfExcessFromJSONTyped,
  RentalExcessReductionAllOfExcessToJSON,
  RentalExcessReductionAllOfExcessToJSONTyped,
  RentalExcessReductionFromJSON,
  RentalExcessReductionFromJSONTyped,
  RentalExcessReductionToJSON,
  RentalExcessReductionToJSONTyped,
  RentalLineItemFromJSON,
  RentalLineItemFromJSONTyped,
  RentalLineItemToJSON,
  RentalLineItemToJSONTyped,
  RentalProductFromJSON,
  RentalProductFromJSONTyped,
  RentalProductToJSON,
  RentalProductToJSONTyped,
  RentalSignatureCustomerFromJSON,
  RentalSignatureCustomerFromJSONTyped,
  RentalSignatureCustomerToJSON,
  RentalSignatureCustomerToJSONTyped,
  RentalSignatureFromJSON,
  RentalSignatureFromJSONTyped,
  RentalSignatureSignatureFromJSON,
  RentalSignatureSignatureFromJSONTyped,
  RentalSignatureSignatureTemplateFromJSON,
  RentalSignatureSignatureTemplateFromJSONTyped,
  RentalSignatureSignatureTemplateToJSON,
  RentalSignatureSignatureTemplateToJSONTyped,
  RentalSignatureSignatureToJSON,
  RentalSignatureSignatureToJSONTyped,
  RentalSignatureToJSON,
  RentalSignatureToJSONTyped,
  RequiredError2 as RequiredError,
  ReservationDetailsFromJSON,
  ReservationDetailsFromJSONTyped,
  ReservationDetailsToJSON,
  ReservationDetailsToJSONTyped,
  ReservationOptionsFromJSON,
  ReservationOptionsFromJSONTyped,
  ReservationOptionsToJSON,
  ReservationOptionsToJSONTyped,
  ReservationPricingEnquiryResponseFromJSON,
  ReservationPricingEnquiryResponseFromJSONTyped,
  ReservationPricingEnquiryResponseToJSON,
  ReservationPricingEnquiryResponseToJSONTyped,
  ReservationSummaryAvailabilityEnum,
  ReservationSummaryFromJSON,
  ReservationSummaryFromJSONTyped,
  ReservationSummaryHirerFromJSON,
  ReservationSummaryHirerFromJSONTyped,
  ReservationSummaryHirerToJSON,
  ReservationSummaryHirerToJSONTyped,
  ReservationSummaryStatusEnum,
  ReservationSummaryToJSON,
  ReservationSummaryToJSONTyped,
  ReservationsApi2 as ReservationsApi,
  ResponseError2 as ResponseError,
  SecondaryProductItemFromJSON,
  SecondaryProductItemFromJSONTyped,
  SecondaryProductItemToJSON,
  SecondaryProductItemToJSONTyped,
  SecondaryProductMapFromJSON,
  SecondaryProductMapFromJSONTyped,
  SecondaryProductMapToJSON,
  SecondaryProductMapToJSONTyped,
  SecondaryProductsFromJSON,
  SecondaryProductsFromJSONTyped,
  SecondaryProductsToJSON,
  SecondaryProductsToJSONTyped,
  SelfServiceApi2 as SelfServiceApi,
  SelfServiceReservationFromJSON,
  SelfServiceReservationFromJSONTyped,
  SelfServiceReservationToJSON,
  SelfServiceReservationToJSONTyped,
  SignRaRequestFromJSON,
  SignRaRequestFromJSONTyped,
  SignRaRequestSignatureFromJSON,
  SignRaRequestSignatureFromJSONTyped,
  SignRaRequestSignatureToJSON,
  SignRaRequestSignatureToJSONTyped,
  SignRaRequestToJSON,
  SignRaRequestToJSONTyped,
  SignRaResponseFromJSON,
  SignRaResponseFromJSONTyped,
  SignRaResponseToJSON,
  SignRaResponseToJSONTyped,
  SignUploadResponseFromJSON,
  SignUploadResponseFromJSONTyped,
  SignUploadResponseToJSON,
  SignUploadResponseToJSONTyped,
  SiteAddress1FromJSON2 as SiteAddress1FromJSON,
  SiteAddress1FromJSONTyped3 as SiteAddress1FromJSONTyped,
  SiteAddress1ToJSON2 as SiteAddress1ToJSON,
  SiteAddress1ToJSONTyped2 as SiteAddress1ToJSONTyped,
  SiteDefaultTimesFromJSON2 as SiteDefaultTimesFromJSON,
  SiteDefaultTimesFromJSONTyped3 as SiteDefaultTimesFromJSONTyped,
  SiteDefaultTimesToJSON2 as SiteDefaultTimesToJSON,
  SiteDefaultTimesToJSONTyped2 as SiteDefaultTimesToJSONTyped,
  SiteFleetTypeSettingsFromJSON,
  SiteFleetTypeSettingsFromJSONTyped,
  SiteFleetTypeSettingsToJSON,
  SiteFleetTypeSettingsToJSONTyped,
  SiteFromJSON2 as SiteFromJSON,
  SiteFromJSONTyped2 as SiteFromJSONTyped,
  SiteHolidaysFromJSON,
  SiteHolidaysFromJSONTyped,
  SiteHolidaysToJSON,
  SiteHolidaysToJSONTyped,
  SiteServiceHoursFromJSON,
  SiteServiceHoursFromJSONTyped,
  SiteServiceHoursMessageFromJSON,
  SiteServiceHoursMessageFromJSONTyped,
  SiteServiceHoursMessageToJSON,
  SiteServiceHoursMessageToJSONTyped,
  SiteServiceHoursToJSON,
  SiteServiceHoursToJSONTyped,
  SiteSettingsResponseFromJSON,
  SiteSettingsResponseFromJSONTyped,
  SiteSettingsResponseToJSON,
  SiteSettingsResponseToJSONTyped,
  SiteSiteSettingsFromJSON,
  SiteSiteSettingsFromJSONTyped,
  SiteSiteSettingsToJSON,
  SiteSiteSettingsToJSONTyped,
  SiteToJSON2 as SiteToJSON,
  SiteToJSONTyped,
  SitesApi2 as SitesApi,
  SitesSettingsDefaultTimesFromJSON,
  SitesSettingsDefaultTimesFromJSONTyped,
  SitesSettingsDefaultTimesToJSON,
  SitesSettingsDefaultTimesToJSONTyped,
  SitesSettingsFleetTypeSettingsFromJSON,
  SitesSettingsFleetTypeSettingsFromJSONTyped,
  SitesSettingsFleetTypeSettingsToJSON,
  SitesSettingsFleetTypeSettingsToJSONTyped,
  SitesSettingsFromJSON,
  SitesSettingsFromJSONTyped,
  SitesSettingsHirePeriodsFromJSON,
  SitesSettingsHirePeriodsFromJSONTyped,
  SitesSettingsHirePeriodsToJSON,
  SitesSettingsHirePeriodsToJSONTyped,
  SitesSettingsHolidaysFromJSON,
  SitesSettingsHolidaysFromJSONTyped,
  SitesSettingsHolidaysToJSON,
  SitesSettingsHolidaysToJSONTyped,
  SitesSettingsPickUpServiceHoursMessagesFromJSON,
  SitesSettingsPickUpServiceHoursMessagesFromJSONTyped,
  SitesSettingsPickUpServiceHoursMessagesToJSON,
  SitesSettingsPickUpServiceHoursMessagesToJSONTyped,
  SitesSettingsProhibitedPickUpSitesFromJSON,
  SitesSettingsProhibitedPickUpSitesFromJSONTyped,
  SitesSettingsProhibitedPickUpSitesToJSON,
  SitesSettingsProhibitedPickUpSitesToJSONTyped,
  SitesSettingsServiceHoursFromJSON,
  SitesSettingsServiceHoursFromJSONTyped,
  SitesSettingsServiceHoursToJSON,
  SitesSettingsServiceHoursToJSONTyped,
  SitesSettingsSitesFromJSON,
  SitesSettingsSitesFromJSONTyped,
  SitesSettingsSitesToJSON,
  SitesSettingsSitesToJSONTyped,
  SitesSettingsToJSON,
  SitesSettingsToJSONTyped,
  TextApiResponse2 as TextApiResponse,
  TollRequestFromJSON,
  TollRequestFromJSONTyped,
  TollRequestToJSON,
  TollRequestToJSONTyped,
  UpdateQuoteResponseFromJSON,
  UpdateQuoteResponseFromJSONTyped,
  UpdateQuoteResponseResponseTypeEnum,
  UpdateQuoteResponseToJSON,
  UpdateQuoteResponseToJSONTyped,
  V2MoneyFromJSON,
  V2MoneyFromJSONTyped,
  V2MoneyToJSON,
  V2MoneyToJSONTyped,
  VoidApiResponse,
  canConsumeForm,
  dateToString2 as dateToString,
  index_default as default,
  exists,
  instanceOfAdditionalDriver,
  instanceOfBalanceDueResponse,
  instanceOfBaseResponse,
  instanceOfBookedWith,
  instanceOfBookingSummaryHirer,
  instanceOfBranchDetails,
  instanceOfCardOnFile,
  instanceOfCheckInRequest,
  instanceOfCompanyDetails,
  instanceOfConfirmQuoteResponse,
  instanceOfConfirmQuoteResponseData,
  instanceOfConfirmReservationResponse,
  instanceOfCreateQuoteResponse,
  instanceOfCustomer,
  instanceOfCustomerAddress,
  instanceOfCustomerData,
  instanceOfCustomerDriversLicense,
  instanceOfDeal,
  instanceOfDealActions,
  instanceOfDealConditions,
  instanceOfDealConditionsBlackoutDates,
  instanceOfDealConditionsEndDateBetween,
  instanceOfDealConditionsStartDateBetween,
  instanceOfDealSummary,
  instanceOfDefaultTime,
  instanceOfDriverDetails,
  instanceOfDynamicPricingResponseInner,
  instanceOfDynamicPricingResponseInnerProduct,
  instanceOfExceptionRequest,
  instanceOfExcessType,
  instanceOfFleetCategory,
  instanceOfFleetCategoryDailyRate,
  instanceOfFleetCategoryMandatoryFees,
  instanceOfFleetCategoryMandatoryFeesPrice,
  instanceOfFleetCategoryTotal,
  instanceOfFleetType,
  instanceOfGeoLocation,
  instanceOfGetAllFleetTypesResponse,
  instanceOfGetAllFleetTypesResponseLocationsAvailableAt,
  instanceOfGetFleetTypeCategoriesResponse,
  instanceOfGetFleetTypeCategoriesResponseData,
  instanceOfGetSitesResponse,
  instanceOfHirePeriodSettings,
  instanceOfHirerDetails,
  instanceOfHirerDetailsHomeAddress,
  instanceOfIdConfirmBody,
  instanceOfInfringementNote,
  instanceOfInsuranceProducts,
  instanceOfLicense,
  instanceOfListDealsResponse,
  instanceOfListReservationsResponse,
  instanceOfLiteAdditionalDriver,
  instanceOfLiteAddress,
  instanceOfLiteContact,
  instanceOfMoney,
  instanceOfOfficeTime,
  instanceOfPagedResponse,
  instanceOfPayment,
  instanceOfPricingResponse,
  instanceOfPricingResponseProductPrices,
  instanceOfProductGroup,
  instanceOfProductType,
  instanceOfQuote,
  instanceOfQuoteAgentBalanceDue,
  instanceOfQuoteAgentCollects,
  instanceOfQuoteAgentCommission,
  instanceOfQuoteBalanceDue,
  instanceOfQuoteBalanceDueWithCCFee,
  instanceOfQuoteTotalPrice,
  instanceOfQuoteWithHirer,
  instanceOfQuoteWithProductOptions,
  instanceOfRateInfo,
  instanceOfRateInfoDaily,
  instanceOfRateInfoDiscount,
  instanceOfRateInfoMonthly,
  instanceOfRateInfoSeason,
  instanceOfRentalAdditionalProduct,
  instanceOfRentalAdditionalProductPrice,
  instanceOfRentalCatalogItem,
  instanceOfRentalCatalogResponse,
  instanceOfRentalDocument,
  instanceOfRentalDocumentDocumentLink,
  instanceOfRentalDocumentUpload,
  instanceOfRentalDocumentUploadResponse,
  instanceOfRentalExcessReduction,
  instanceOfRentalExcessReductionAllOfBond,
  instanceOfRentalExcessReductionAllOfExcess,
  instanceOfRentalLineItem,
  instanceOfRentalProduct,
  instanceOfRentalSignature,
  instanceOfRentalSignatureCustomer,
  instanceOfRentalSignatureSignature,
  instanceOfRentalSignatureSignatureTemplate,
  instanceOfReservationDetails,
  instanceOfReservationOptions,
  instanceOfReservationPricingEnquiryResponse,
  instanceOfReservationSummary,
  instanceOfReservationSummaryHirer,
  instanceOfSecondaryProductItem,
  instanceOfSecondaryProductMap,
  instanceOfSecondaryProducts,
  instanceOfSelfServiceReservation,
  instanceOfSignRaRequest,
  instanceOfSignRaRequestSignature,
  instanceOfSignRaResponse,
  instanceOfSignUploadResponse,
  instanceOfSite,
  instanceOfSiteAddress1,
  instanceOfSiteDefaultTimes,
  instanceOfSiteFleetTypeSettings,
  instanceOfSiteHolidays,
  instanceOfSiteServiceHours,
  instanceOfSiteServiceHoursMessage,
  instanceOfSiteSettingsResponse,
  instanceOfSiteSiteSettings,
  instanceOfSitesSettings,
  instanceOfSitesSettingsDefaultTimes,
  instanceOfSitesSettingsFleetTypeSettings,
  instanceOfSitesSettingsHirePeriods,
  instanceOfSitesSettingsHolidays,
  instanceOfSitesSettingsPickUpServiceHoursMessages,
  instanceOfSitesSettingsProhibitedPickUpSites,
  instanceOfSitesSettingsServiceHours,
  instanceOfSitesSettingsSites,
  instanceOfTollRequest,
  instanceOfUpdateQuoteResponse,
  instanceOfV2Money,
  mapValues,
  querystring2 as querystring,
  stringToDate2 as stringToDate
};
//# sourceMappingURL=index.mjs.map